/**
 * English (en) resource catalog — Wave 1.
 *
 * MUST mirror ca.ts key-for-key (parity is asserted by the i18n factory in development).
 * Keys are English code identifiers; only values are localized.
 */

import { existenciesEn } from './existencies/en.js';
import { qualityEn } from './quality/en.js';
import { compresEn } from './compres/en.js';
import { catalegEn } from './cataleg/en.js';

export const en = {
  // ── Cross-cutting infra ────────────────────────────────────────────────────
  common: {
    appName: 'Obrava',
    save: 'Save',
    cancel: 'Cancel',
    confirm: 'Confirm',
    ok: 'OK',
    delete: 'Delete',
    edit: 'Edit',
    add: 'Add',
    search: 'Search',
    back: 'Back',
    next: 'Next',
    close: 'Close',
    loading: 'Loading…',
    retry: 'Try again',
    yes: 'Yes',
    no: 'No',
    all: 'All',
    none: 'None',
    today: 'Today',
    required: 'Required',
    optional: 'Optional',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    updateAvailableTitle: 'An update is available',
    updateAvailableBody: 'Save what you are working on, then update to load the latest version.',
    updateAction: 'Update',
  },

  auth: {
    signIn: 'Sign in',
    signOut: 'Sign out',
    pin: 'PIN',
    enterPin: 'Enter PIN',
    email: 'Email',
    password: 'Password',
    wrongPin: 'Incorrect PIN',
    selectOperator: 'Select operator',
    shift: 'Shift',
  },

  errors: {
    generic: 'Something went wrong',
    network: 'Connection error',
    notFound: 'Not found',
    forbidden: 'You do not have permission',
    conflict: 'Data conflict',
    tryAgain: 'Try again',
  },

  validation: {
    required: 'This field is required',
    invalidEmail: 'Invalid email',
    invalidNumber: 'Invalid number',
    mustBePositive: 'Must be positive',
    reasonRequired: 'A reason is required',
    tooShort: 'Too short',
    tooLong: 'Too long',
  },

  sync: {
    online: 'Online',
    offline: 'Offline',
    syncing: 'Syncing',
    syncError: 'Sync error',
    pendingChanges: 'Pending changes',
    allSynced: 'All synced',
    savedOffline: 'Saved offline',
    title: 'Sync',
    queued: '{{count}} operations queued',
    retrying: 'Retrying automatically…',
    stuck: 'Could not sync. We keep trying; your work is saved on the device.',
    blocked: 'Session expired. Sign in again to sync the pending work.',
    lastSync: 'Last sync',
    never: 'Not yet',
    lastError: 'Last error',
    attempts: 'Attempts',
    syncNow: 'Sync now',
    downloadStuck:
      'No new data could be downloaded. We keep trying; what you see is the last copy saved on the device.',
    downloadTenantMoved:
      'The active obrador changed from the office. Pick the obrador again to keep receiving data.',
    lastDownload: 'Last download',
    lastDownloadError: 'Last download error',
    retryDownload: 'Download now',
  },

  notifications: {
    title: 'Notifications',
    markRead: 'Mark as read',
    empty: 'No notifications',
    lowStock: 'Stock below minimum',
    expirySoon: 'Expiry approaching',
    incidentHigh: 'Severe incident',
  },

  // ── Domain vocabulary (the 118 shared terms, split per doc 04) ───────────────
  'domain.articles': {
    article: 'Article',
    rawMaterial: 'Raw material',
    'rawMaterial.abbr': 'RM',
    intermediateProduct: 'Semi-finished product',
    finishedProduct: 'Finished product',
    packaging: 'Packaging',
    bom: 'Ingredients list',
    'bom.abbr': 'BOM',
    technicalSheet: 'Tech sheet',
    'technicalSheet.abbr': 'TS',
    smartImporter: 'Smart importer',
  },

  'domain.recipes': {
    recipe: 'Recipe',
    recipeVersion: 'Recipe version',
    recipeStep: 'Production step',
    recipeScaling: 'Recipe scaling',
    yield: 'Yield',
    leadTime: 'Lead time',
  },

  'domain.lots': {
    lot: 'Batch',
    finishedLot: 'Finished-product batch',
    supplierLot: 'Supplier batch',
    internalLot: 'Internal batch',
    lotNumber: 'Batch number',
    expiry: 'Expiry',
    shelfLife: 'Shelf life',
    productionDate: 'Production date',
    label: 'Label',
  },

  'domain.stock': {
    stock: 'Stock',
    stockMovement: 'Stock movement',
    stockAdjustment: 'Stock adjustment',
    inventory: 'Inventory',
    stockCount: 'Count',
    minimumStock: 'Minimum stock',
    belowMinimum: 'Below minimum',
    loss: 'Waste',
    'lossReason.burnt': 'Burnt',
    'lossReason.dropped': 'Dropped',
    'lossReason.expired': 'Expired',
    warehouseZone: 'Warehouse zone',
    'zone.dry': 'Dry',
    'zone.refrigerated': 'Refrigerated',
    'zone.frozen': 'Frozen',
    'zone.shipping': 'Shipping',
    'zone.quarantine': 'Quarantine',
  },

  'domain.recipes.orders': {
    workOrder: 'Work order',
    'workOrder.abbr': 'WO',
    productionOrder: 'Production order',
    'productionOrder.abbr': 'PO',
    packagingOrder: 'Packaging order',
    cleaningOrder: 'Cleaning order',
    weighing: 'Weighing',
    consumption: 'Consumption',
    plannedQuantity: 'Planned quantity',
    realQuantity: 'Actual quantity',
    deviation: 'Deviation',
    closing: 'Closing',
    closeWorkOrder: 'Close work order',
    agenda: 'Production schedule',
    planning: 'Planning',
  },

  'domain.quality': {
    traceability: 'Traceability',
    recall: 'Product recall',
    haccp: 'HACCP',
    criticalControlPoint: 'Critical control point',
    'criticalControlPoint.abbr': 'CCP',
    nonConformity: 'Non-conformity',
    correctiveAction: 'Corrective action',
    incident: 'Incident',
    severity: 'Severity',
    'severity.low': 'Low',
    'severity.medium': 'Medium',
    'severity.high': 'High',
    auditLog: 'Audit log',
  },

  // Annex II of Reg. (EU) 1169/2011, verbatim: these are DENOMINATIONS, not tags.
  // Shortening one changes what the fitxa tècnica and the label declare, so they
  // stay whole — the sulphite threshold included. Lactose is NOT a separate
  // allergen: it lives inside the milk denomination.
  'domain.allergens': {
    allergen: 'Allergen',
    allergenTraces: 'Traces',
    gluten: 'Cereals containing gluten and products thereof',
    crustaceans: 'Crustaceans and products thereof',
    eggs: 'Eggs and products thereof',
    fish: 'Fish and products thereof',
    peanuts: 'Peanuts and products thereof',
    soybeans: 'Soybeans and products thereof',
    milk: 'Milk and products thereof (including lactose)',
    nuts: 'Tree nuts and products thereof',
    celery: 'Celery and products thereof',
    mustard: 'Mustard and products thereof',
    sesame: 'Sesame seeds and products thereof',
    sulphites: 'Sulphur dioxide and sulphites (>10 mg/kg or >10 mg/l)',
    lupin: 'Lupin and products thereof',
    molluscs: 'Molluscs and products thereof',
  },

  'domain.roles': {
    operator: 'Operator',
    productionManager: 'Production manager',
    quality: 'Quality',
    purchasing: 'Purchasing',
    sales: 'Sales',
    admin: 'Administrator',
    viewer: 'Viewer',
    section: 'Section',
    workPoint: 'Work point',
    device: 'Device',
    scale: 'Scale',
    labelPrinter: 'Label printer',
  },

  'domain.units': {
    'unit.kg': 'kg',
    'unit.units': 'units',
    'unit.boxes': 'boxes',
    'unit.loaves': 'loaves',
    name_kg: 'Kilogram',
    name_L: 'Liter',
    name_Un: 'Unit',
    handlingUnit: 'Handling unit',
    'handlingUnit.abbr': 'HU',
    fefo: 'FEFO',
  },

  'domain.status': {
    'status.planned': 'Planned',
    'status.inProgress': 'In progress',
    'status.completed': 'Completed',
    'status.cancelled': 'Cancelled',
    'status.pending': 'Pending',
    'connection.online': 'Synced',
    'connection.offline': 'Offline',
    'connection.syncing': 'Syncing',
    'connection.pending': '{{count}} to sync',
    'connection.pendingRetrying_one': '{{count}} pending · retrying',
    'connection.pendingRetrying_other': '{{count}} pending · retrying',
    'connection.sessionExpired': 'Session expired',
    'connection.stale': 'Data out of date',
    'reason.required': 'Reason',
    'shift.morning': 'Morning',
    'shift.afternoon': 'Afternoon',
    'shift.night': 'Night',
  },

  'domain.parties': {
    obrador: 'Workshop',
    tenant: 'Workshop',
    supplier: 'Supplier',
    client: 'Customer',
    reception: 'Receiving',
    manualReception: 'Manual receiving',
    deliveryNote: 'Delivery note',
    purchaseOrder: 'Purchase order',
    salesOrder: 'Sales order',
    shipment: 'Shipment',
    shift: 'Shift',
    alert: 'Alert',
    notification: 'Notification',
  },

  // Audit (F0.9) — actions, actor, columns and entity labels shared by the
  // backoffice viewer and the console viewer.
  'domain.audit': {
    action_create: 'Created',
    action_update: 'Updated',
    action_delete: 'Removed',
    action_restore: 'Restored',
    action_support_view: 'Support access',
    action_tenant_switch: 'Workshop switch',
    action_packaging_labels_lost: 'Label selection not saved',
    action_reception_exceptions_lost: 'Reception exceptions not opened',
    action_reception_quality_nc_lost: 'Reception non-conformities not opened',
    action_reception_on_unavailable_order: 'Reception of an unavailable order',
    action_pin_issued: 'PIN issued',
    action_pin_regenerated: 'PIN regenerated',
    action_pin_revoked: 'PIN revoked',
    action_pin_reactivated: 'PIN reactivated',
    action_pin_converted: 'Converted to PIN',
    action_pin_login: 'PIN sign-in',
    action_device_enrolled: 'Device enrolled',
    action_device_revoked: 'Device revoked',
    action_device_unlock: 'Device unlocked',
    action_stock_without_expiry: 'Stock without expiry',
    action_consumption_on_unavailable_lot: 'Consumption of an unavailable lot',
    action_stock_state_change: 'Stock state change',
    action_reception_missing_data: 'Reception without supplier or lot',
    action_access_code_sent: 'Access code sent',
    actorSystem: 'System',
    actorSupport: 'Obrava (support)',
    supportBadge: 'Obrava support',
    colWhen: 'Date & time',
    colWho: 'User',
    colAction: 'Action',
    colEntity: 'Item',
    colDetails: 'Details',
    reasonLabel: 'Reason',
    entity_article_categories: 'Article category',
    entity_articles: 'Article',
    entity_article_ingredients: 'Article ingredient',
    entity_article_food_safety_notes: 'Article food safety',
    entity_clients: 'Client',
    entity_client_shipping_addresses: 'Client shipping address',
    entity_compliance_items: 'Compliance item',
    entity_conservation_conditions: 'Conservation condition',
    entity_container_types: 'Container type',
    entity_devices: 'Device',
    entity_ingredients: 'Ingredient',
    entity_label_templates: 'Label template',
    entity_legislation_notes: 'Legislation note',
    entity_packaging_units: 'Packaging unit',
    entity_preparation_modes: 'Preparation mode',
    entity_processes: 'Process',
    entity_process_resources: 'Process resource',
    entity_recipes: 'Recipe',
    entity_recipe_versions: 'Recipe version',
    entity_resources: 'Resource',
    entity_resource_types: 'Resource type',
    entity_sections: 'Section',
    entity_shifts: 'Shift',
    entity_storage_locations: 'Location',
    entity_supplier_articles: 'Supplier article',
    entity_suppliers: 'Supplier',
    entity_tech_sheets: 'Tech sheet',
    entity_taxes: 'Tax',
    entity_units: 'Unit',
    entity_usage_types: 'Usage type',
    entity_warehouse_zones: 'Warehouse zone',
    entity_work_points: 'Work point',
    entity_users: 'User',
    entity_user_roles: 'User role',
    entity_user_menu_access: 'Menu visibility',
    entity_supplier_documents: 'Supplier document',
    entity_non_conformities: 'Non-conformity',
    entity_corrective_actions: 'Corrective action',
    entity_self_check_templates: 'Self-check template',
    entity_self_check_template_fields: 'Self-check field',
    entity_worker_trainings: 'Handler training',
    entity_tenant_settings: 'Settings',
    entity_purchase_orders: 'Purchase order',
    entity_purchase_order_lines: 'Purchase order line',
    entity_purchase_order_templates: 'Purchase order template',
    entity_purchase_order_template_lines: 'Template line',
    entity_receptions: 'Reception',
    entity_reception_exceptions: 'Reception exception',
    entity_purchase_returns: 'Purchase return',
    entity_stock_state_changes: 'Lot state',
    entity_stock_counts: 'Stock count',
    entity_lot_label_reprints: 'Lot label reprint',
    entity_reception_corrections: 'Reception correction',
    entity_work_orders: 'Work order',
    entity_work_order_resources: 'Work order resource',
    entity_work_order_packaging_labels: 'Requested packaging labels',
    entity_sales_orders: 'Sales order',
    entity_sales_order_lines: 'Sales order line',
    entity_shipments: 'Shipment',
    entity_shipment_lines: 'Shipment line',
    entity_audit_log: 'Activity log',
  },

  // ── App-screen namespaces (Wave 1) ──────────────────────────────────────────
  'planta.login': {
    queuedForOtherObradorNamed:
      'This device still has unsynced work from {{obrador}}. Sign in to {{obrador}}, let it sync, then switch obrador.',
    queuedForOtherObrador:
      'This device still has unsynced work from another obrador. Sign in to it, let it sync, then switch obrador.',
    noPlantaAccessNamed:
      'This user has no access to the floor app at {{obrador}}. Ask your administrator to add the Operator role.',
    noPlantaAccess:
      'This user has no access to the floor app. Ask your administrator to add the Operator role.',
    noPlantaAccessOtherObradors: 'If you work at another workshop, switch to it from the Backoffice.',
    title: 'Obrava',
    subtitle: 'Everything, under control.',
    signInTitle: 'Start your shift',
    email: 'Email',
    emailPlaceholder: 'operator@workshop.com',
    password: 'Password',
    signInCta: 'Sign in',
    signingIn: 'Signing in…',
    preparing: 'Preparing your work orders…',
    invalidCredentials: 'Incorrect email or password',
    noTenantTitle: 'Account without a workshop',
    noTenantBody:
      'This account has no workshop assigned. Contact your workshop manager.',
    selectObrador: 'Select workshop',
    selectObradorBody: 'Your account has access to more than one workshop. Choose which one you are working at this shift.',
    continue: 'Continue',
    // PIN sign-in and tablet enrolment (OBR-25)
    pinTitle: 'Sign in with your PIN',
    pinSubtitle: 'Enter the {{total}} characters of your PIN.',
    pinSlotsLabel: 'PIN, {{entered}} of {{total}} characters entered',
    pinBackspace: 'Delete the last character',
    pinInvalid: 'Incorrect PIN',
    pinConnecting: 'Connecting…',
    pinChecking: 'Checking the PIN…',
    pinOffline: 'No connection. A connection is needed to sign in with a PIN.',
    pinTryLater: 'Could not sign in right now. Try again.',
    pinLockedTimed: 'This tablet was locked after too many wrong PINs. Try again in {{time}}.',
    pinLocked: 'This tablet was locked after too many wrong PINs. Ask a manager to unlock it.',
    queuedForOtherOperator:
      'This tablet still has unsynced work from another operator. Have them sign in and let it sync, or have a manager sign in with email and password.',
    useEmail: 'Sign in with email',
    usePin: 'Sign in with PIN',
  },

  'planta.nav': {
    orders: 'Orders',
    alerts: 'Alerts',
    reception: 'Receiving',
    sales: 'Sales',
    settings: 'More',
    badgeLabel: '{{label}}. Pending tasks: {{count}}',
  },

  'planta.settings': {
    title: 'Settings',
    language: 'Language',
    theme: 'Theme',
    themeLight: 'Light',
    themeDark: 'Dark',
    obrador: 'Workshop',
    version: 'Version {{version}} · Build {{build}}',
    printer: 'Label printer',
    printerChoose: 'Choose where labels are printed',
    printerNone:
      'No printer configured. An administrator needs to add one in the Backoffice (Settings · Devices).',
    printerUnavailable: 'This app version cannot print. Install the latest version.',
    printerTest: 'Test print',
    printerTesting: 'Sending…',
    printerTestOk: 'Test sent to {{printer}}',
    printerTestError:
      'Could not print to {{printer}}. Check that it is on and connected to the same network as the tablet.',
  },

  'planta.obrador': {
    title: 'Workshop',
    change: 'Change workshop',
    sheetTitle: 'Shift workshop',
    sheetBody:
      'Pick where you are working. Orders and data for that workshop will be loaded again.',
    active: 'Active',
    switching: 'Switching workshop…',
    syncNow: 'Sync now',
    errorOffline: 'No connection. Go online to change workshop.',
    errorUnsynced: 'You have work left to sync. Sync it before changing workshop.',
    errorFailed: 'Could not change workshop. Try again.',
    errorNotAMember: 'You no longer have access to this workshop.',
    errorNoAccess:
      'You have no access to the floor app at this workshop. Ask your administrator to add the Operator role.',
    errorSignedOut: 'The shift was ended. Sign in again to continue.',
  },

  'planta.home': {
    title: 'Home',
    greeting: 'Hi, {{name}}',
    myWorkOrders: 'My work orders',
    today: 'Today',
    pending: 'Pending',
    inProgress: 'In progress',
    noWorkOrders: 'You have no assigned orders',
    noWorkOrdersBody: 'When the manager publishes orders for you, they will appear here.',
    seeAll: 'See all',
    refreshing: 'Refreshing…',
    pullToRefresh: 'Pull to refresh',
    signOutConfirm: 'End your shift?',
  },

  'planta.workOrders': {
    title: 'Work orders',
    start: 'Start',
    resume: 'Resume',
    detail: 'Work order detail',
    ingredients: 'Ingredients',
    steps: 'Steps',
    filterAll: 'All',
    filterPending: 'Pending',
    filterInProgress: 'In progress',
    filterCompleted: 'Done',
    summary: '{{pending}} pending, {{inProgress}} in progress',
    offlineNotice: 'You can keep working. Data will be sent when the connection returns.',
    empty: 'No orders in this state',
    emptyBody: 'Change the filter or pull to refresh.',
    emptyStale: 'Work orders could not be downloaded',
    emptyStaleBody:
      'What you see is the last copy saved on the device. We keep trying; pull to retry now.',
    quantityWithUnit: '{{quantity}} {{unit}}',
    number: 'WO {{number}}',
    executionPending: 'Execution available soon',
    executionPendingBody:
      'Work-order execution (weighing, steps and closing) is coming in an upcoming version.',
  },

  'planta.execution': {
    title: 'Execution',
    startWorkOrder: 'Start the work order',
    resumeWorkOrder: 'Resume the work order',
    starting: 'Starting…',
    step: 'Step {{number}}',
    stepCount: 'Step {{current}} of {{total}}',
    markStepDone: 'Mark step as done',
    stepDone: 'Step done',
    stepPending: 'Pending',
    stepInProgress: 'In progress',
    stepBlockedByPcc: 'You must record the critical control (CCP) to continue',
    blockingStep: 'Blocking step',
    allStepsDone: 'All steps done',
    recordControl: 'Record control',
    targetTemperature: 'Target temperature',
    targetTemperatureLabeled: 'Target temperature: {{value}}',
    estimatedDuration: 'Estimated duration',
    estimatedDurationLabeled: 'Estimated duration: {{value}}',
    unitCelsius: '°C',
    minutes: '{{count}} min',
    noSteps: 'This recipe has no steps',
    todayProduction: 'Today\'s production',
    plannedVsConsumed: 'Planned / Consumed',
    notStartedTitle: 'Work order ready to start',
    notStartedBody: 'Start the work order to reveal the recipe steps.',
    progress_one: '{{done}} of {{count}} step done',
    progress_other: '{{done}} of {{count}} steps done',
    controlBadgeWeight: 'Weighing',
    controlBadgeTimer: 'Timer',
    controlBadgeTemperature: 'Temperature',
    controlBadgePcc: 'CCP',
    pccBlockingBadge: 'Blocking CCP',
    controlRequired: 'Control required',
    recordControlTitle: 'Record control',
    measurementValue: 'Measured value',
    measurementUnit: 'Unit',
    measurementNote: 'Notes',
    measurementNotePlaceholder: 'Notes (optional)',
    saveMeasurement: 'Save control',
    savingMeasurement: 'Saving…',
    measurementSaved: 'Control recorded',
    lastMeasurement: 'Last control: {{value}}',
    criticalLimits: 'Critical limits: {{range}}',
    limitRangeMin: 'min. {{min}}',
    limitRangeMax: 'max. {{max}}',
    limitRangeBetween: '{{min}} – {{max}}',
    withinLimits: 'Within limits',
    outOfLimits: 'Out of limits',
    nonConforming: 'Non-conforming',
    requireIncidentTitle: 'Measurement out of limits',
    requireIncidentBody:
      'The critical control is out of limits. You must log an incident and a corrective action before continuing.',
    logIncident: 'Log incident',
    goToWeighing: 'Go to weighing',
    reportIncidentLink: 'Incident',
    blockedStepHint: 'You must record the critical control before you can mark this step.',
      closedLockTitle: 'Work order closed',
    closedLockBody: 'This work order is finished. Reopen it from the BackOffice to change anything.',
    goToRegister: 'Weighing register',
},

  'planta.weighing': {
    title: 'Weighing and consumption',
    subtitle: 'Record the actual consumption of each ingredient',
    planned: 'Planned',
    real: 'Actual',
    deviation: 'Deviation',
    deviationValue: '{{value}} %',
    deviationWarning: 'Deviation above the limit ({{threshold}} %)',
    deviationWarningShort: 'High deviation',
    confirmWeight: 'Confirm weight',
    weighing: 'Saving…',
    enterRealQuantity: 'Actual quantity consumed',
    selectLot: 'Select the batch',
    selectArticle: 'Select the ingredient',
    ingredient: 'Ingredient',
    noIngredients: 'This recipe has no ingredients',
    recorded: 'Weight recorded',
    recordedAt: 'Recorded at {{time}}',
    consumed: 'Consumed',
    remaining: 'Remaining',
    notStartedTitle: 'Start the work order before weighing',
    notStartedBody: 'You must start the work order to record ingredient consumption.',
    weighIngredient: 'Weigh the ingredient',
    plannedVsReal: 'Planned {{planned}} · Actual {{real}}',
    todayProductionTitle: 'Today\'s production',
    todayProductionEmpty: 'No consumption recorded yet',
    consumedVsPlanned: '{{consumed}} / {{planned}}',
    notWeighedYet: 'Not weighed',
    source: 'Source',
    sourceManual: 'Manual',
    sourceScale: 'Scale',
    save: 'Save consumption',
    saving: 'Saving…',
    saved: 'Consumption recorded',
      theoreticalQuantity: 'Theoretical quantity',
    proposedFromRecipe: 'Proposed by the recipe',
    tolerance: 'Tolerance ±{{value}} %',
    overTolerance: 'Outside tolerance (±{{tolerance}} %)',
    incidentAutoCreated: 'A deviation incident has been raised',
    lotsTitle: 'Batches consumed',
    lotsSubtitle: 'Choose where the raw material comes from',
    lotAvailable: 'Available {{quantity}}',
    lotExpiry: 'Exp. {{date}}',
    lotNoExpiry: 'No expiry',
    noLotsAvailable: 'No batched stock for this ingredient',
    noLotsHint: 'You can still record the consumption; it will have no batch.',
    addLot: 'Add a batch',
    removeLot: 'Remove batch',
    lotQuantity: 'Quantity from this batch',
    totalSelected: 'Total selected {{quantity}}',
    scanLabel: 'Scan the label',
    scanTitle: 'Scan the label QR',
    scanHint: 'Point the camera at the batch QR code',
    scanPermissionTitle: 'Camera permission needed',
    scanPermissionBody: 'To read labels, grant camera access.',
    scanPermissionGrant: 'Grant access',
    applyTheoretical: 'Apply the theoretical quantity',
    applyTheoreticalHint: 'The recipe quantity will be consumed, without weighing',
    sourceTheoretical: 'Theoretical',
    registerOpen: 'Weighing register',
    registerTitle: 'Weighing register',
    registerSubtitle: 'Everything declared on this work order',
    registerEmpty: 'No consumption declared yet',
    editWeighing: 'Correct',
    deleteWeighing: 'Remove',
    voidTitle: 'Why are you correcting this?',
    voidBodyEdit: 'The previous entry is kept, voided, with the reason.',
    voidBodyDelete: 'The entry is kept, voided, with the reason.',
    voidReason: 'Reason',
    voidReasonPlaceholder: 'e.g. mistyped the figure',
    voidReasonRequired: 'A reason is required',
    voidConfirm: 'Void the entry',
    voidedBadge: 'Voided',
    correctedBadge: 'Correction',
    voidedBy: 'Voided by {{name}}',
    alreadyWeighed: 'Already declared',
    sequentialTitle: 'Sequential preparation',
    sequentialSubtitle: 'One ingredient at a time, in recipe order',
    sequentialLocked: 'Finish the previous ingredient to continue',
    sequentialProgress: 'Ingredient {{current}} of {{total}}',
    sequentialAllDone: 'All ingredients are prepared',
    closedTitle: 'Work order closed',
    scaleNote: '×{{factor}} the recipe: the quantities already carry the multiplier.',
    scaleUnknown:
      'This order cannot be scaled (recipe and order do not speak the same unit): the quantities are the recipe base batch.',
    closedBody: 'Consumption on a closed work order cannot be changed.',
    noPermissionToEdit: 'You do not have permission to correct this entry',
},

  'planta.incidents': {
    photoUnavailable: 'Attaching a photo is not available yet.',
    title: 'Incident',
    subtitle: 'Report an incident without stopping work',
    newIncident: 'New incident',
    type: 'Type',
    severity: 'Severity',
    comment: 'Comment',
    photo: 'Photo (optional)',
    addPhoto: 'Add photo',
    photoAttached: 'Photo attached',
    removePhoto: 'Remove photo',
    photoDeferredHint: 'The photo will upload when there is a connection.',
    report: 'Report the incident',
    reporting: 'Sending…',
    reported: 'Incident reported',
    typeRawMaterial: 'Raw material',
    typeEquipment: 'Equipment',
    typeProcess: 'Process',
    typeQuality: 'Quality',
    typeOther: 'Other',
    severityLow: 'Low',
    severityMedium: 'Medium',
    severityHigh: 'High',
    commentPlaceholder: 'Describe what happened',
    nonBlockingHint: 'Reporting an incident does not stop the work order.',
    selectType: 'Choose the type',
    selectSeverity: 'Choose the severity',
    reportAnother: 'Report another',
    backToOt: 'Back to the work order',
    recentIncidents: 'Incidents on this work order',
    reportedAt: 'Reported at {{time}}',
      takePhoto: 'Take a photo',
    retakePhoto: 'Retake the photo',
    photoPermissionDenied: 'Without camera access the photo cannot be taken',
    photoPendingUpload: 'The photo will upload when the connection returns',
    autoDeviationComment: 'Deviation of {{deviation}} on {{article}} (tolerance ±{{tolerance}} %)',
},

  'planta.closing': {
    title: 'Closing the work order',
    subtitle: 'Confirm the actual production and generate the batch',
    realQuantity: 'Actual quantity',
    plannedQuantity: 'Planned quantity',
    enterRealQuantity: 'Actual quantity produced',
    realQuantityRequired: 'Enter the actual quantity produced',
    merma: 'Waste',
    mermaValue: '{{quantity}} ({{percent}} %)',
    mermaValueNoPct: '{{quantity}}',
    overyield: 'Overproduction',
    mermaNone: 'No waste',
    mermaReason: 'Waste reason',
    mermaReasonRequired: 'You must state the waste reason',
    mermaReasonPrompt: 'Choose the waste reason',
    mermaWarning: 'Waste above the limit ({{threshold}} %)',
    'mermaReason.burnt': 'Burnt',
    'mermaReason.dropped': 'Dropped',
    'mermaReason.expired': 'Expired',
    'mermaReason.damage': 'Damaged',
    'mermaReason.loss': 'Loss',
    suggestedExpiry: 'Suggested expiry',
    noExpiry: 'No expiry defined',
    lotNumber: 'Batch number',
    lotPending: 'To be assigned',
    lotPendingHint: 'The final number will be assigned on sync.',
    generateLot: 'Generate the batch',
    generatedLotSection: 'Batch to be generated',
    closing: 'Closing…',
    closed: 'Work order closed',
    completedTitle: 'Work order closed',
    completedBody: 'The batch has been generated and will sync automatically.',
    lotGenerated: 'Batch generated',
    printLabel: 'Print label',
    closeoutNote: 'Closing note',
    packagingLabelsTitle: 'Packaging labels',
    packagingLabelsHint:
      'Every pack of the article comes ticked. Untick what you do not need.',
    packagingLabelCountUnknown: 'Whoever issues the label will set the count',
    packagingLabelCount_one: '{{count}} label',
    packagingLabelCount_other: '{{count}} labels',
    closeoutNotePlaceholder: 'Closing note (optional)',
    consumptionSummary: 'Recorded consumption',
    noConsumption: 'No weighing has been recorded yet.',
    confirmClose: 'Close the work order and generate the batch',
    closeError: 'The work order could not be closed. Try again.',
    backToOrder: 'Back to the work order',
    openCloseScreen: 'Close the work order',
  },

  'planta.datePicker': {
    title: 'Pick a date',
    empty: 'No date',
    previousMonth: 'Previous month',
    nextMonth: 'Next month',
    clear: 'Clear date',
  },

  'planta.reception': {
    tasksTitle: 'Pending',
    tasksNothing: 'Nothing due today and nothing overdue',
    returnsTitle: 'Pending returns',
    returnsEmpty: 'No returns waiting to leave.',
    taskOverdue_one: '{{count}} overdue',
    taskOverdue_other: '{{count}} overdue',
    taskToday_one: '{{count}} today',
    taskToday_other: '{{count}} today',
    taskFuture_one: '{{count}} upcoming',
    taskFuture_other: '{{count}} upcoming',
    taskUndated_one: '{{count}} undated',
    taskUndated_other: '{{count}} undated',
    groupOverdue: 'Overdue',
    groupToday: 'Today',
    groupFuture: 'Upcoming',
    groupUndated: 'No planned date',
    returnPlanned: 'Planned: {{date}}',
    returnChipOverdue: 'Overdue',
    returnChipToday: 'Today',
    returnDetailTitle: 'Return exit',
    returnLot: 'Lot',
    returnSupplierLot: 'Supplier lot',
    returnReason: 'Reason',
    returnObservations: 'Observations',
    returnReception: 'Receiving note {{number}}',
    returnOrder: 'Order {{number}}',
    returnVerifyHint: 'Check that the article, the lot and the quantity are what the carrier is loading.',
    returnBlockedNote: 'These goods are blocked until they leave: production cannot use them.',
    returnConfirm: 'Confirm the exit',
    returnConfirming: 'Confirming…',
    returnExited: 'Exit confirmed: {{date}}, {{time}}',
    returnNumberKnown: 'Return delivery note {{number}}',
    returnNumberPending: 'The return delivery note will be numbered once it syncs.',
    returnNotFound: 'This return is no longer on the list: it may have left already.',
    returnBack: 'Back to returns',
    returnNoPermission: 'You are not allowed to confirm returns.',
    returnError: 'The exit could not be recorded. Please try again.',
    title: 'Raw material receiving',
    supplier: 'Supplier',
    article: 'Article',
    quantity: 'Quantity',
    supplierLotCode: 'Supplier batch',
    expiry: 'Expiry',
    addLine: 'Add article',
    confirm: 'Confirm receiving',
    selectMode: 'How do you want to record the goods-in?',
    newReception: 'New receiving',
    deliveryNote: 'Supplier delivery-note no.',
    deliveryNoteRequired: 'The supplier delivery-note number is required',
    selectSupplier: 'Choose a supplier',
    selectArticle: 'Choose an article',
    lines: 'Lines',
    removeLine: 'Remove',
    lotOptional: 'Supplier batch (optional)',
    expiryOptional: 'Expiry (optional)',
    control: 'Reception control',
    controlOk: 'Conforming',
    controlNok: 'Non-conforming',
    confirmed: 'Receiving confirmed',
    pendingSync: 'Pending sync',
    recent: 'Recent receivings',
    empty: 'No receiving recorded yet',
    noSuppliers: 'No suppliers. Sync when online.',
    noArticles: 'No purchasable articles available.',
    noPendingOrders: 'No order awaiting delivery',
    addAtLeastOneLine: 'Add at least one line with a quantity',
    webUnsupported: 'Receiving is done from the mobile app.',
    scan: 'Scan delivery note',
    scanning: 'Scanning…',
    scanFailed: 'Could not read the delivery note. Enter it manually.',
    scanUnmatched_one: '{{count}} line not recognized. Add it manually.',
    scanUnmatched_other: '{{count}} lines not recognized. Add them manually.',
    print: 'Print label',
    printUnsupported: 'Printing requires an app build with a printer.',
    printedTo: 'Label sent to {{printer}}',
    printNoPrinter:
      'No printer is configured. An administrator needs to add one in the Backoffice.',
    printError:
      'Could not print to {{printer}}. Check that it is on and on the same network.',
    ordered: 'Ordered',
    receivedSoFar: 'Already received',
    pendingQuantity: 'Pending',
    orderedConversion: '{{ordered}} × {{factor}} = {{total}}',
    quantityWithBase: '{{quantity}} ({{base}})',
    receivedQuantity: 'Received quantity',
    receivedQuantityIn: 'Received quantity ({{unit}})',
    equalsBase: '= {{total}}',
    entryUnit: 'Quantity unit',
    noConversion: 'The «{{unit}}» packaging has no conversion on the article card. Enter the quantity in {{base}}.',
    receiveOrder: 'Receive an order',
    receiveWithoutOrder: 'Receive without an order',
    ordersToReceive: 'Orders to receive',
    windowDue: 'Due by today',
    windowWeek: 'This week',
    windowAll: 'All',
    statusAll: 'All statuses',
    statusAwaiting: 'Awaiting delivery',
    statusPartial: 'Partially delivered',
    allSuppliers: 'All suppliers',
    expectedDelivery: 'Expected: {{date}}',
    noExpectedDelivery: 'No expected delivery date',
    overdue: 'Overdue',
    noOrdersMatch: 'No orders match these filters',
    clearFilters: 'Clear filters',
    entry: 'Entry {{number}}',
    removeEntry: 'Remove the entry',
    addEntry: 'Add another entry',
    thisReceipt: 'On this delivery note',
    willRemainPending: 'Will remain pending',
    expiryEstimatedLabel: 'Estimated expiry',
    expiryEstimatedHint_one: 'Suggested: reception + {{count}} day of shelf life. Change it if the supplier gives a real one.',
    expiryEstimatedHint_other: 'Suggested: reception + {{count}} days of shelf life. Change it if the supplier gives a real one.',
    lineSummary: 'Ordered {{ordered}} · Already received {{received}} · Pending {{pending}}',
    entriesNone: 'No entries yet',
    entriesSummary_one: '{{count}} entry · {{total}}',
    entriesSummary_other: '{{count}} entries · {{total}}',
    lineNeedsNote: 'Note missing',
    expandLine: 'Expand the line',
    collapseLine: 'Collapse the line',
    exceptionOverQuantity: 'That is {{excess}} more than was missing. It will be recorded as an exception.',
    exceptionOutsideOrder: 'Article not ordered but being received. It will be recorded as an exception and a reason is required.',
    pickerTitle: 'Add an article',
    pickerClose: 'Close',
    pickerSearch: 'Search by name or code',
    pickerUnrequestedHelp: 'Article not ordered but being received. A reason is required.',
    pickerNoSupplierArticles: 'This supplier has no linked articles.',
    pickerNoMatch: 'No article matches the search',
    addArticleRestricted: 'Only a manager can add an article that is not on the order.',
    unrequestedNote: 'Why are you receiving it?',
    unrequestedNoteHint: 'E.g. the supplier added it to the delivery note',
    scanUnrequestedSkipped_one: '{{count}} line for an article that is not on the order was not added: only a manager can add it.',
    scanUnrequestedSkipped_other: '{{count}} lines for articles that are not on the order were not added: only a manager can add them.',
    flagException: 'Mark as an exception',
    unflagException: 'Remove the mark',
    flaggedException: 'Exception',
    exceptionNote: "What's wrong?",
    exceptionNoteHint: 'E.g. 20 kg sacks instead of 25',
    exceptionNoteRequired: 'Say what is wrong',
    exceptionNotesMissing: 'Say what is wrong with the entries marked as an exception.',
    controlNote: 'Observation',
    controlNoteHint: 'What is not conforming?',
    createIncident: 'Create an incident',
    createIncidentHint: 'It will be tied to this reception and the manager will see it.',
    controlNoteRequired: 'Say what is wrong to create the incident',
    controlNotesMissing: 'Say what is wrong with the non-conforming entries that will open an incident.',
    filterByWindow: 'Filter by expected delivery',
    filterByStatus: 'Filter by status',
    filterBySupplier: 'Filter by supplier',
    orderNumber: 'Order {{number}}',
    orderNoLongerPending: 'This order is no longer awaiting delivery.',
    historyOpen: 'Reception history',
    historyTitle: 'Reception history',
    historySearch: 'Search by supplier, delivery note or order',
    historyFilterByWindow: 'Filter by date',
    historyWindowToday: 'Today',
    historyWindowWeek: '7 days',
    historyWindowMonth: '30 days',
    historyWindowAll: 'All',
    historyEmpty: 'You have not made any reception on this device yet',
    historyNoMatch: 'No reception matches the search',
    historyDeliveryNote: 'Delivery note {{number}}',
    historyNoSupplier: 'No supplier',
    historyCapped: 'Showing the {{count}} most recent. Narrow the search to find others.',
    historyScopeNote: 'It lists the receptions made on this device.',
    printCopies_one: 'Print {{count}} label',
    printCopies_other: 'Print {{count}} labels',
    copiesLabel: 'Copies',
    copiesLess: 'Fewer copies',
    copiesMore: 'More copies',
  },

  'planta.sales': {
    title: 'Sales',
    prepareCta: 'Prepare orders',
    newOrderCta: 'New order',
    recent: 'Recent activity',
    empty: 'No orders yet',
    newOrderTitle: 'New sales order',
    client: 'Customer',
    selectClient: 'Pick a customer',
    noClients: 'No customers. Sync while online.',
    deliveryDate: 'Delivery date',
    notes: 'Notes',
    lines: 'Lines',
    addLine: 'Add article',
    selectArticle: 'Pick an article',
    noArticles: 'No sellable articles available.',
    quantity: 'Quantity',
    removeLine: 'Remove',
    addAtLeastOneLine: 'Add at least one line with a quantity',
    create: 'Create the order',
    created: 'Order created',
    pendingSync: 'Pending sync',
    available: 'Available: {{quantity}}',
    committed: 'Committed: {{quantity}}',
    insufficientStock: 'Insufficient stock (warning, non-blocking)',
    webUnsupported: 'Sales are managed from the mobile app.',
  },

  'planta.picking': {
    noLotsToShip:
      'No lot is available to ship these articles. Check that the stock was recorded with a lot: stock created by a manual adjustment cannot be shipped.',
    title: 'Prepare orders',
    pendingOrders: 'Pending orders',
    noPendingOrders: 'No orders awaiting preparation',
    filterToday: 'Today',
    filterWeek: 'This week',
    filterAll: 'All',
    deliveryFor: 'Delivery: {{date}}',
    ordered: 'Ordered',
    prepared: 'Prepared',
    lot: 'Lot',
    suggestedLot: 'Suggested lot (FEFO)',
    changeLot: 'Change lot',
    noLots: 'No lots available',
    expiresOn: 'Expires: {{date}}',
    available: 'Available: {{quantity}}',
    insufficientStock: 'Insufficient stock (warning, non-blocking)',
    ship: 'Ship',
    shipConfirmTitle: 'Ship the order?',
    shipConfirmBody: 'Stock will be deducted and the sales delivery note created.',
    partialHint: 'Unserved quantities stay pending (backorder).',
    shipped: 'Order shipped',
    pendingSync: 'Pending sync',
    webUnsupported: 'Order preparation happens in the mobile app.',
  },

  'planta.config': {
    title: 'Incomplete configuration',
    body: 'Required environment variables for connecting to the server are missing. The app cannot start until they are set in the deployment.',
    missingLabel: 'Missing variables:',
    hint: 'Set them in the project configuration (Vercel) and redeploy.',
  },

  'backoffice.agenda': {
    title: 'Planning',
    subtitle: 'Work orders planned by day and shift',
    byShift: 'By shift',
    byDay: 'By day',
    publish: 'Publish',
    publishing: 'Publishing…',
    draft: 'Draft',
    published: 'Published',
    create: 'Create WO',
    date: 'Date',
    today: 'Today',
    previousDay: 'Previous day',
    nextDay: 'Next day',
    noShift: 'No shift',
    unassigned: 'Unassigned',
    quantityWithUnit: '{{quantity}} {{unit}}',
    empty: 'There are no work orders for this day. Create one to plan production.',
    emptyFiltered: 'No work order matches this filter for this day.',
    loadError: 'The work orders could not be loaded',
    publishError: 'The work order could not be published',
    // Quick filters (dashboard shortcuts)
    filterAll: 'All',
    filterInProgress: 'In progress',
    filterDrafts: 'Drafts',
    // Monthly production calendar
    previousMonth: 'Previous month',
    nextMonth: 'Next month',
    dayHeading: 'Production for {{date}}',
    ordersCount: '{{count}} WO',
    moreOrders: '+{{count}} more',
  },

  'backoffice.workOrders': {
    title: 'Work orders',
    create: 'Create WO',
    assign: 'Assign',
    assignToUser: 'Assign to operator',
    assignToSection: 'Assign to section',
    sectionWithoutMembers:
      'Nobody is assigned to the "{{name}}" section. This work order will not appear on any device until you assign someone in Settings · Users.',
    cancel: 'Cancel',
    cancellationReason: 'Cancellation reason',
    lowStockWarning: 'Insufficient stock (warning, does not block)',
    // Create WO (screen 09)
    createTitle: 'New work order',
    createSubtitle: 'Plan a work order from a published recipe',
    backToAgenda: 'Back to the schedule',
    sectionRecipe: 'Recipe and product',
    sectionPlanning: 'Quantity and planning',
    sectionAssignment: 'Assignment',
    sectionResources: 'Resources',
    sectionResourcesHint:
      'The recipe asks for these resources. Choose which one this work order will use; if you don’t know yet, you can do it from the work order detail.',
    resourceNeed: '{{quantity}} × {{type}}',
    resourceDuration: '{{minutes}} min',
    resourceMachine: 'Assigned resource',
    resourceNotChosen: 'Not chosen',
    recipe: 'Recipe',
    recipeHint: 'Only recipes with an active published version',
    selectRecipe: 'Select a recipe',
    pinnedVersionNote:
      'The work order pins the active recipe version at this moment; it will not change even if the recipe is revised later.',
    workOrderType: 'Work order type',
    plannedQuantity: 'Planned quantity',
    plannedUnit: 'Unit',
    plannedUnitHint: 'Set by the recipe: it yields {{quantity}} {{unit}}.',
    plannedUnitNoRecipe: 'Set by the recipe you pick.',
    scaleHint: 'This is {{factor}} times the recipe: the operator will weigh every ingredient multiplied by that number.',
    selectUnit: 'Select a unit',
    productionDate: 'Production date',
    shift: 'Shift',
    noShift: 'No shift',
    assignTo: 'Assign to',
    assignToHint: 'A work order is assigned to a person OR to a section',
    selectMember: 'Select an operator',
    selectSection: 'Select a section',
    saveDraft: 'Save draft',
    saveAndPublish: 'Save and publish',
    publishing: 'Publishing…',
    publishHint:
      'Save as a draft to plan without showing it on the schedule, or save and publish so the plant app can execute it.',
    shortIngredient:
      '{{name}}: {{required}} needed, {{available}} available',
    fixValidation: 'Review the marked fields before saving.',
    createError: 'The work order could not be created',
    assignmentError:
      'The work order was created but the assignment could not be saved. Review it from the schedule.',
    cancelError: 'The work order could not be cancelled',
    noUnits:
      'No units have been defined for the workshop yet. Define them in Settings before creating work orders.',
    noPlannableRecipes:
      'There is no recipe with a published version. Publish a recipe before creating a work order.',
    // Work order type enum labels
    'type.production': 'Production',
    'type.packaging': 'Packaging',
    // Work order status enum labels
    'status.planned': 'Planned',
    'status.in_progress': 'In progress',
    'status.completed': 'Completed',
    'status.cancelled': 'Cancelled',
    // Work order step status enum labels (OT detail / traceability)
    'stepStatus.pending': 'Pending',
    'stepStatus.in_progress': 'In progress',
    'stepStatus.completed': 'Done',
    'stepStatus.skipped': 'Skipped',
    // Weighing source enum labels
    'source.manual': 'Manual',
    'source.scale': 'Scale',
    'source.theoretical': 'Theoretical',
  },

  'backoffice.workOrderDetail': {
    editTitle: 'Edit planning',
    editBody: 'You can correct the quantity, date, shift and who works on it while it has not been started. The recipe and its version cannot be changed: to produce something else, cancel the OT and create a new one.',
    subtitle: 'Work order traceability',
    backToAgenda: 'Back to agenda',
    loadError: 'Could not load the work order',
    notFound: 'Work order not found.',
    // Header
    article: 'Product',
    plannedQuantity: 'Planned quantity',
    realQuantity: 'Actual quantity',
    merma: 'Waste',
    mermaValue: '{{quantity}} {{unit}} ({{pct}}%)',
    assignment: 'Assignment',
    unassigned: 'Unassigned',
    shift: 'Shift',
    noShift: 'No shift',
    productionDate: 'Production date',
    publishedAt: 'Published',
    startedAt: 'Started',
    completedAt: 'Completed',
    cancellationReason: 'Cancellation reason',
    empty: '—',
    quantityWithUnit: '{{quantity}} {{unit}}',
    // Steps
    sectionSteps: 'Production steps',
    stepsEmpty: 'The operator has not activated any step yet.',
    sectionResources: 'Resources',
    resourcesEmpty: 'This work order’s recipe asks for no resources.',
    resourceSaveError: 'The resource could not be saved. Try again.',
    stepBlocking: 'Blocking (CCP)',
    stepCompletedAt: 'Done {{at}}',
    stepCompletedBy: 'by {{name}}',
    recordMeasured: 'Measurement',
    recordNote: 'Note',
    // Weighings
    sectionWeighings: 'Weighings',
    weighingsEmpty: 'No weighing recorded yet.',
    colArticle: 'Raw material',
    colPlanned: 'Planned',
    colReal: 'Actual',
    colDeviation: 'Deviation',
    colSource: 'Source',
    deviationValue: '{{pct}}%',
    // Incidents
    sectionIncidents: 'Incidents',
    incidentsEmpty: 'No incident reported for this WO.',
    incidentResolved: 'Resolved {{at}}',
    // Lot
    sectionLot: 'Produced lot',
    lotEmpty: 'No lot generated yet (the WO has not been closed).',
    packagingLabelsTitle: 'Packaging labels',
    packagingUnitUnknown: 'Pack',
    packagingLabelPrint_one: 'Generate {{packaging}} ({{count}} label)',
    packagingLabelPrint_other: 'Generate {{packaging}} ({{count}} labels)',
    packagingLabelPrinting: 'Generating…',
    packagingLabelIssued: 'Already issued',
    packagingLabelError:
      'The {{packaging}} selection could not be saved, or its label could not be generated. Try again.',
    packagingLoadError:
      'The packaging labels for this order could not be read. Nothing can be changed while that is true: reload the page.',
    packagingNothingRequested:
      'This packaging order was closed with no pack requested. Tick the ones you need so their labels can be generated.',
    packagingNoPermission:
      'Issuing labels is for the production manager or an administrator.',
    packagingCopiesRange: 'Enter a number of labels between 1 and {{max}}.',
    packagingTooManyCopies:
      '{{total}} are needed in total, more than the {{max}} that can be generated at once. Generate them in batches.',
    packagingFactorDrift:
      '{{requested}} units per pack were requested; the article now says {{configured}}.',
    packagingIssuedUnknown:
      'Whether labels have been issued is not known yet',
    packagingLabelNoLotYet:
      'The work order is not closed yet: packs can be ticked, but labels are only generated once there is a lot.',
    packagingLabelNoTemplate:
      'No usable label format for this article. Define one in Settings · Label formats, or pick one on the article card.',
    packagingUnconfigured: 'No longer a pack of the article',
    packagingIssuedLocked: 'Labels have already been issued: the request cannot be undone',
    lotQuantity: 'Produced quantity',
    lotExpiry: 'Expiry',
    lotNoExpiry: 'No expiry',
    // Common
    recordedBy: 'Recorded by',
    recordedAt: 'Recorded',
    by: 'by {{name}}',
      sourceTheoretical: 'Theoretical',
    voided: 'Voided',
    voidedReason: 'Reason: {{reason}}',
    tolerance: 'Tolerance',
},

  'backoffice.dashboard': {
    title: 'Dashboard',
    subtitle: 'Operational overview of the workshop · {{date}}',
    createOt: 'Create WO',
    loadError: 'Some dashboard data could not be loaded',
    // Planning KPIs (planning role)
    planningSection: 'Today\'s planning',
    otTodayLabel: 'Today\'s WOs',
    otTodayHint: 'Work orders planned for today',
    otByStatusLabel: 'WOs by status',
    draftsLabel: 'Pending drafts',
    goToAgenda: 'View schedule',
    // Read KPIs (any member)
    operationsSection: 'Operations',
    lowStockItems: 'Articles below minimum',
    lowStockEmpty: 'No article below minimum. All in order.',
    recentLots: 'Latest batches produced',
    recentLotsEmpty: 'No batch has been produced yet.',
    goToExistences: 'View stock',
    // Visual dashboard (KPIs + charts)
    production: 'Production',
    inProgressLabel: 'In progress',
    kpiActive: 'active',
    kpiPending: 'pending',
    productionChartSubtitle: 'Lots produced · last 14 days',
    thisPeriod: 'This period',
    otByStatusSubtitle_one: '{{count}} order · last 30 days',
    otByStatusSubtitle_other: '{{count}} orders · last 30 days',
    donutTotal: 'total',
    lotsProduced: 'Lots produced',
    lotsChartSubtitle: 'By day · last 7 days',
    lotsTotal_one: '{{count}} lot',
    lotsTotal_other: '{{count}} lots',
    // Dashboard personalization (edit mode, saved in the browser)
    customize: 'Customize the dashboard',
    customizeDone: 'Done',
    customizeReset: 'Reset',
    customizeHint:
      'Hide, show and reorder the cards with each card’s controls. Changes are saved in this browser.',
    moveEarlier: 'Move earlier',
    moveLater: 'Move later',
    showCard: 'Show card',
    hideCard: 'Hide card',
    // Sales and Purchases (KPIs + panels)
    salesTitle: 'Sales',
    salesSubtitle: 'Open orders · by delivery date',
    salesPendingLabel: 'Pending fulfilment',
    salesEmpty: 'No orders pending fulfilment.',
    goToSales: 'View sales',
    deliveriesToday: '{{count}} today',
    purchasesTitle: 'Purchases',
    purchasesSubtitle: 'Open purchase orders · by expected date',
    purchasesOpenLabel: 'Open purchases',
    purchasesEmpty: 'No open purchase orders.',
    goToPurchases: 'View purchases',
    noDate: 'No date',
    // Incidents (KPI + panel, with realtime)
    incidentsTitle: 'Recent incidents',
    incidentsSubtitle: 'Pending resolution',
    incidentsEmpty: 'No pending incidents.',
    goToIncidents: 'View incidents',
    kpiAttention: 'attention',
    kpiTitleOtToday: 'Today\'s orders',
    kpiDescOtToday: 'Planned for today',
    kpiTitleInProgress: 'In progress',
    kpiDescInProgress: 'Orders in production',
    kpiTitleProduction: 'Production',
    kpiDescProduction: 'Batches produced today',
    kpiTitleSales: 'Awaiting delivery',
    kpiDescSales: 'Open orders',
    kpiTitlePurchases: 'Purchases',
    kpiDescPurchases: 'Purchases in progress',
    kpiTitleDrafts: 'Recipes',
    kpiDescDrafts: 'Pending drafts',
    kpiTitleLowStock: 'Stock',
    kpiDescLowStock: 'Items below minimum',
    kpiTitleIncidents: 'Incidents',
    kpiDescIncidents: 'Open incidents',
    // Legacy (not currently used)
    productionToday: 'Today\'s production',
    openIncidents: 'Open incidents',
    expiringSoon: 'Expiring soon',
  },

  'backoffice.production': {
    title: 'Production',
    subtitle: 'Batches produced by day',
    date: 'Date',
    today: 'Today',
    previousDay: 'Previous day',
    nextDay: 'Next day',
    lotNumber: 'Lot',
    product: 'Product',
    quantity: 'Quantity',
    expiry: 'Expiry',
    noExpiry: 'No expiry',
    status: 'Status',
    quantityWithUnit: '{{quantity}} {{unit}}',
    empty: 'No batch was produced on this day.',
    loadError: 'The produced batches could not be loaded',
  },

  'backoffice.incidents': {
    title: 'Incidents',
    subtitle: 'Incidents reported from the shop floor',
    filterAll: 'All',
    filterOpen: 'Open',
    filterResolved: 'Resolved',
    statusOpen: 'Open',
    statusResolved: 'Resolved',
    severity: 'Severity',
    severityLow: 'Low',
    severityMedium: 'Medium',
    severityHigh: 'High',
    type: 'Type',
    typeRawMaterial: 'Raw material',
    typeEquipment: 'Equipment',
    typeProcess: 'Process',
    typeQuality: 'Quality',
    typeOther: 'Other',
    workOrder: 'WO',
    noWorkOrder: 'No WO',
    reception: 'Reception',
    deliveryNoteRef: 'delivery note {{number}}',
    supplier: 'Supplier',
    purchaseOrder: 'Order',
    article: 'Article',
    lot: 'Lot',
    supplierLotRef: 'supplier lot {{code}}',
    reportedBy: 'Reported by',
    reportedAt: 'Reported',
    resolvedAt: 'Resolved',
    unknownUser: 'Operator',
    resolve: 'Mark as resolved',
    resolving: 'Resolving…',
    resolveError: 'The incident could not be resolved',
    live: 'Live',
    empty: 'No incidents.',
    emptyOpen: 'No open incidents. All in order.',
    loadError: 'The incidents could not be loaded',
    toastTitle: 'New incident',
    toastView: 'View',
    resolveTitle: 'Close the incident',
    resolveBody:
      'Say how it was resolved. Closing it does not move stock: if goods must go back, a return is created separately.',
    resolutionLabel: 'Resolution',
    resolutionNoAction: 'No action',
    resolutionAccepted: 'Accepted',
    resolutionTotalReturn: 'Full return',
    resolutionPartialReturn: 'Partial return',
    resolutionCredit: 'Credit / compensation',
    resolvedWith: 'Resolved: {{resolution}}',
    resolutionNoteLabel: 'Note (optional)',
    resolutionNotePlaceholder: 'What was agreed with the supplier',
    closeAction: 'Close the incident',
    createReturn: 'Create a return',
    resolveErrorResolutionRequired: 'Choose how it was resolved.',
    resolveErrorAlreadyResolved: 'Someone else has already closed this incident.',
    resolveErrorForbidden: 'You do not have permission to close incidents.',
  },

  'backoffice.articles': {
    title: 'Articles',
    create: 'Create article',
    sku: 'Code',
    name: 'Name',
    type: 'Type',
    shelfLife: 'Shelf life (days)',
    allergens: 'Allergens',
    allergensDerived: 'Allergens derived automatically from the BOM',
  },

  'backoffice.common': {
    appName: 'Obrava Backoffice',
    themeLight: 'Light',
    themeDark: 'Dark',
    saving: 'Saving…',
    creating: 'Creating…',
    actionError: 'The action could not be completed',
    placeUnavailable: '{{place}} (unavailable)',
    supplierInactive: '{{name}} (inactive)',
    supplierUnavailable: 'Supplier unavailable',
    loadError: 'The data could not be loaded',
    readOnly: 'Read only',
    noPermission: 'You do not have permission for this action',
    optional: 'Optional',
    none: 'None',
    dashboard: 'Dashboard',
    agenda: 'Planning',
    production: 'Production',
    incidents: 'Incidents',
    catalog: 'Catalog',
    articles: 'Articles',
    recipes: 'Recipes',
    existences: 'Stock',
    purchases: 'Purchasing',
    purchaseOrders: 'Purchase orders',
    purchaseDeliveryNotes: 'Purchase delivery notes',
    receptionExceptions: 'Exceptions',
    purchaseReturns: 'Returns',
    purchaseTemplates: 'Templates',
    thirdParties: 'Third parties',
    clients: 'Clients',
    suppliers: 'Suppliers',
    team: 'Team',
    users: 'Users',
    operators: 'Operators',
    sales: 'Sales',
    salesOrders: 'Sales orders',
    technicalSheet: 'Tech sheet',
    import: 'Import',
    settings: 'Settings',
    language: 'Language',
    theme: 'Theme',
    account: 'Account & profile',
    passwordTitle: 'Password',
    passwordHint: 'To change it we email you a link where you choose the new password.',
    passwordCta: 'Email me a link to change it',
    copyVersion: 'Copy version',
    versionCopied: 'Version copied',
    collapse: 'Collapse',
    navSectionOperative: 'Operations',
    navSectionProduct: 'Product',
    navSectionStock: 'Stock & movements',
    navSectionPurchases: 'Purchasing',
    navSectionSales: 'Sales',
    navSectionQuality: 'Quality',
    navNonConformities: 'Non-conformities',
    navSelfChecks: 'Self-checks',
    navEvidence: 'Evidence',
    navStockCounts: 'Stock counts',
    navSectionData: 'Data',
    navSectionManagement: 'Management',
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
    comingSoon: 'Coming soon',
    obrador: 'Workshop',
    switchObrador: 'Switch workshop',
    switchingObrador: 'Switching workshop…',
    switchObradorError: 'Could not switch workshop',
    activeObrador: 'Active workshop',
    memberDeleted: '{{name}} (deactivated)',
  },

  'backoffice.auth': {
    title: 'Obrava Backoffice',
    subtitle: 'Everything, under control.',
    signInTitle: 'Sign in',
    email: 'Email',
    password: 'Password',
    signInCta: 'Sign in',
    signingIn: 'Signing in…',
    signInError: 'Incorrect credentials',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    noTenantTitle: 'Account without a workshop',
    noTenantBody:
      'This account has no workshop assigned. Contact your workshop administrator.',
    noBackofficeAccessTitle: 'No access to the Backoffice',
    noBackofficeAccessBody:
      'At {{obrador}}, your roles only open the floor app. If you also need the Backoffice, ask your administrator to add an office role, such as Viewer.',
    noRolesBody:
      'You have no role assigned at {{obrador}}. Ask your administrator to assign you one.',
    backToSignIn: 'Back to sign in',
    rememberMe: 'Keep me signed in on this computer',
    rememberMeHint:
      'Enable this only on computers you use regularly. On shared computers, leave it off and the session will end when you close the browser.',
    forgotPassword: 'Forgot your password?',
    resetTitle: 'Reset your password',
    resetEmailStep: 'Enter your email and we will send you a link to choose a new password.',
    resetSendCta: 'Send me the link',
    resetSending: 'Sending…',
    resetNewPassword: 'New password',
    resetConfirmPassword: 'Confirm password',
    resetSubmitCta: 'Save the new password',
    resetSubmitting: 'Saving…',
    resetSuccess: 'Password updated. You can sign in now.',
    resetSendError: 'The email could not be sent. Check the address and try again.',
    resetLinkSent: 'We have sent an email to {{email}}. Open the link in it to choose a new password (check your spam folder too).',
    resetLinkInvalid: 'This link has expired or has already been used. Request a new one.',
    resetRetry: 'Did not get it? Try again',
    resetPasswordWeak: 'The password must be at least 6 characters.',
    resetPasswordMismatch: 'The passwords do not match.',
    resetGenericError: 'The password could not be reset.',
    accessLoadErrorTitle: 'Your access could not be loaded',
    accessLoadErrorBody:
      'We could not check what you can open at {{obrador}}, and until we know we show no screen. Try again; if the problem persists, let your administrator know.',
    accessLoadRetry: 'Retry',
    noMenuAccessTitle: 'No options available',
    noMenuAccessBody:
      'All Backoffice options are hidden for you at {{obrador}}. Ask your administrator to turn some back on.',
  },

  'backoffice.catalog': {
    title: 'Catalog',
    subtitle: 'Workshop articles: purchase, semi-finished and sale',
    newArticle: 'New article',
    import: 'Import articles',
    importSupplierArticles: 'Import article-supplier links',
    searchPlaceholder: 'Search by code or name',
    filterType: 'Type',
    typeAll: 'All',
    typePurchase: 'Purchase',
    typeIntermediate: 'Semi-finished',
    typeSale: 'Sale',
    filterCategory: 'Category',
    filterTax: 'Tax',
    categoryAll: 'All',
    colSku: 'Code',
    colName: 'Name',
    colType: 'Type',
    colCategory: 'Category',
    colActions: 'Actions',
    techSheetAction: 'Tech sheet',
    colStock: 'Stock',
    empty: 'There are no articles yet',
    emptyFiltered: 'No article matches the filters',
    showInactive: 'Show deactivated',
    inactiveBadge: 'Inactive',
    colNature: 'Nature',
    'nature.is_purchased': 'Purchased',
    'nature.is_manufactured': 'Manufactured',
    'nature.is_sold': 'Sold',
    noCategory: 'No category',
    noTax: 'No tax',
    selectAll: 'Select every visible article',
    selectedCount: 'Selected: {{count}}',
    clearSelection: 'Clear selection',
    bulkType: 'Change type',
    bulkNature: 'Change nature',
    bulkCategory: 'Change category',
    bulkTax: 'Change tax',
    bulkApply: 'Apply change',
    bulkConfirm: 'Articles affected by this change: {{count}}.',
    bulkNatureHint: 'Only the flags you change are written; the rest are left exactly as they are.',
    bulkNatureManualNote:
      'Turning «manufactured» off also moves these articles to manual food-safety information.',
    bulkNatureKeep: 'Leave it as it is',
    bulkNatureOn: 'Turn it on',
    bulkNatureOff: 'Turn it off',
    bulkMixed: 'Mixed values',
  },

  'backoffice.articleForm': {
    linkStock: 'Existences',
    linkPurchases: 'Purchases',
    linkSales: 'Sales',
    unsavedChanges: 'Unsaved changes',
    backToCatalog: 'Back to catalog',
    foodSafetyMode: 'Information source',
    foodSafetyModeHint: 'How the tech sheet\'s ingredients, allergens and nutrition are obtained.',
    foodSafetyMode_manual: 'Manual — declared on this card',
    foodSafetyMode_derived: 'Calculated from the recipe',
    foodSafetyModeManualOnly: 'Manual. A raw material or auxiliary product has no recipe to derive the information from.',
    safetyNotesLabel: 'Other information',
    derivedNoRecipe: 'This article has no published recipe version yet, so the information cannot be calculated. Publish the recipe or switch the source to manual.',
    derivedFromRecipe: 'Calculated from recipe version {{version}}.',
    derivedNutritionEmpty: 'No raw material in the recipe declares nutrition information.',
    derivedNutritionBasisYield: 'Per 100 g of finished product, against the recipe yield.',
    derivedNutritionBasisBom: 'Per 100 g, against the sum of the ingredients (the recipe has no mass yield).',
    derivedNutritionCoverage: 'Only {{percent}}% of the recipe weight declares nutrition information: the values under-declare.',
    titleNew: 'New article',
    titleEdit: 'Article record',
    subtitleNew: 'Create a catalog article',
    sectionIdentity: 'Identification',
    sectionClassification: 'Classification and behavior',
    sectionUnits: 'Units and conversions',
    sectionStock: 'Stock and replenishment',
    sectionAllergens: 'Allergens',
    sectionDanger: 'Deactivate article',
    sku: 'Code (SKU)',
    name: 'Commercial name',
    articleType: 'Type',
    category: 'Category',
    barcode: 'Barcode',
    isPurchased: 'Purchased',
    isManufactured: 'Manufactured',
    isSold: 'Sold',
    baseUnit: 'Base unit (stock)',
    baseUnitPlaceholder: 'Select a unit',
    recipeUnit: 'Recipe unit',
    recipeUnitHint:
      'The unit the recipe uses for this raw material. Stock and purchasing stay in the base unit.',
    recipeUnitSameAsBase: 'Same as the base unit',
    recipeUnitSameAsBaseNamed: 'Same as the base ({{code}})',
    recipeUnitUnavailable: 'Unit not available',
    recipeFactor: 'Recipe equivalence',
    recipeFactorLead: '1 {{base}} equals',
    recipeFactorAria: 'Amount of {{recipe}} equal to 1 {{base}}',
    recipeFactorExample:
      'A recipe that uses 1 {{recipe}} will deduct {{quantity}} {{base}} from stock.',
    recipeFactorRequired: 'Enter the equivalence: the recipe unit differs from the base unit.',
    recipeFactorNotPositive: 'The equivalence must be a number greater than zero.',
    recipeUnitChangeWarning_one:
      'There is {{count}} recipe line that uses this article in {{unit}}. If you change the recipe unit it will lose its equivalence with the base unit and stock will be deducted 1:1.',
    recipeUnitChangeWarning_other:
      'There are {{count}} recipe lines that use this article in {{unit}}. If you change the recipe unit they will lose their equivalence with the base unit and stock will be deducted 1:1.',
    validationError: 'Not saved: check the fields marked in red.',
    validationErrorField: 'Not saved: check “{{field}}”.',
    noUnits:
      'No units have been defined for the workshop yet. Define them in Settings before creating articles.',
    isVariableConversion: 'Variable conversion',
    isVariableConversionHint:
      'Article with no fixed equivalence between the unit it is counted in and the one it is measured in (e.g. cut pieces): it is weighed on every movement.',
    shelfLifeDays: 'Shelf life (days)',
    shelfLifeHint: 'Determines the suggested batch expiry when closing the work order.',
    vatRate: 'VAT (%)',
    tax: 'Tax',
    taxHint: 'Picked from the taxes master (Settings · Taxes)',
    taxNone: 'No tax',
    taxUnavailable: 'Deactivated tax',
    valueUnavailable: 'Unavailable',
    noTaxesMaster: 'No taxes configured. Create one in Settings · Taxes.',
    minStock: 'Minimum stock',
    maxStock: 'Maximum stock',
    minStockHint: 'Warning for stock below minimum (never blocks).',
    defaultSupplier: 'Default supplier',
    defaultPlace: 'Default place',
    defaultPlaceHint: 'Where existences land when they are received or produced, without asking anyone. It can be a zone, a section or a location.',
    purchasePrice: 'Purchase price',
    allergensManualHint:
      'Mark the allergens present in the raw material or packaging (contains / traces).',
    allergensDerivedHint:
      'The allergens of a finished or semi-finished product are derived automatically from the recipe BOM. They are not edited manually.',
    allergensDerivedEmpty:
      'No derived allergens yet. They will be aggregated from the BOM once the recipe is defined (Wave 1 — M3).',
    presenceContains: 'Contains',
    presenceTraces: 'Traces',
    presenceNone: 'Absent',
    save: 'Save the article',
    saved: 'Article saved',
    saveError: 'The article could not be saved',
    allergensSaveError:
      'The article was saved, but the allergens could not be saved. Try again.',
    conversionsSaveError:
      'The article was saved, but the conversions could not be saved. Try again.',
    create: 'Create article',
    created: 'Article created',
    createdBody: 'The article was added to the catalog.',
    createError: 'The article could not be created',
    cancel: 'Cancel',
    deactivate: 'Deactivate',
    deactivateTitle: 'Deactivate the article',
    deactivateBody:
      'Reversible soft delete: the article disappears from the active catalog but is kept for traceability. State the reason (required).',
    deactivatedBanner: 'This article is deactivated.',
    reactivate: 'Reactivate the article',
    notFound: 'The article was not found',
    addIngredient: 'Add ingredient',
    addPackagingUnit: 'Add packaging unit',
    addSupplier: 'Add supplier',
    complianceLabel: 'Legislation & compliance',
    complianceSaveError: 'Could not save the compliance claims.',
    conservationCondition: 'Conservation condition',
    containerNotes: 'Container notes',
    containerSize: 'Size',
    containerType: 'Container type',
    containerUnitsPer: 'Units per container',
    containerWeight: 'Weight (g)',
    createIngredient: 'Create ingredient',
    createIngredientError: 'Could not create the ingredient.',
    defaultLabelTemplate: 'Default label format',
    description: 'Description',
    descriptionHint: 'Short description shown on the tech sheet',
    image: 'Product image',
    imageHint: 'JPG or PNG, up to 5 MB',
    imageInvalidType: 'The file is not an image.',
    imageTooLarge: 'The image exceeds 5 MB.',
    imageUploadError: 'Could not upload the image.',
    imageUploading: 'Uploading…',
    ingredient: 'Ingredient',
    ingredientPercentage: '%',
    ingredientsHint: 'In decreasing weight order; shown on the tech sheet',
    ingredientsLabel: 'Declared composition',
    ingredientsSaveError: 'Could not save the composition.',
    isManufacturedHint: 'Manufactured from an own recipe',
    isPurchasedHint: 'Purchased from a supplier',
    isSoldHint: 'Sold to a customer',
    isStockTracked: 'Track stock',
    isStockTrackedHint: 'When off, the article generates no stock movements or alerts (e.g. tap water) but stays available in recipes and the tech sheet.',
    lastPrice: 'Purchase price (base unit)',
    lastPriceUnit: 'Purchase price ({{code}})',
    purchaseUnit: 'Purchase unit',
    purchaseUnitHint: 'The format this supplier sells in (Box, Sack…)',
    purchaseUnitBase: 'Base unit',
    moveDown: 'Move down',
    moveUp: 'Move up',
    newIngredientName: 'New ingredient',
    noComplianceItems: 'No compliance claims configured. Add them in Settings.',
    noIngredientsMaster: 'No ingredients in the master. Create one below or in Settings.',
    noPackagingUnits: 'No packaging units configured. Add them in Settings.',
    noActivePrimarySupplier: 'This article\'s primary supplier ({{name}}) has been deactivated. The article has no active primary supplier until you pick another one.',
    noSuppliers: 'No suppliers linked yet.',
    noSuppliersMaster: 'No suppliers in the master. Create them in Settings.',
    nutritionLabel: 'Nutrition (per 100 g)',
    packagingDefault: 'Default purchase',
    packagingDefaultBase: 'Default purchase: base unit ({{code}})',
    packagingDefaultSales: 'Default sales',
    packagingDefaultSalesBase: 'Default sales: base unit ({{code}})',
    packagingHint: '1 packaging unit = N {{code}} (always against the base unit)',
    packagingRecipeEquivalence: 'Equals {{quantity}} {{unit}} for recipes.',
    packagingQuantity: 'Equals',
    packagingSaveError: 'Could not save the packaging units.',
    packagingUnit: 'Packaging unit',
    packagingUnitsLabel: 'Packaging units',
    pdfInvalidType: 'The file must be a PDF.',
    pdfTooLarge: 'The PDF exceeds 10 MB.',
    preparationMode: 'Preparation mode',
    removeSupplier: 'Remove supplier',
    priceHistory: 'Price history',
    removeSupplierBody: 'A reason is required to remove this supplier. The link is deactivated, not deleted.',
    removeSupplierTitle: 'Remove supplier',
    salePrice: 'Sale price',
    salePriceHint: 'Per base unit. Pre-fills the price on sales-order lines.',
    salePriceHintUnit: 'Per {{code}}. Pre-fills the price on sales-order lines.',
    sectionConservation: 'Conservation & use',
    sectionLabeling: 'Labelling',
    sectionPackaging: 'Packaging & logistics',
    sectionSafety: 'Food safety',
    sectionSafetyHint: 'Write the food-safety information as free text blocks. They appear on the tech sheet in this order.',
    safetyBlockTitle: 'Title',
    safetyBlockTitlePlaceholder: 'e.g. Allergens',
    safetyBlockBody: 'Text',
    safetyBlockBodyPlaceholder: 'Write the text you want to appear on the tech sheet',
    addSafetyBlock: 'Add text block',
    removeSafetyBlock: 'Remove block',
    moveSafetyBlockUp: 'Move up',
    moveSafetyBlockDown: 'Move down',
    safetyEmpty: 'No text blocks yet. Add one to get started.',
    safetyBodyRequired: 'The block text cannot be empty',
    sectionSuppliers: 'Suppliers',
    skuAutoHint: 'Generated automatically on save',
    supplier: 'Supplier',
    supplierDescription: 'Supplier description',
    supplierInactiveHint: 'Deactivated supplier: it cannot be used for new purchases. The link, price and reference are kept for the history.',
    supplierPrimary: 'Primary supplier',
    supplierRef: 'Supplier reference',
    suppliersHint: 'At most one primary supplier',
    suppliersSaveError: 'Could not save the suppliers.',
    techSheet: 'Supplier tech sheet',
    techSheetCreateHint: 'Save the article to attach the tech sheet',
    techSheetDownload: 'Download',
    techSheetUploadError: 'Could not upload the tech sheet.',
    techSheetUploading: 'Uploading…',
    usageType: 'Usage type',
    nutritionEnergyKj: 'Energy (kJ)',
    nutritionEnergyKcal: 'Energy (kcal)',
    nutritionFat: 'Fat (g)',
    nutritionSaturatedFat: 'of which saturates (g)',
    nutritionCarbohydrate: 'Carbohydrate (g)',
    nutritionSugars: 'of which sugars (g)',
    nutritionProtein: 'Protein (g)',
    nutritionSalt: 'Salt (g)',
  },

  'backoffice.technicalSheetScreen': {
    sourceManual: 'Information declared on the article card (manual source).',
    sourceDerived: 'Calculated from version {{version}} of the active recipe.',
    ingredientsEmpty: 'No ingredients to declare yet.',
    allergensEmpty: 'No allergen declared.',
    title: 'Tech sheet',
    subtitle: 'Regulatory document derived from the BOM (EU Reg. 1169/2011)',
    generateVersion: 'Generate version',
    generating: 'Generating…',
    generateError: 'The version could not be generated',
    versionHistory: 'Version history',
    noVersions: 'There is no tech-sheet version yet',
    version: 'Version',
    versionLabel: 'v{{n}}',
    date: 'Date',
    responsible: 'Responsible',
    status: 'Status',
    statusDraft: 'Draft',
    statusApproved: 'Approved',
    statusArchived: 'Archived',
    ingredientsTitle: 'Ingredients (by weight)',
    allergensTitle: 'Allergens (EU 14, aggregated)',
    nutritionTitle: 'Nutritional information / 100 g',
    foodSafetyTitle: 'Food safety',
    foodSafetyHint: 'The article’s food-safety texts. Edited on the article card and frozen into each technical sheet version.',
    foodSafetyEmpty: 'This article has no food-safety texts yet.',
    legislationTitle: 'Legislation & standards',
    legislationHint: 'The obrador’s legal texts (Settings · Legislation & standards). Printed as the closing text of the technical sheet.',
    legislationEmpty: 'No legislation texts configured yet.',
    nutritionPlaceholder: 'Pending (Wave 2 — M15)',
    labelNote:
      'The label is NOT generated here: it is generated when closing the work order or when packaging (with batch and expiry).',
    conservation: 'Storage',
    netWeight: 'Net weight',
    legalDenomination: 'Legal denomination',
    shelfLifeDays: 'Shelf life (days)',
    noAllergens: 'No declared allergens',
    bomNoRecipe:
      'This article has no recipe with a published version. Define and publish the recipe so the sheet can derive its ingredients.',
    allergensFromBom: 'Allergens aggregated automatically from the recipe BOM.',
    onlyFinishedHint: 'The technical sheet is only generated for manufactured articles (produced with an own recipe).',
    nutritionHint: 'Values per 100 g (EU Reg. 1169/2011). Edited on the article card and frozen into each technical sheet version.',
    nutritionEmpty: 'No nutrition information yet.',
    nutritionEnergyKj: 'Energy (kJ)',
    nutritionEnergyKcal: 'Energy (kcal)',
    nutritionFat: 'Fat (g)',
    nutritionSaturatedFat: 'of which saturates (g)',
    nutritionCarbohydrate: 'Carbohydrate (g)',
    nutritionSugars: 'of which sugars (g)',
    nutritionProtein: 'Protein (g)',
    nutritionSalt: 'Salt (g)',
    diffModalTitle: 'Changes in the new version',
    diffModalIntro: 'Review what changes against the current version before generating the draft.',
    diffFirstVersion: 'There is no previous version: the first technical sheet version will be created.',
    diffNoChanges: 'No changes against the current version.',
    diffKindAdded: 'Added',
    diffKindRemoved: 'Removed',
    diffKindChanged: 'Changed',
    diffKindUnchanged: 'Unchanged',
    diffFieldIngredients: 'Ingredients',
    diffFieldAllergens: 'Allergens',
    diffFieldShelfLife: 'Shelf life (days)',
    diffFieldNutrition: 'Nutrition information',
    diffFieldRecipeVersion: 'Recipe version',
    confirmGenerate: 'Generate and approve version',
    colActions: 'Actions',
    archive: 'Archive',
    archiving: 'Archiving…',
    archiveError: 'The version could not be archived',
    frozenError: 'An approved version is frozen by the server: it cannot be modified. Generate a new version instead.',
    pdfDocumentTitle: 'Product technical sheet',
    pdfVersion: 'Version',
    pdfRevisionDate: 'Revision date',
    pdfArticleName: 'Article name',
    pdfProductCode: 'Product code',
    pdfDescription: 'Product description',
    pdfUsage: 'Usage',
    pdfNutritionTitle: 'Nutrition per 100 g',
    pdfConservationTitle: 'Storage conditions',
    pdfContainerType: 'Container type',
    pdfShelfLife: 'Best before',
    pdfLogisticsTitle: 'Logistics',
    pdfContainerFormatTitle: 'Container format',
    pdfUnitsPerBox: 'Units/box',
    pdfBoxesPerPallet: 'Boxes/pallet',
    pdfDimensions: 'Dimensions (H×L×D)',
    pdfContains: 'Contains',
    pdfMayContain: 'May contain',
    pdfLegislationTitle: 'Legislation',
    pdfDays: 'days',
    downloadPdf: 'Download PDF',
    generatingPdf: 'Generating…',
    pdfGenerationError: 'The tech-sheet PDF could not be generated.',
  },

  'backoffice.articleTypeLabel': {
    raw_material: 'Raw material',
    intermediate: 'Semi-finished product',
    finished: 'Finished product',
    packaging: 'Auxiliary product',
  },

  'backoffice.articleCategoryLabel': {
    raw_material: 'Raw material',
    auxiliary: 'Auxiliary',
    intermediate: 'Semi-finished',
    finished: 'Finished',
    packaging: 'Packaging',
  },

  'backoffice.recipes': {
    // List (screen 13)
    title: 'Recipes',
    subtitle: 'Workshop recipes with immutable versioning',
    newRecipe: 'New recipe',
    searchPlaceholder: 'Search by name or article',
    colName: 'Recipe',
    colOutput: 'Produced article',
    colVersion: 'Active version',
    empty: 'There are no recipes yet',
    emptyFiltered: 'No recipe matches the search',
    showInactive: 'Show deactivated',
    inactiveBadge: 'Inactive',
    noActiveVersion: 'No active version',
    draftBadge: 'Draft',
    create: 'Create recipe',
    newVersion: 'New version',
    // Detail (screen 13)
    detailSubtitle: 'Active version, BOM by weight and history',
    backToList: 'Back to recipes',
    activeVersion: 'Active version',
    versionLabel: 'Version {{n}}',
    noActiveVersionTitle: 'This recipe does not have a published version yet',
    noActiveVersionBody:
      'Publish a draft version so it appears in the catalog and work orders can be created.',
    sectionMeta: 'Version data',
    sectionIngredients: 'Ingredients (by weight)',
    sectionSteps: 'Production',
    sectionAllergens: 'Allergens (EU 14, aggregated from the BOM)',
    sectionScaling: 'Recipe scaling',
    sectionVersions: 'Version history',
    recipeType: 'Recipe type',
    recipeTypeHint:
      'A packaging one consumes the bulk product and the packaging, and on the floor it asks which packs you want labels for.',
    recipeType_normal: 'Normal',
    recipeType_packaging: 'Packaging',
    leadTimeDays: 'Lead time (days)',
    notes: 'Notes',
    colIngredient: 'Ingredient',
    colQuantity: 'Quantity',
    colUnit: 'Unit',
    colStep: 'Step',
    colDescription: 'Name',
    colDuration: 'Duration (min)',
    colTemperature: 'Temperature (°C)',
    colControl: 'Control',
    blockingBadge: 'Blocking',
    expandIntermediate: 'Expand the BOM',
    collapseIntermediate: 'Collapse',
    intermediateNoRecipe: 'This semi-finished item has no published recipe',
    noIngredients: 'This version has no ingredients',
    noSteps: 'This version has no production steps',
    noAllergens: 'No declared allergens',
    presenceTraces: 'Traces',
    // Scaling
    scalingHint:
      'Enter a target yield or a factor to see the scaled quantities. It does not modify the recipe.',
    scalingTargetYield: 'Target yield',
    scalingFactor: 'Factor',
    scalingResultTitle: 'Scaled quantities',
    scalingInvalid: 'Enter a positive value',
    // Version history
    versionNumber: 'Version',
    versionStatus: 'Status',
    versionDate: 'Date',
    statusDraft: 'Draft',
    statusPublished: 'Published',
    statusActive: 'Active',
    openDraft: 'Edit the draft',
    viewVersion: 'View',
    // Actions
    newVersionFromActive: 'New version',
    editDraft: 'Edit',
    publish: 'Publish',
    publishing: 'Publishing…',
    discard: 'Discard',
    discarding: 'Discarding…',
    publishHint:
      'Publishing freezes this version, aggregates the BOM allergens and makes it the active recipe version.',
    fixValidation: 'Review the marked rows before saving or publishing.',
    packagingNotDeclared:
      'Any packaging on this list is consumed by the work order like any other line, but it does NOT appear in the ingredients declaration on the tech sheet or the label: only what is eaten goes there.',
    publishNeedsIngredients:
      'A version with no ingredients cannot be published: the allergens, the tech sheet and the label all come from this list. Add at least one ingredient. You can still save the draft.',
    rescaleOffer:
      'The yield changed from {{from}} to {{to}}. Multiply the ingredients by {{factor}}? If you were correcting a yield that was written wrong, leave them as they are.',
    rescaleApply: 'Rescale the ingredients',
    rescaleKeep: 'Leave them as they are',
    // Editor
    editorTitleNew: 'New recipe',
    editorTitleEdit: 'Edit draft',
    editorSubtitleNew: 'Create a recipe and its version 1 (draft)',
    editorSubtitleEdit: 'Edit the draft version before publishing',
    editorSectionMeta: 'Planning',
    editorSectionOutputs: 'Recipe result',
    editorSectionIngredients: 'Raw material',
    editorSectionSteps: 'Production',
    editorSectionResources: 'Resources',
    name: 'Recipe name',
    // Recipe result (outputs table). The first line is the recipe's main product;
    // the rest are additional products of the same production run.
    outputProduct: 'Product',
    outputsHint:
      'The first line is the recipe’s main product. Add more if the production run yields others.',
    addOutput: 'Add line',
    removeOutput: 'Remove',
    addIngredient: 'Add ingredient',
    removeIngredient: 'Remove',
    moveUp: 'Up',
    moveDown: 'Down',
    ingredient: 'Ingredient',
    quantity: 'Quantity',
    unit: 'Unit',
    addStep: 'Add a blank step',
    // Resources (what the production run occupies that is not raw material)
    resource: 'Resource type',
    resourceQuantity: 'Quantity',
    resourceDuration: 'Time (min)',
    selectResource: 'Select a type',
    addResource: 'Add resource',
    removeResource: 'Remove',
    noResources: 'This recipe has no resources yet.',
    noResourcesMaster:
      'No resource types defined for this workshop. Define them in Settings · Resources to ask for them in the recipe.',
    colResource: 'Resource type',
    colResourceDuration: 'Time (min)',
    resourceLegacyMachine: 'pinned resource: {{name}}',
    removeStep: 'Remove',
    process: 'Process',
    selectProcess: 'Select a process',
    // Production steps (OBR-17): editable instances, with their own ingredients and resources
    stepLabel: 'Step {{n}}',
    stepName: 'Step name',
    stepNamePlaceholder: 'e.g. First kneading',
    stepInstructions: 'Instructions',
    stepInstructionsPlaceholder: 'What to do in this step (optional)',
    stepControl: 'Control',
    stepProcessHint:
      'Optional. A step can be written by hand; if you pick a process, its values fill in as a starting point and you then adapt them to the recipe.',
    noProcess: 'No process',
    processRetired: 'Retired process',
    addStepFromProcess: 'Add a step from a process',
    stepFromProcessPlaceholder: 'Pick a process…',
    reapplyProcess: 'Apply the process values again',
    reapplyConfirmTitle: 'Apply the process «{{process}}» again',
    reapplyConfirmBody: 'This step already has values that differ from the process. If you continue they will be overwritten with the process values, and what was written in these fields will be lost:',
    reapplyConfirmAction: 'Overwrite the step',
    removeStepConfirmTitle: 'Remove {{step}}',
    removeStepConfirmBody: 'The step «{{name}}» will be removed, with the instructions, control and resources you defined on it.',
    removeStepConfirmIngredients_one: 'The ingredient that goes in stays in the BOM, with no step: {{names}}.',
    removeStepConfirmIngredients_other: 'The {{count}} ingredients that go in stay in the BOM, with no step: {{names}}.',
    removeStepConfirmUnsaved: 'It is not saved yet: if you change your mind, leave without saving and the saved version stays.',
    removeStepConfirmAction: 'Remove the step',
    duplicateOutput: 'A recipe for «{{name}}» already exists. An article can only have one recipe: open it and make a new version.',
    duplicateOutputGeneric: 'A recipe for this article already exists. An article can only have one recipe: open it and make a new version.',
    openExistingRecipe: 'Open the existing recipe',
    duplicateStep: 'Duplicate',
    stepResources: 'Required resources',
    noStepResources: 'This step needs no resources.',
    stepIngredients: 'Ingredients added here',
    noStepIngredients: 'None. They are assigned from the raw-materials section.',
    stepOrderHint:
      'Drag a step by its name or use the arrows to change its order. Numbering is for display only.',
    noStepsYet: 'There are no steps yet. Add a blank one or one from a process.',
    ingredientStep: 'Added in step',
    ingredientStepNone: 'No step',
    ingredientStepHint:
      'Optional: the step each ingredient is added in, so the App can show it at the right moment.',
    stepMissing: 'The step assigned to this ingredient no longer exists',
    looseResourcesTitle: 'Resources with no step',
    looseResourcesHint:
      'Resource lines the recipe asked for as a whole, from before resources went by step. They are kept as they are; to assign one to a step, add it to the step and remove it from here.',
    saveNothing: 'No changes to save',
    stepDuration: 'Duration (min)',
    stepTemperature: 'Temperature (°C)',
    isBlocking: 'Blocking (CCP only)',
    save: 'Save the draft',
    saved: 'Draft saved',
    cancel: 'Cancel',
    noUnits:
      'No units have been defined for the workshop yet. Define them in Settings before creating recipes.',
    noOutputArticles:
      'There are no manufactured or finished articles in the catalog. Create one before defining a recipe.',
    noProcesses:
      'No processes have been defined yet. You can write the steps by hand, or define processes in Settings · Processes to use as templates.',
    selectArticle: 'Select an article',
    selectUnit: 'Select a unit',
    readOnly: 'Read only',
    // Errors / validation
    requiredIngredient: 'At least one ingredient is required',
    blockingNeedsPcc: 'Only a step with CCP control can be blocking',
    outputCannotBeIngredient:
      'A recipe cannot list an article it produces as one of its ingredients',
    createError: 'The recipe could not be created',
    loadError:
      'The recipe data could not be loaded. Try again before editing it.',
    saveError: 'The draft could not be saved',
    publishError: 'The version could not be published',
    publishErrorActivate:
      'The version was published but could not be marked as active. Try publishing again.',
    discardError: 'The draft could not be discarded',
    unresolvedAllergensWarning:
      'Some BOM semi-finished items have no published recipe; their allergens could not be aggregated.',
    // Discard dialog
    discardTitle: 'Discard the draft',
    discardBody:
      'This draft version along with its BOM and steps will be deleted. If it is the only version, the recipe will also be deactivated. State the reason (required).',
    notFound: 'The recipe was not found',
    // Step control type enum labels
    'controlType.none': 'None',
    'controlType.weight': 'Weighing',
    'controlType.timer': 'Timer',
    'controlType.temperature': 'Temperature',
    'controlType.pcc': 'CCP',
      tolerancePct: 'Tolerance (%)',
    tolerancePctHint: 'Empty = the obrador tolerance applies',
    lineStockEquivalent: 'Stock: {{quantity}} {{unit}} theoretical consumption.',
    lineNoEquivalenceUseBase:
      '“{{unit}}” has no equivalence with the article’s base unit ({{base}}): stock would be deducted 1:1. Set the line in {{base}}.',
    lineNoEquivalence:
      '“{{unit}}” has no equivalence with the article’s base unit ({{base}}): stock would be deducted 1:1. Set the recipe unit on the article card.',
    tolerancePctPlaceholder: 'Obrador',
    sequentialPreparation: 'Sequential preparation',
    sequentialPreparationHint: 'The operator prepares ingredients one at a time, in this order',
    dragToReorder: 'Drag to reorder',
    dragHandle: 'Reorder ingredient',
    ingredientOrderHint: 'This list\'s order is the preparation order on the floor',
},

  'backoffice.stock': {
    articleTitle: 'Existences — {{article}}',
    articleCard: 'Article card',
    allArticles: 'All existences',
    articleEmpty: 'This article has no existences recorded yet.',
    title: 'Stock',
    subtitle: 'Stock by article, batch and expiry',
    available: 'Available',
    blocked: 'Blocked',
    blockedChip: 'Blocked: {{quantity}} {{unit}}',
    blockedHint:
      'Reception exceptions awaiting a decision and returns waiting to go back: not counted as available for production.',
    blockedSummary:
      'On hand: {{physical}} {{unit}} · Blocked: {{blocked}} {{unit}} · Available: {{free}} {{unit}}',
    byLot: 'By batch',
    byZone: 'By zone',
    belowMinimum: 'Below minimum',
    // Article list
    searchPlaceholder: 'Search by SKU or name',
    filterType: 'Type',
    filterStatus: 'Status',
    statusAll: 'All',
    statusExpiring: 'Expiring soon',
    statusExpired: 'Expired',
    expiringBadge: 'Expiring soon',
    expiredBadge: 'Expired',
    typeAll: 'All',
    typePurchase: 'Purchase',
    typeIntermediate: 'Semi-finished',
    typeSale: 'Sale',
    colSku: 'SKU',
    colName: 'Article',
    colType: 'Type',
    colAvailable: 'Available',
    colCommitted: 'Committed',
    colUnit: 'Unit',
    empty: 'There is no stock yet. It will be generated through receiving and production.',
    emptyFiltered: 'No article matches the filter.',
    loadError: 'The stock could not be loaded',
    // Lot breakdown
    lotBreakdown: 'Breakdown by batch',
    colLot: 'Batch',
    colExpiry: 'Expiry',
    colPlace: 'Place',
    colQuantity: 'Quantity',
    colStatus: 'Status',
    noLots: 'This article has no stock by batch.',
    noPlace: 'No place',
    // Existences by place (filter, OBR-90)
    filterPlace: 'Place',
    placeAll: 'All places',
    colInPlace: 'In this place',
    placeNoteZone: 'Existences in {{place}}, including its sections and locations.',
    placeNoteSection: 'Existences in {{place}}, including its locations.',
    placeNoteLocation: 'Existences in {{place}}.',
    placeNoteNone: 'Existences that have no place yet. From the lot breakdown, “Move” takes them where they belong.',
    emptyPlace: 'There are no existences in this place.',
    noLotsInPlace: 'This article has no lots in this place.',
    noExpiry: 'No expiry',
    supplierLotCode: 'Supplier lot: {{code}}',
    expand: 'Show batches',
    collapse: 'Hide batches',
    'lotStatus.available': 'Available',
    'lotStatus.quarantined': 'In quarantine',
    'lotStatus.consumed': 'Consumed',
    'lotStatus.expired': 'Expired',
    notTracked: 'Stock not tracked',
    inBaseUnit: '(= {{quantity}} {{unit}})',
    // Move existences (transfer between places of the obrador)
    moveAction: 'Move',
    moveTitle: 'Move existences',
    moveBody: 'Changes where the existences are. The article total does not change: two transfer movements are recorded, one out and one in.',
    moveFrom: 'From',
    moveTo: 'To',
    moveToPlaceholder: 'Choose where they go',
    moveQuantity: 'Quantity',
    moveAvailable: 'Available at the origin: {{quantity}} {{unit}}',
    moveQuantityInvalid: 'It must be greater than zero and cannot exceed what the origin holds.',
    moveNote: 'Note',
    moveNotePlaceholder: 'Optional',
    moveConfirm: 'Move',
    moving: 'Moving…',
    moveError: 'The existences could not be moved.',
    moveErrorInsufficient: 'The origin no longer holds enough. Reopen the lot to see what is left.',
    moveNoDestinations: 'There is no other place to move them to. Set up the obrador structure.',
  },

  'backoffice.inventory': {
    title: 'Inventory',
    startCount: 'Start count',
    adjust: 'Adjust',
    adjustReason: 'Adjustment reason',
    // Adjust dialog (screen 14)
    adjustTitle: 'Adjust stock',
    adjustBody:
      'Stock movements are never edited. An adjustment creates a new movement with the documented reason.',
    delta: 'Variation (sign included)',
    deltaHint: 'Positive to add stock, negative to subtract.',
    deltaInvalid: 'Enter a non-zero variation.',
    reason: 'Adjustment reason',
    reasonNote: 'Reason note',
    reasonNotePlaceholder: 'Describe the reason for the adjustment',
    confirm: 'Record the adjustment',
    adjusting: 'Recording…',
    adjustError: 'The adjustment could not be recorded',
    selectReason: 'Select a reason',
  },

  'backoffice.movements': {
    title: 'Stock movements',
    type: 'Type',
    reason: 'Reason',
    reverse: 'Reverse',
    appendOnlyNote: 'Movements are not edited; corrections are counter-movements',
    // Ledger (screen 14)
    colDate: 'Date',
    colType: 'Type',
    colQuantity: 'Quantity',
    colReason: 'Reason',
    colPlace: 'Place',
    colWho: 'Recorded by',
    empty: 'There are no movements for this article yet.',
    loadError: 'The movements could not be loaded',
    quantityWithUnit: '{{quantity}} {{unit}}',
  },

  'backoffice.movementType': {
    reception: 'Receiving',
    o01_consumption: 'WO consumption',
    production_output: 'Production',
    adjustment: 'Adjustment',
    write_off: 'Write-off',
    reversal: 'Counter-movement',
    shipment: 'Shipment',
    transfer: 'Transfer',
    purchase_return: 'Purchase return',
  },

  'backoffice.movementReason': {
    recount: 'Recount',
    loss: 'Waste',
    damage: 'Damage',
    expired: 'Expired',
    burnt: 'Burnt',
    dropped: 'Dropped',
    correction: 'Correction',
    reception: 'Receiving',
    production: 'Production',
    consumption: 'Consumption',
  },

  'backoffice.purchaseOrders': {
    articleTitle: 'Purchases — {{article}}',
    articleCard: 'Article card',
    allOrders: 'All orders',
    articleEmpty: 'This article has never been purchased: no order and no purchase delivery note.',
    articleLinesTitle: 'Order lines',
    articleNoLines: 'This article does not appear on any purchase order.',
    articleReceptionsTitle: 'Receptions',
    articleNoReceptions: 'This article has not been received on any note.',
    totalOrdered: 'Ordered',
    totalReceived: 'Received',
    totalPending: 'Pending delivery',
    unmeasurableLines_one:
      '{{count}} line is left out of the totals: its format has no conversion set for the article.',
    unmeasurableLines_other:
      '{{count}} lines are left out of the totals: their format has no conversion set for the article.',
    notVisibleToPlanta:
      'This order is not visible in the planta app yet. Confirm it so the operator can receive it.',
    title: 'Purchase orders',
    newOrder: 'New order',
    allSuppliers: 'All suppliers',
    searchSupplier: 'Search a supplier',
    noSupplierMatches: 'No supplier matches',
    expectedFrom: 'Expected delivery from',
    expectedTo: 'Expected delivery to',
    clearFilters: 'Clear filters',
    searchLabel: 'Search by number or supplier',
    emptyFiltered: 'No order matches these filters. Try widening them, or clear them.',
    selectAll: 'Select all visible orders',
    selectedCount_one: '{{count}} order selected',
    selectedCount_other: '{{count}} orders selected',
    clearSelection: 'Clear selection',
    colSent: 'Sent',
    finishOrder: 'Finish',
    finishOrderLabel: 'Finish order {{number}}',
    closedWithPending: 'With pending',
    closedWithPendingHint: 'Finished with a pending balance: what was missing is no longer expected.',
    bulkConfirm: 'Confirm ({{count}})',
    bulkSend_one: 'Send ({{count}})',
    bulkSend_other: 'Send ({{count}})',
    bulkApply: 'Apply the action',
    bulkConfirmTitle: 'Confirm the orders',
    bulkSendTitle: 'Send the orders',
    bulkConfirmBody_one:
      '{{count}} of the {{selected}} selected will be confirmed and move to \u201cawaiting delivery\u201d. The rest stay as they are, and you will be told why.',
    bulkConfirmBody_other:
      '{{count}} of the {{selected}} selected will be confirmed and move to \u201cawaiting delivery\u201d. The rest stay as they are, and you will be told why.',
    bulkSendBody_one: '{{count}} of the {{selected}} selected will go out through its supplier’s channel: by email if they have one, or marked as sent if they receive orders by other means. Those of suppliers that take orders by WhatsApp do not go out on their own: we will offer them to you to open and send yourself. The rest stay as they are and you will be told why.',
    bulkSendBody_other: '{{count}} of the {{selected}} selected will go out through their supplier’s channel: by email if they have one, or marked as sent if they receive orders by other means. Those of suppliers that take orders by WhatsApp do not go out on their own: we will offer them to you to open and send yourself. The rest stay as they are and you will be told why.',
    bulkFinish: 'Finish ({{count}})',
    bulkFinishTitle: 'Finish the orders',
    bulkFinishBody_one:
      '{{count}} of the {{selected}} selected will be finished: you confirm you are not expecting the rest. It will become «Delivered» with what was received; delivery notes and stock do not change, and what is missing is recorded as unreceived with your name, the date and the reason. The rest stay as they are, and you will be told why.',
    bulkFinishBody_other:
      '{{count}} of the {{selected}} selected will be finished: you confirm you are not expecting the rest. They will become «Delivered» with what was received; delivery notes and stock do not change, and what is missing from each is recorded as unreceived with your name, the date and the same reason. The rest stay as they are, and you will be told why.',
    bulkFinishConfirm_one: 'Finish {{count}} order',
    bulkFinishConfirm_other: 'Finish {{count}} orders',
    bulkFinishBlockedClosed: '{{order}}: everything has already been received, there is nothing to finish.',
    bulkFinishBlockedNothing: '{{order}}: nothing has been received yet; if it is not going to arrive, cancel it.',
    bulkFinished_one: '{{count}} order finished',
    bulkFinished_other: '{{count}} orders finished',
    skip_nothing_received_one: '{{count}} has not received anything yet (if it is not going to arrive, it is cancelled)',
    skip_nothing_received_other: '{{count}} have not received anything yet (if they are not going to arrive, they are cancelled)',
    bulkApplied_one: '{{count}} order updated',
    bulkApplied_other: '{{count}} orders updated',
    skip_already_confirmed_one: '{{count}} was already confirmed',
    skip_already_confirmed_other: '{{count}} were already confirmed',
    skip_not_confirmed_one: '{{count}} is still an unconfirmed draft',
    skip_not_confirmed_other: '{{count}} are still unconfirmed drafts',
    skip_already_sent_one: '{{count}} was already recorded as sent',
    skip_already_sent_other: '{{count}} were already recorded as sent',
    skip_closed_one: '{{count}} has already arrived',
    skip_closed_other: '{{count}} have already arrived',
    skip_cancelled_one: '{{count}} is cancelled',
    skip_cancelled_other: '{{count}} are cancelled',
    bulkFailed_one: '{{count}} failed: {{orders}}',
    bulkFailed_other: '{{count}} failed: {{orders}}',
    orderNumber: 'No.',
    supplier: 'Supplier',
    orderDate: 'Date',
    expectedDate: 'Expected delivery',
    status: 'Status',
    lines: 'Lines',
    empty: 'No purchase orders yet',
    searchPlaceholder: 'Search by number or supplier…',
    bulkEmailed_one: '{{count}} emailed',
    bulkEmailed_other: '{{count}} emailed',
    bulkMarked_one: '{{count}} marked as sent',
    bulkMarked_other: '{{count}} marked as sent',
    bulkWhatsapp_one: '{{count}} belongs to a supplier that takes orders by WhatsApp: it was not sent, open it from here',
    bulkWhatsapp_other: '{{count}} belong to suppliers that take orders by WhatsApp: they were not sent, open them from here',
    bulkWhatsappOpen: 'Open them by WhatsApp',
    whatsappQueueTitle: 'Orders by WhatsApp',
    whatsappQueueBody: 'Each order opens in WhatsApp with the message written and you send it. WhatsApp does not tell us whether it was sent: once you have, press “I have sent it” so there is a record.',
    whatsappQueueOpen: 'Open WhatsApp',
    whatsappQueueSent: 'I have sent it',
    whatsappQueueDone: 'Recorded as sent',
    whatsappQueueNoNumber: 'No usable number: the supplier file needs the country prefix',
    whatsappQueueLoading: 'Preparing the messages…',
    whatsappQueueError: 'The dispatch of this order could not be recorded.',
    whatsappQueueClose: 'Close',
    skip_no_recipients_one: '{{count}} has no supplier email',
    skip_no_recipients_other: '{{count}} have no supplier email',
    skip_not_found_one: '{{count}} was not found',
    skip_not_found_other: '{{count}} were not found',
  },

  'backoffice.purchaseTemplates': {
    title: 'Templates',
    newTemplate: 'New template',
    editTemplate: 'Edit template',
    empty:
      'No templates yet. A template saves the order that repeats — supplier, articles, units and quantities — so you do not have to build it again each time.',
    colName: 'Template',
    colNotes: 'Notes',
    name: 'Template name',
    nameHint: 'What you call it between you: “Weekly”, “Urgent restock”.',
    nameRequired: 'Give the template a name.',
    nameTaken: 'This supplier already has a template with that name. Pick another one.',
    notesHint: 'They travel to the order that is generated; you can change them there.',
    usualQuantity: 'Usual quantity',
    emptyLines: 'Add at least one article to the template.',
    createOrder: 'Create order',
    saveAsTemplate: 'Save as template',
    deleteTitle: 'Deactivate template',
    deleteBody:
      'It will stop appearing in the list and in the New order “Template” field. Orders already made from it are untouched.',
  },
  'backoffice.priceHistory': {
    title: 'Price history',
    subtitle: '{{supplier}} · prices per {{unit}}, excluding VAT',
    subtitleNoUnit: '{{supplier}} · prices per base unit, excluding VAT',
    intro: 'Every change to the price on the article card is recorded. What is paid on a specific order is not shown here: it stays on the document.',
    loadError: 'The price history could not be loaded.',
    empty: 'There are no price changes for this relationship yet.',
    colWhen: 'Date',
    colBefore: 'Previous price',
    colAfter: 'New price',
    colChange: 'Change',
    colSource: 'Source',
    colWho: 'Who',
    kindInitial: 'First load',
    kindCleared: 'Price cleared',
    sourceManual: 'Article card',
    sourceImport: 'Import',
    sourcePurchaseOrder: 'Order {{number}}',
    sourcePurchaseOrderUnknown: 'Order',
    sourceLegacy: 'Before the history',
    system: 'System',
    close: 'Close',
  },
  'backoffice.purchaseOrderDetail': {
    newTitle: 'New purchase order',
    editTitle: 'Order {{number}}',
    supplier: 'Supplier',
    selectSupplier: 'Select a supplier',
    orderDate: 'Order date',
    expectedDate: 'Expected delivery date',
    deliveryDaysHint: 'Delivers on {{days}}',
    template: 'Template',
    templatePlaceholder: 'Start from a template (optional)',
    templateHint:
      'Fills in the supplier, articles, units and quantities. Everything can be changed before saving.',
    noTemplateMatches: 'No template matches',
    templateSupplierInactive: 'This template\'s supplier has been deactivated, so no new orders can be made with it. Reactivate it or change the template\'s supplier.',
    replaceTemplateTitle: 'Replace with the template?',
    replaceTemplateBody:
      'The order will become the “{{name}}” template: supplier, articles, quantities and notes. What is there now will be lost.',
    replaceTemplateConfirm: 'Replace',
    deliveryNotUsualDay:
      'Not one of this supplier’s delivery days (it delivers on {{days}}). You can still save.',
    deliveryBeforeOrder: 'Delivery can’t be earlier than the order date.',
    notes: 'Notes',
    linesTitle: 'Lines',
    addLine: 'Add article',
    article: 'Article',
    selectArticle: 'Select an article',
    orderedQuantity: 'Quantity',
    purchaseUnit: 'Purchase unit',
    baseUnit: 'Base unit',
    agreedPrice: 'Agreed price',
    agreedPriceUnit: 'Price (\u20ac/{{unit}})',
    pricePerUnit: '{{price}} \u20ac/{{unit}}',
    lineAmount: '= {{amount}} \u20ac',
    priceNeedsConversion: 'The packaging conversion is needed to enter the price per base unit.',
    colPrice: 'Agreed price',
    priceCatalog: 'Current in the catalogue: {{price}} €/{{unit}}',
    priceCatalogNone: 'No current price in the catalogue',
    updateSupplierPrice: 'Update the supplier\'s price',
    updatePriceTitle: 'Update the supplier\'s price',
    updatePriceBody: '«{{article}}» at «{{supplier}}»: the current price will change from {{from}} to {{to}} €/{{unit}}. The change will be recorded in the price history with this order as its source. Orders already placed do not change.',
    updatePriceBodyFirst: '«{{article}}» at «{{supplier}}» has no current price yet: it will become {{to}} €/{{unit}}. The change will be recorded in the price history with this order as its source. Orders already placed do not change.',
    updatePriceConfirm: 'Update the price',
    updatePriceError: 'The price could not be updated.',
    updatePriceDone: 'Supplier price updated. It is recorded in the article\'s price history.',
    priceMissingHint: 'No agreed price in the catalogue for this supplier: enter it to value the line.',
    lineQuantityTimes: '{{quantity}} \u00d7 {{unit}}',
    lineEquivalenceShort: '{{quantity}} {{pack}} \u00d7 {{factor}} {{base}}',
    lineNotes: 'Line note',
    lineNotesHint: 'Optional, for this article only. The order note goes at the top.',
    unknownQuantityHint:
      'Cannot be marked delivered: some lines use a format with no conversion set, so how much was ordered is unknown. Set the conversion on the article card, under Units and conversions, or finish the order leaving the rest unreceived.',
    unknownQuantityBanner:
      'Some lines cannot be matched against what arrives: their format has no conversion set for the article. They are shown in the purchase unit.',
    pickSupplierFirst: 'Pick the supplier first',
    searchArticle: 'Search by code or description',
    noArticleMatches: 'No article matches',
    noSupplierMatches: 'No supplier matches',
    supplierHasNoArticles:
      'This supplier has no linked articles. Link them from the article card, under Suppliers.',
    articleUnlinked: 'not linked to this supplier',
    droppedArticles_one:
      'Removed from the order because this supplier does not carry it: {{articles}}.',
    droppedArticles_other:
      'Removed from the order because this supplier does not carry them: {{articles}}.',
    supplierArticlesUnavailable:
      'Could not read which articles this supplier carries. Try again; meanwhile the list may come up empty.',
    missingConversion:
      'This format has no conversion set for the article: the order will not be able to match what arrives against what was ordered. Set it on the article card, under Units and conversions.',
    ordered: 'Ordered',
    received: 'Received',
    pending: 'Pending',
    remove: 'Remove',
    save: 'Save',
    confirm: 'Confirm order',
    confirmHint: 'The order moves to “Awaiting delivery”.',
    closeOrder: 'Finish the order',
    registerReception: 'Record the reception',
    registerReceptionHint:
      'For when the goods have arrived but nobody recorded them on the shop floor: enter what really arrived and the delivery note is created.',
    markDelivered: 'Mark as delivered',
    markDeliveredHint: 'Everything has arrived: this only records it on the order.',
    closeOrderHint:
      'If the rest is no longer going to arrive, finish the order with what was received: what is missing is recorded as unreceived and stops being pending. A reason is required.',
    closeDialogTitle: 'Finish order {{number}}',
    closeDialogBody:
      'You confirm you are not expecting the rest. The order will become «Delivered» with what was received: delivery notes and stock do not change, and what is missing is recorded as unreceived with your name, the date and the reason.',
    closeDialogPendingTitle: 'Left unreceived',
    closeDialogUnknown: 'unknown quantity (the packaging has no conversion)',
    closeError: 'The order could not be finished. Please try again.',
    closeErrorNotOpen:
      'This order can no longer be finished: it may have been served or cancelled in the meantime. Refresh the page.',
    closeErrorNothingPending:
      'Nothing is left to receive: the order can now be marked as delivered. Refresh the page.',
    closeErrorNothingReceived:
      'Nothing has been received on this order, so it cannot be finished. If it is not going to arrive, cancel it.',
    closedTitle: 'Finished with a pending balance',
    closedMeta: '{{user}} · {{date}}',
    closedReason: 'Reason',
    closedLinesTitle: 'Left unreceived',
    closedLine: '{{article}}: {{pending}} of {{ordered}}',
    closedLineUnknown: '{{article}}: unknown quantity',
    nothingReceivedHint:
      'Nothing has been received for this order yet. An order cannot be marked delivered without received quantities: record the reception first.',
    receptionsTitle: 'Purchase delivery notes',
    noReceptions: 'No reception recorded yet.',
    emptyLines: 'Add at least one line to the order.',
    lowStockWarning: 'Stock below minimum for some articles (warning, non-blocking).',
    pdfTitle: 'Purchase order',
    pdfSupplierRef: 'Supplier ref.',
    pdfError: 'The PDF could not be generated.',
    emailDefaultSubject: 'Order {{comanda}} · {{obrador}}',
    sendingTitle: 'Sending to the supplier',
    sendingDraftHint: 'Confirm the order to be able to send it and download its PDF.',
    sendingPending: 'Not sent yet',
    sendingSent: 'Sent',
    sendingWillEmail: 'It will be emailed to {{recipients}}.',
    sendingManualChannel: 'This supplier receives orders by other means: mark it as sent once you have passed it on.',
    sendingNoRecipients: 'This supplier has no order email. Add one to their record (Settings › Suppliers), type one when sending, or mark it as sent. The general email is not used for this.',
    sendingLastEmail: 'Emailed on {{date}} to {{recipients}}.',
    sendingLastManual: 'Marked as sent on {{date}}.',
    sendingWhatsappReady: 'This supplier receives orders by WhatsApp at {{phone}}. «Send by WhatsApp» opens WhatsApp with the order already written and you send it: Obrava cannot know whether the message went out or arrived, so you confirm it.',
    sendingWhatsappNoPhone: 'This supplier receives orders by WhatsApp but has no number on file. Add it in Third parties · Suppliers, or mark the order as sent once you have passed it on.',
    sendingWhatsappNoPrefix: 'This supplier’s WhatsApp number («{{phone}}») cannot be opened: it needs the country prefix («+34…»). Fix it on the supplier file, or mark the order as sent once you have passed it on.',
    sendWhatsappAction: 'Send by WhatsApp',
    resendWhatsappAction: 'Resend by WhatsApp',
    whatsappDialogTitle: 'WhatsApp · order {{number}}',
    whatsappDialogBody: 'WhatsApp has been opened with the order written, for the number {{phone}}. WhatsApp does not tell us whether you sent the message or whether it arrived: once you have sent it, confirm it here and the order will keep a record.',
    whatsappDialogNumber: 'WhatsApp number: +{{number}}',
    whatsappDialogOmitted_one: 'One line did not fit: the message says there are more. Complete it by another means (e.g. the PDF by email).',
    whatsappDialogOmitted_other: '{{count}} lines did not fit: the message says there are more. Complete it by another means (e.g. the PDF by email).',
    whatsappPreview: 'The message',
    whatsappReopen: 'Open WhatsApp again',
    whatsappConfirm: 'I have sent it',
    whatsappDefaultTemplate: 'Hello, we are sending you order {{comanda}} from {{obrador}}.\n\n{{linies}}\n\nExpected delivery: {{entrega}}\n\nThank you!',
    whatsappDefaultTemplateNoDelivery: 'Hello, we are sending you order {{comanda}} from {{obrador}}.\n\n{{linies}}\n\nThank you!',
    whatsappLineRef: 'ref. {{ref}}',
    whatsappReviewBody: 'This is the message that will open in WhatsApp, for the number {{phone}}. You can tweak it in WhatsApp before sending; what is written there is what you send, not what is shown here.',
    whatsappOpenAction: 'Open WhatsApp',
    whatsappTooLong: 'The text is too long for the WhatsApp link and may not open in full. Shorten the supplier’s WhatsApp template.',
    historyMessage: 'View the message',
    whatsappNotes: 'Notes: {{notes}}',
    whatsappMoreLines: '… and {{count}} more lines',
    sendingLastWhatsapp: 'Marked as sent by WhatsApp on {{date}} (to {{phone}}). WhatsApp does not confirm delivery.',
    resultWhatsapp: 'Marked as sent · delivery not confirmed',
    sendSkippedNoPhone: 'The supplier has no WhatsApp number on file.',
    sendSkippedWhatsapp: 'This supplier receives orders by WhatsApp: it is not sent from here. Open WhatsApp from the order.',
    sendingLegacy: 'Recorded as sent on {{date}}.',
    sendAction: 'Send to supplier',
    sendByEmailAction: 'Send by email',
    resendAction: 'Resend',
    markSentAction: 'Mark as sent',
    downloadPdf: 'Download PDF',
    sendDialogTitle: 'Send order {{number}}',
    resendDialogTitle: 'Resend order {{number}}',
    sendTo: 'To',
    sendToHint: 'Separate addresses with commas. Changing them only applies to this send.',
    sendToInvalid: 'Some addresses are not valid: {{list}}',
    sendToEmpty: 'Enter at least one address.',
    sendSubject: 'Subject',
    sendAttachPdf: 'Attach the order PDF',
    sendConfirm: 'Send',
    resendConfirm: 'Resend',
    markSentTitle: 'Mark as sent',
    markSentBody: 'It will be recorded that order {{number}} reached the supplier by other means, with the current date and time. No email is sent.',
    sendResultSent: 'Order sent to {{recipients}}.',
    sendResultMarked: 'Order marked as sent.',
    sendResultFailed: 'The email did not go out. Try again; the reason is in the sending history.',
    sendSkipped: 'This order cannot be sent right now.',
    sendSkippedNoRecipients: 'The supplier has no order email to send it to.',
    sendError: 'The order could not be sent. Try again.',
    confirmAndSend: 'Confirm and send',
    autoSentBanner: 'Order confirmed and sent to {{recipients}}.',
    autoSendFailed: 'Order confirmed, but it could not be sent. Send it from “Sending to the supplier”.',
    historyTitle: 'Sending history',
    historyWhen: 'When',
    historyWho: 'Who',
    historyChannel: 'Channel',
    historyTo: 'To',
    historyResult: 'Result',
    channelEmail: 'Email',
    channelManual: 'Manual',
    channelWhatsapp: 'WhatsApp',
    resultSent: 'Sent',
    resultMarked: 'Marked',
    resultFailed: 'Failed',
  },

  'backoffice.receptions': {
    title: 'Purchase delivery notes',
    receptionNumber: 'Note no.',
    deliveryNote: 'Supplier note',
    supplier: 'Supplier',
    date: 'Date',
    order: 'Order',
    lines: 'Lines',
    invoiced: 'Invoiced',
    notInvoiced: 'Not invoiced',
    markInvoiced: 'Mark as invoiced',
    markNotInvoiced: 'Unmark invoiced',
    invoiceNumber: 'Supplier invoice no.',
    invoiceNumberPrompt: 'Enter the supplier invoice number.',
    downloadPdf: 'Download PDF',
    empty: 'No purchase delivery notes yet',
    searchLabel: 'Search by delivery note, supplier or invoice',
    emptyFiltered: 'No delivery note matches the search.',
    searchPlaceholder: 'Search by supplier delivery-note number…',
    documentTitle: 'Purchase delivery note',
    article: 'Article',
    supplierLot: 'Supplier lot',
    internalLot: 'Internal lot',
    expiry: 'Expiry',
    quantity: 'Quantity',
    notesLabel: 'Notes',
    exceptionPending: 'Exception pending',
    control: 'Check',
    corrected: 'Corrected',
    cancelled: 'Cancelled',
    correctedNote: 'Corrected on {{date}}: {{reason}}',
    wasQuantity: 'was {{quantity}}',
  },

  // Manual reception from the BackOffice (OBR-77): the goods arrived and nobody recorded them on the shop floor.
  'backoffice.receptionRegister': {
    title: 'Record the reception',
    subtitle: '{{order}} · {{supplier}}',
    intro:
      'For when the goods have already arrived but were not recorded on the shop floor. Enter what really arrived: nothing is assumed to be received, and stock goes up only by what you enter.',
    notOpen:
      'This order is not waiting for goods: a reception can only be recorded for an order that is pending delivery or partially delivered.',
    existing_one: 'This order already has {{count}} delivery note: {{list}}. Check that it is not the same delivery.',
    existing_other: 'This order already has {{count}} delivery notes: {{list}}. Check that it is not the same delivery.',
    existingItem: '{{number}} ({{date}}, note {{note}})',
    unsyncedWarning:
      'If a shop-floor device has a reception for this order waiting to sync, it is not visible here: check before recording it, or the delivery would be duplicated.',
    headerTitle: 'The delivery note',
    deliveryNote: 'Supplier delivery note no.',
    deliveryNoteHint: 'Required: the number printed on the supplier’s delivery note.',
    sameNumber: 'This order already has delivery note {{number}} with this number.',
    receptionDate: 'Reception date',
    receptionDateHint: 'The day the goods arrived. The estimated expiry is calculated from it.',
    dateInvalid: 'Enter a real date, today or earlier.',
    notes: 'Notes',
    notesHint: 'Optional.',
    linesTitle: 'What arrived',
    ordered: 'Ordered',
    receivedSoFar: 'Already received',
    pending: 'Pending',
    orderedConversion: '{{ordered}} × {{factor}} = {{total}}',
    quantityWithBase: '{{quantity}} ({{base}})',
    entryUnit: 'Quantity unit',
    entry: 'Entry {{number}}',
    removeEntry: 'Remove the entry',
    addEntry: 'Add another entry',
    quantity: 'Received quantity',
    quantityIn: 'Received quantity ({{unit}})',
    equalsBase: '= {{total}}',
    noConversion: 'The «{{unit}}» packaging has no conversion on the article card. Enter the quantity in {{base}}.',
    lot: 'Supplier batch (optional)',
    expiry: 'Expiry (optional)',
    expiryEstimated: 'Estimated expiry',
    expiryEstimatedHint_one: 'Suggested: reception + {{count}} day of shelf life. Change it if the supplier gives a real one.',
    expiryEstimatedHint_other: 'Suggested: reception + {{count}} days of shelf life. Change it if the supplier gives a real one.',
    control: 'Reception control',
    controlNone: 'No check',
    controlOk: 'Conforming',
    controlNok: 'Non-conforming',
    flagException: 'Mark as an exception',
    unflagException: 'Remove the mark',
    flaggedException: 'Exception',
    exceptionNote: 'What’s wrong?',
    exceptionNoteHint: 'E.g. 20 kg sacks instead of 25',
    exceptionNoteRequired: 'Say what is wrong',
    thisReceipt: 'On this delivery note',
    willRemainPending: 'Will remain pending',
    excess: 'That is {{excess}} more than was missing. It will be recorded as an exception.',
    confirmLabel: 'I confirm this delivery has not been recorded yet, neither in the App nor as a delivery note.',
    submit: 'Record the reception',
    submitting: 'Recording…',
    missingList: 'To record it you still need: {{list}}.',
    missing_deliveryNote: 'the delivery note number',
    missing_date: 'a real reception date, today or earlier',
    missing_nothingEntered: 'the quantity of at least one entry',
    missing_flagNotes: 'the note on the entries marked as an exception',
    missing_notConfirmed: 'to confirm the delivery has not been recorded',
    errorGeneric:
      'The reception could not be recorded. Check the order’s delivery notes to see whether it was recorded before trying again.',
    openPicker: 'Record a reception',
    pickerTitle: 'Record a reception',
    pickerOrder: 'Order',
    pickerPlaceholder: 'Choose the order that arrived',
    pickerHint: 'Only orders pending delivery or partially delivered.',
    pickerEmpty: 'No order waiting to be received',
    pickerConfirm: 'Continue',
    articleMissing:
      'This article is no longer in the catalogue: its reception cannot be recorded from here.',
    errorTenant:
      'The active obrador of your account has changed (maybe in another tab). Nothing was recorded: reload the page and try again.',
  },

  // Reception exceptions (OBR-83): what arrived differently from what was ordered.
  'backoffice.receptionExceptions': {
    title: 'Exceptions',
    subtitle:
      'What arrived differently from what was ordered. Review it here and record the decision.',
    filterLabel: 'Status',
    filterPending: 'Pending',
    filterResolved: 'Resolved',
    filterAll: 'All',
    empty: 'There are no exceptions waiting for review.',
    searchLabel: 'Search by article, delivery note or supplier',
    emptyFiltered: 'No exception matches the search.',
    emptyAll: 'There are no reception exceptions yet.',
    kindOverQuantity: 'More than ordered',
    kindUnorderedArticle: 'Article not on the order',
    kindOperatorFlagged: 'Marked by the operator',
    statusPending: 'Pending decision',
    statusAccepted: 'Accepted',
    statusRejected: 'Rejected',
    deliveryNoteMeta: 'Supplier delivery note {{number}}',
    ordered: 'Ordered',
    received: 'Received on this delivery note',
    excess: 'Excess',
    operatorNote: 'Operator note',
    resolutionAccepted: 'Accepted by {{user}} · {{date}}',
    resolutionRejected: 'Rejected by {{user}} · {{date}}',
    resolutionNote: 'Note',
    accept: 'Accept',
    reject: 'Reject',
    acceptTitle: 'Accept the exception',
    acceptBody:
      'The goods become available stock. It records who decided and when; the order does not change.',
    acceptNoteLabel: 'Note (optional)',
    acceptNotePlaceholder: 'Why it is accepted, if it needs explaining',
    rejectTitle: 'Reject the exception',
    rejectBody:
      'The goods can no longer be available for production: a return is created. Choose what happens to them and explain why it is rejected.',
    rejectNoteLabel: 'Reason for rejecting',
    rejectNotePlaceholder: 'Why it is rejected',
    resolveErrorGone: 'This exception no longer exists. Refresh the screen.',
    resolveErrorAlreadyResolved: 'Someone else has already resolved this exception.',
    resolveErrorNoteRequired: 'A reason is needed to reject it.',
    resolveErrorForbidden: 'You do not have permission to resolve exceptions.',
    resolveError: 'The decision could not be saved. Try again.',
    resolutionLabel: 'What happens to the goods',
    resolutionImmediate: 'Immediate return to the supplier',
    resolutionImmediateHint:
      'The carrier takes them now. They do not stay in stock: the return is recorded as returned.',
    resolutionPending: 'Pending return',
    resolutionPendingHint:
      'They stay at the obrador, blocked (not available for production), until the physical return is confirmed.',
    allocationsTitle: 'Lots being returned',
    allocationsHint:
      '{{quantity}} {{unit}} must go back. It has been split across the lots of this delivery note, from the most recent entry backwards; you can change it.',
    allocationLot: 'Lot {{lot}} · up to {{max}} {{unit}}',
    allocationSum: 'Total: {{sum}} of {{required}} {{unit}}',
    allocationMismatch: 'The total must be {{required}} {{unit}}.',
    allocationShortfall:
      'The lots of this delivery note do not hold enough to return {{required}} {{unit}}.',
    allocationNoLots: 'The lots of this delivery note could not be found.',
    resolutionRejectedImmediate: 'Immediate return to the supplier',
    resolutionRejectedPending: 'Pending return',
    returnsLink: 'View the returns',
    blockedNote:
      'Until it is decided, {{quantity}} {{unit}} do not count as available for production.',
    resolveErrorResolutionRequired: 'Choose what happens to the goods.',
    resolveErrorAllocation: 'The split across lots does not match the quantity to return.',
    resolveErrorExceedsLot:
      'More cannot be returned than arrived in a lot (there may already be returns for that lot).',
    resolveErrorWithoutSupplier: 'The delivery note has no supplier: the return cannot be created.',
  },

  // Purchase returns (OBR-117): goods going back to the supplier.
  'backoffice.purchaseReturns': {
    title: 'Returns',
    subtitle:
      'Goods going back to the supplier. A pending return is still at the obrador and does not count as available; confirming the return takes it out of stock.',
    filterStatusLabel: 'Status',
    filterPending: 'Waiting to be returned',
    filterReturned: 'Returned',
    filterAll: 'All',
    filterSupplierLabel: 'Supplier',
    allSuppliers: 'All suppliers',
    empty: 'There are no returns waiting to be returned.',
    searchLabel: 'Search by article, supplier, lot or delivery note',
    emptyFiltered: 'No return matches the search.',
    emptyAll: 'There are no purchase returns yet.',
    loadError: 'The returns could not be loaded.',
    statusPending: 'Waiting to be returned',
    statusReturned: 'Returned',
    originException: 'Rejected exception',
    originIncident: 'Incident',
    originReception: 'Delivery note',
    supplier: 'Supplier',
    reception: 'Delivery note',
    purchaseOrder: 'Order',
    article: 'Article',
    lot: 'Lot',
    quantity: 'Quantity',
    reason: 'Reason',
    observations: 'Notes',
    createdBy: 'Created by {{user}} · {{date}}',
    returnedBy: 'Returned by {{user}} · {{date}}',
    blockedNote: 'This quantity is blocked: it does not count as available until the return is confirmed.',
    confirmReturn: 'Confirm the return',
    confirmTitle: 'Confirm the return',
    confirmBody:
      '{{quantity}} {{unit}} of {{article}} (lot {{lot}}) will stop being part of stock. Confirm only once the goods have left the obrador.',
    confirm: 'Confirm',
    confirmErrorGone: 'This return no longer exists. Refresh the screen.',
    confirmErrorAlreadyReturned: 'This return is already confirmed.',
    confirmErrorForbidden: 'You do not have permission to confirm returns.',
    confirmError: 'The return could not be confirmed. Try again.',
    create: 'Create a return',
    createTitle: 'New purchase return',
    createBody:
      'From a lot of a purchase delivery note. Unless you mark it as already returned, the goods stay at the obrador, blocked, until you confirm the return.',
    lotLabel: 'Lot',
    lotOption: '{{lot}} · up to {{max}} {{unit}}',
    quantityLabel: 'Quantity ({{unit}})',
    reasonLabel: 'Reason (required)',
    reasonPlaceholder: 'Why it is being returned',
    observationsLabel: 'Notes (optional)',
    alreadyReturned: 'Already returned: the carrier has taken it',
    alreadyReturnedHint: 'The goods leave stock right now.',
    createAction: 'Create the return',
    noLots: 'This delivery note has no lots to return.',
    createErrorExceedsLot:
      'More cannot be returned than arrived in this lot (there may already be other returns).',
    createErrorReasonRequired: 'A reason is required.',
    createErrorWithoutSupplier: 'The delivery note has no supplier: the return cannot be created.',
    createErrorForbidden: 'You do not have permission to create returns.',
    createError: 'The return could not be created. Try again.',
    reasonOfIncident: 'Incident: {{comment}}',
    plannedDateLabel: 'Planned pickup or return date',
    plannedDateHint:
      'When the carrier is expected to collect it. The planta App shows it under Pending returns as due today, overdue or upcoming.',
    plannedShort: 'Planned pickup',
    plannedNone: 'No planned date',
    plannedChipOverdue: 'Overdue',
    plannedChipToday: 'Today',
    returnNumber: 'Return delivery note {{number}}',
    downloadNote: 'Download the delivery note (PDF)',
    downloadingNote: 'Generating the PDF…',
    downloadNoteError: 'The delivery note could not be generated. Please try again.',
    reschedule: 'Change the date',
    rescheduleTitle: 'Planned date of the return',
    rescheduleBody:
      'When the carrier is expected to collect it. You can leave it blank if it is not known yet.',
    rescheduleAction: 'Save the date',
    rescheduleErrorGone: 'This return no longer exists. Refresh the screen.',
    rescheduleErrorAlreadyReturned: 'This return has already left: the date can no longer be changed.',
    rescheduleErrorForbidden: 'You are not allowed to change the date of returns.',
    rescheduleError: 'The date could not be saved. Please try again.',
    exitHint: 'It can also be confirmed from the planta App, under Receiving › Pending returns.',
    noteTitle: 'Return delivery note',
    noteNumber: 'No.',
    noteDate: 'Date of exit',
    noteSupplier: 'Supplier',
    noteOrigin: 'Source document',
    noteReception: 'Receiving note',
    noteOrder: 'Order',
    noteSupplierDeliveryNote: 'Supplier delivery note',
    noteArticle: 'Article',
    noteLot: 'Lot',
    noteSupplierLot: 'Supplier lot',
    noteQuantity: 'Quantity',
    noteReason: 'Reason',
    noteObservations: 'Observations',
    noteIssuedBy: 'Exit confirmed by',
    noteFooter:
      'This document certifies the physical exit of the goods and their return to the supplier. It is not a corrective invoice or a credit note.',
    noteSignatureOut: 'Handed over by',
    noteSignatureCarrier: 'Received by the carrier',
  },


  // Reception delivery note detail and its correction (OBR-86).
  'backoffice.receptionDetail': {
    title: 'Delivery note {{number}}',
    titleFallback: 'Purchase delivery note',
    notFound: 'This delivery note was not found.',
    returnsTitle: 'Returns of this delivery note',
    returnsEmpty: 'This delivery note has no returns.',
    returnsCreate: 'Create a return',
    returnsOpen: 'View them under Returns',
    linesTitle: 'Entries',
    exceptionsTitle: 'Exceptions',
    exceptionsOpen: 'Review them in Exceptions',
    correctionsTitle: 'Corrections',
    noCorrections: 'This delivery note has not been corrected.',
    receivedAs: 'Received: {{quantity}}',
    counted: 'Counted: {{quantity}} {{pack}} \u00d7 {{factor}} {{base}}',
    writtenAs: 'Written when received: {{code}}',
    noLotCode: 'No lot',
    noExpiry: 'no date',
    correct: 'Correct the delivery note',
    correctHint:
      'To correct the quantity, the supplier lot, the expiry or the delivery note number. The original entry is never touched: the correction is recorded.',
    correctionMeta: '{{user}} · {{date}}',
    correctionReason: 'Reason',
    quantityChange: '{{before}} → {{after}}',
    lotCodeChange: 'Supplier lot: {{before}} → {{after}}',
    expiryChange: 'Expiry: {{before}} → {{after}}',
    movement: 'Corrective movement: {{delta}}',
    lotBalance: 'Lot stock afterwards: {{balance}}',
    lotBalanceNegative: 'The lot stock is now negative: it had already been used.',
    headerDeliveryNote: 'Supplier delivery note: {{from}} → {{to}}',
    headerDate: 'Date: {{from}} → {{to}}',
    orderEffect: 'Order {{order}}: {{from}} → {{to}}',
    cancelledNote: 'Every entry is now zero: the delivery note was cancelled.',
    dialogTitle: 'Correct delivery note {{number}}',
    dialogBody:
      'The original entry is never touched: the correction is recorded with its reason, and stock is corrected with a movement for the difference.',
    fieldDeliveryNote: 'Supplier delivery note',
    fieldDate: 'Delivery note date',
    fieldQuantity: 'Quantity ({{unit}})',
    fieldLotCode: 'Supplier lot',
    fieldExpiry: 'Expiry',
    currentHint: 'Now: {{quantity}}',
    annulEntry: 'Cancel the entry',
    entryTitle: '{{article}} · {{lot}}',
    reasonLabel: 'Reason for the correction',
    reasonPlaceholder: 'What happened and why it is being corrected',
    impactTitle: 'What will happen',
    impactNothing: 'There is no change yet.',
    impactLine: '{{article}}: {{before}} → {{after}} ({{delta}})',
    impactNegative: 'Lot {{lot}} has already been used: its stock will be {{balance}}.',
    impactCancelled: 'Every entry ends at zero: the delivery note will be cancelled.',
    impactOrder: 'Order {{order}} will go from «{{from}}» to «{{to}}».',
    impactOrderClosed: 'Order {{order}} was closed by hand with a reason: it is not reopened.',
    confirm: 'Confirm the correction',
    invalidQuantity: 'Enter a valid quantity (zero or more).',
    invalidDate: 'Enter a valid date.',
    errorReasonRequired: 'The reason for the correction is required.',
    errorNotFound: 'This delivery note no longer exists. Refresh the screen.',
    errorNotConfirmed: 'This delivery note can no longer be corrected (it is cancelled or not final).',
    errorLotNotFound: 'One of the entries is no longer part of this delivery note. Refresh the screen.',
    errorNothingToChange: 'There is no change to save.',
    errorBadQuantity: 'One of the quantities is not valid.',
    errorBadHeader: 'The delivery note number or date is not valid.',
    errorForbidden: 'You do not have permission to correct delivery notes.',
    errorGeneric: 'The correction could not be saved. Try again.',
  },

  'backoffice.purchaseOrderStatus': {
    in_progress: 'In progress',
    pending_dispatch: 'Pending dispatch',
    awaiting_delivery: 'Awaiting delivery',
    receiving: 'Partially delivered',
    delivered: 'Delivered',
    cancelled: 'Cancelled',
  },

  'backoffice.receptionControlResult': {
    conforme: 'Conforming',
    no_conforme: 'Non-conforming',
  },

  'backoffice.salesOrders': {
    articleTitle: 'Sales — {{article}}',
    articleCard: 'Article card',
    allOrders: 'All orders',
    articleEmpty: 'This article has never been sold: no order and no delivery note.',
    articleLinesTitle: 'Order lines',
    articleNoLines: 'This article does not appear on any sales order.',
    articleShipmentsTitle: 'Shipments',
    articleNoShipments: 'This article has not been shipped on any note.',
    totalOrdered: 'Ordered',
    totalShipped: 'Shipped',
    totalPending: 'Pending shipment',
    title: 'Sales orders',
    newOrder: 'New order',
    orderNumber: 'No.',
    client: 'Customer',
    orderDate: 'Date',
    deliveryDate: 'Delivery',
    status: 'Status',
    lines: 'Lines',
    empty: 'No sales orders yet',
    searchLabel: 'Search by number or client',
    emptyFiltered: 'No order matches the search.',
    searchPlaceholder: 'Search by number or customer…',
  },

  'backoffice.salesOrderDetail': {
    newTitle: 'New sales order',
    editTitle: 'Order {{number}}',
    client: 'Customer',
    selectClient: 'Select a customer',
    orderDate: 'Order date',
    deliveryDate: 'Delivery date',
    deliveryAddress: 'Delivery address',
    deliveryAddressHint: 'If left empty, the customer address is used.',
    deliveryPoint: 'Delivery point',
    deliveryPointFiscal: 'Client fiscal address',
    deliveryPointCustom: 'Another address',
    notes: 'Notes',
    linesTitle: 'Lines',
    addLine: 'Add article',
    article: 'Article',
    selectArticle: 'Select an article',
    orderedQuantity: 'Quantity',
    salesUnit: 'Sales unit',
    baseUnit: 'Base unit',
    unitPrice: 'Price (optional)',
    ordered: 'Ordered',
    shipped: 'Shipped',
    pending: 'Pending',
    remove: 'Remove',
    save: 'Save',
    confirm: 'Confirm order',
    confirmHint: 'The order moves to “Confirmed”, awaiting preparation.',
    closeOrder: 'Close order',
    closeOrderHint: 'Closes the order leaving the remainder unserved.',
    cancelOrder: 'Cancel order',
    shipmentsTitle: 'Sales delivery notes',
    noShipments: 'No shipment recorded yet.',
    emptyLines: 'Add at least one line to the order.',
    availableStock: 'Available',
    committedStock: 'Committed',
    insufficientStockWarning:
      'Insufficient stock for some articles (warning, non-blocking).',
  },

  'backoffice.shipments': {
    title: 'Sales delivery notes',
    shipmentNumber: 'Note no.',
    client: 'Customer',
    date: 'Date',
    order: 'Order',
    lines: 'Lines',
    downloadPdf: 'Download PDF',
    cancel: 'Cancel shipment',
    cancelPrompt:
      'Cancellation reason (mandatory). Reversal stock movements will be created.',
    empty: 'No sales delivery notes yet',
    status: 'Status',
    searchPlaceholder: 'Search by number or customer…',
    documentTitle: 'Sales delivery note',
    article: 'Article',
    lot: 'Lot',
    expiry: 'Expiry',
    quantity: 'Quantity',
    unitPrice: 'Price',
    amount: 'Amount',
    notesLabel: 'Notes',
  },

  'backoffice.salesOrderStatus': {
    in_progress: 'In progress',
    confirmed: 'Confirmed',
    preparing: 'Preparing',
    shipped: 'Shipped',
    cancelled: 'Cancelled',
    partially_shipped: 'Partially shipped',
  },

  'backoffice.shipmentStatus': {
    confirmed: 'Confirmed',
    cancelled: 'Cancelled',
  },

  'backoffice.technicalSheet': {
    title: 'Tech sheet',
    generate: 'Generate TS',
    legalDenomination: 'Legal denomination',
    ingredientsText: 'Ingredients',
    allergensText: 'Allergens',
    approve: 'Approve',
    netWeight: 'Net weight',
    conservation: 'Storage',
  },

  'backoffice.labels': {
    title: 'Labels',
    template: 'Template',
    print: 'Print',
    internalLot: 'Internal batch',
    generatedOnClose: 'Generated when closing the work order',
  },

  'backoffice.smartImporter': {
    title: 'Import',
    subtitle: 'Import articles from a spreadsheet: upload, map, review and create',
    back: 'Back',
    // Step indicator
    stepUpload: 'Upload',
    stepMap: 'Map',
    stepPreview: 'Review',
    stepResult: 'Result',
    // Upload step
    uploadFileTitle: 'Upload a file',
    uploadFileHint:
      'Choose a CSV, XLS or XLSX file exported from your spreadsheet. The first row must be the header.',
    chooseFile: 'Choose a file',
    // Templates
    templatesTitle: 'Templates',
    templatesHint:
      'Download a sheet with the column names the importer recognises and an «Instructions» tab that explains what goes in each one. Start from scratch, or download your current catalog, edit it and upload it back.',
    downloadTemplateBlank: 'Blank template',
    downloadTemplateCatalog: 'Template with current data ({{count}})',
    downloadTemplateCatalogLoading: 'Loading the catalog…',
    downloadTemplateCatalogHint:
      'The template with data carries the active articles of your catalog, already filled in. Rows you do not touch come back as «unchanged» and write nothing.',
    templateFilename: 'articles-template.xlsx',
    templateCatalogFilename: 'articles-catalog.xlsx',
    templateSheetName: 'Articles',
    templateExampleName: 'Wheat flour',
    templateExamplePackaging: 'Sack=25; Pallet=1000',
    booleanTrue: 'Yes',
    booleanFalse: 'No',
    // Column guide (OBR-9): the template's instructions sheet + the in-app guide
    guideSheetName: 'Instructions',
    guideTitle: 'How to fill in the article template',
    guideShow: 'What goes in each column',
    guideHide: 'Hide the column guide',
    guideRequiredChip: 'Required',
    guideColColumn: 'Column',
    guideColRequired: 'Required',
    guideColDescription: 'What it is',
    guideColFormat: 'Format',
    guideColBlank: 'If left blank',
    guideColValues: 'Accepted values',
    guideRuleSheet:
      'One row per article on the «{{sheet}}» sheet, which must stay first: it is the only one Obrava reads. Do not rename the columns; you can delete the ones you do not need.',
    guideRuleRequired: 'Only {{fields}} are required. If one is missing, the row is not imported.',
    guideRuleExisting:
      'If the code is already in the catalog, the row updates that article (or leaves it as it is, if you choose so at the review). A blank cell never erases anything.',
    guideRuleNumbers:
      'Numbers without thousands separators; decimals with a point or a comma: 1.5.',
    guideRuleReview:
      'Before writing anything, Obrava shows what it will do with each row, and the rows with an error, with the row number and the reason.',
    guideValuesAny: '—',
    guideValuesNone: 'None yet.',
    guideValuesYesNo: 'Yes and No; also 1 and 0, and an X for yes.',
    guideValuesComposition:
      'Any ingredient; those not in Settings · Ingredients are created on import.',
    guideDesc_sku:
      'The article’s code in the obrador. If it already exists, the row updates that article.',
    guideFormat_sku: 'Text. It cannot repeat within the sheet.',
    guideBlank_sku: 'The row is not imported.',
    guideDesc_name: 'The article’s name, as everyone will see it in Obrava.',
    guideFormat_name: 'Text.',
    guideBlank_name: 'The row is not imported.',
    guideDesc_article_type: 'What kind of article it is.',
    guideFormat_article_type:
      'A value from the list, in full or abbreviated. With any other value, the row is not imported.',
    guideBlank_article_type: 'Raw material.',
    guideDesc_base_unit: 'The unit the article’s stock is counted in.',
    guideFormat_base_unit:
      'The code of a unit from Settings · Units. With any other, the row is not imported.',
    guideBlank_base_unit: 'The row is not imported.',
    guideDesc_recipe_unit:
      'The unit the recipe uses for this raw material when it is not the base unit (eggs: base Un, recipe kg).',
    guideFormat_recipe_unit:
      'The code of a unit from Settings · Units. Raw materials only. With an unknown code the row is not imported; the same as the base unit means the recipe already uses it.',
    guideBlank_recipe_unit: 'The recipe uses the base unit.',
    guideDesc_recipe_unit_factor:
      'How many recipe units equal ONE base unit: 1 Un = 0.06 kg is written 0.06.',
    guideFormat_recipe_unit_factor:
      'A number greater than zero, with comma or dot. Required when the recipe unit differs from the base; it cannot be set without a recipe unit.',
    guideBlank_recipe_unit_factor: 'No equivalence: the recipe uses the base unit.',
    guideDesc_category: 'The catalog category the article belongs to.',
    guideFormat_category:
      'The name of a category from Settings · Categories. If it is not there, you get a warning and the article has no category.',
    guideBlank_category: 'No category.',
    guideDesc_barcode: 'The article’s barcode (EAN), if it has one.',
    guideFormat_barcode: 'The digits as printed on the label, as text or as a number.',
    guideBlank_barcode: 'No barcode.',
    guideDesc_shelf_life_days:
      'How many days the article lasts. It suggests the lots’ expiry date.',
    guideFormat_shelf_life_days: 'Whole number of days, 0 or more.',
    guideBlank_shelf_life_days: 'No shelf life.',
    guideDesc_min_stock: 'Below this quantity, Obrava warns that stock is running low.',
    guideFormat_min_stock: 'Number in the base unit, 0 or more.',
    guideBlank_min_stock: 'No minimum.',
    guideDesc_max_stock: 'The most stock you want to hold of the article.',
    guideFormat_max_stock: 'Number in the base unit, 0 or more.',
    guideBlank_max_stock: 'No maximum.',
    guideDesc_tax: 'The tax that applies to the article.',
    guideFormat_tax:
      'The name or the rate of a tax from Settings · Taxes. With any other, the row is not imported.',
    guideBlank_tax: 'No tax; you can pick it later on the article card.',
    guideDesc_purchase_price: 'The purchase price, per base unit.',
    guideFormat_purchase_price: 'Number, 0 or more.',
    guideBlank_purchase_price: 'No purchase price.',
    guideDesc_sale_price: 'The sale price, per base unit. Only used if the article is sold.',
    guideFormat_sale_price: 'Number, 0 or more.',
    guideBlank_sale_price: 'No sale price.',
    guideDesc_is_purchased: 'Whether the article is bought from a supplier.',
    guideFormat_is_purchased: 'Yes or No.',
    guideBlank_is_purchased: 'No.',
    guideDesc_is_manufactured: 'Whether the article is made in the obrador with a recipe.',
    guideFormat_is_manufactured: 'Yes or No.',
    guideBlank_is_manufactured: 'No.',
    guideDesc_is_sold: 'Whether the article is sold to customers.',
    guideFormat_is_sold: 'Yes or No.',
    guideBlank_is_sold: 'No.',
    guideDesc_is_variable_weight:
      'Whether the article has no fixed equivalence and is weighed at every movement.',
    guideFormat_is_variable_weight: 'Yes or No.',
    guideBlank_is_variable_weight: 'No.',
    guideDesc_default_zone:
      'The warehouse zone where the article goes when it is received or produced.',
    guideFormat_default_zone:
      'The code of a zone from Settings · Obrador structure. With any other, the row is not imported.',
    guideBlank_default_zone: 'No default zone.',
    guideDesc_packaging_units: 'How the article is packed: how many base units each package holds.',
    guideFormat_packaging_units:
      'Package=quantity, separated by semicolons: Sack=25; Pallet=1000. It adds or updates; it never removes one.',
    guideBlank_packaging_units: 'No packages.',
    guideDesc_composition: 'The ingredients the label declares, from most to least.',
    guideFormat_composition:
      'Ingredients in order, separated by semicolons; the % is optional: Flour=62; Water=30; Salt. It replaces the whole list.',
    guideBlank_composition: 'No composition.',
    pasteTitle: 'Or paste the data',
    pasteLabel: 'Paste CSV or TSV',
    pasteHint:
      'Copy the cells from Excel or Numbers and paste them here. The first row must be the header.',
    parsePaste: 'Continue',
    noRows: 'No data rows found. Check that there is a header and at least one row.',
    fileReadError: 'The file could not be read',
    // Map step
    mapTitle: 'Map the columns',
    mapHint:
      'Tell us which article field each column matches. We have proposed a mapping from the headers and from previous imports.',
    mapToLabel: 'Target field',
    unmapped: 'Do not map',
    sampleValue: 'Example: {{value}}',
    mapMissingRequired: 'Required fields missing: {{fields}}',
    toPreview: 'Review the rows',
    checkingCatalog: 'Checking the catalog…',
    // Mappable target fields
    field_sku: 'Code (SKU)',
    field_name: 'Name',
    field_article_type: 'Type',
    field_base_unit: 'Base unit',
    field_recipe_unit: 'Recipe unit',
    field_recipe_unit_factor: 'Recipe equivalence',
    field_category: 'Category',
    field_barcode: 'Barcode',
    field_shelf_life_days: 'Shelf life (days)',
    field_min_stock: 'Minimum stock',
    field_max_stock: 'Maximum stock',
    field_vat_rate: 'VAT (%)',
    field_tax: 'Tax (VAT)',
    field_purchase_price: 'Purchase price',
    field_sale_price: 'Sale price',
    field_is_purchased: 'Purchased',
    field_is_manufactured: 'Manufactured',
    field_is_sold: 'Sold',
    field_is_variable_conversion: 'Variable conversion',
    field_default_zone: 'Default zone (code)',
    field_packaging_units: 'Packaging units',
    field_composition: 'Declared composition',
    // Validation messages (per row)
    msgRequiredMissing: 'Required field «{{field}}» is missing',
    msgUnknownTax:
      'The tax «{{value}}» does not exist. Use the name or the rate of a tax from Settings · Taxes.',
    msgUnknownUnit: 'Unknown unit: «{{code}}»',
    msgRecipeUnitFactorMissing:
      'The recipe equivalence is missing: the recipe unit «{{code}}» differs from the base unit. Enter how many recipe units equal one base unit.',
    msgRecipeUnitFactorInvalid:
      'The recipe equivalence «{{value}}» must be a number greater than zero.',
    msgRecipeFactorWithoutUnit:
      'A recipe equivalence was given without a recipe unit: it cannot be applied.',
    msgRecipeUnitNotRawMaterial:
      'The recipe unit «{{code}}» is for raw materials only: it will be skipped for this article.',
    msgUnknownCategory: 'Unknown category «{{name}}»; it will be skipped',
    msgUnknownZone: 'Zone «{{code}}» does not exist. Use the code of a warehouse zone.',
    msgSalePriceNotSold:
      'Sale price «{{value}}» on an article marked as not sellable: it will be saved, but the article sheet will not show it.',
    msgUnknownPackagingUnit:
      'Packaging unit «{{name}}» is not in the master; create it in Settings before importing.',
    msgPackaging_syntax:
      'Packaging unit #{{position}} («{{part}}») is not in the «Unit=factor» form.',
    msgPackaging_factor:
      'Packaging unit #{{position}} («{{part}}») has no numeric factor greater than zero.',
    msgPackaging_duplicate:
      'Packaging unit #{{position}} («{{part}}») repeats a unit: an article can only have one size of it.',
    msgComposition_syntax:
      'Ingredient #{{position}} («{{part}}») is not in the «Ingredient» or «Ingredient=%» form.',
    msgComposition_percentage:
      'Ingredient #{{position}} («{{part}}») has no numeric percentage between 0 and 100.',
    msgComposition_duplicate:
      'Ingredient #{{position}} («{{part}}») already appears earlier in the list: an article cannot declare it twice.',
    msgNewIngredients_one: 'Ingredient «{{names}}» will be created in the master.',
    msgNewIngredients_other: '{{count}} new ingredients will be created in the master: {{names}}.',
    msgCompositionDerived:
      'This article takes its ingredients from the recipe, not from the article sheet: the composition will be saved, but the tech sheet will not use it.',
    msgCompositionReplaces:
      'It will replace the {{before}} ingredients the article has now with the {{after}} on the sheet: the cell stands for the whole list, because the order is what matters on the label.',
    msgInvalidArticleType: 'Invalid type: «{{value}}»',
    msgInvalidNumber: 'Invalid number in «{{field}}»: «{{value}}»',
    msgInvalidBoolean: 'Invalid yes/no value in «{{field}}»: «{{value}}»',
    msgSchemaIssue: '{{path}}: {{message}}',
    msgDuplicateInFile: 'Code repeated in the file (already on row {{row}})',
    msgSkuFormat:
      'The code «{{code}}» does not follow the obrador’s article code format (like {{example}}). It is not corrected automatically: fix it in the file.',
    // Preview step
    previewTitle: 'Row review',
    previewSummary_one:
      '{{count}} row · {{created}} to create · {{updated}} to update · {{unchanged}} unchanged · {{skipped}} to skip · {{error}} failed',
    previewSummary_other:
      '{{count}} rows · {{created}} to create · {{updated}} to update · {{unchanged}} unchanged · {{skipped}} to skip · {{error}} failed',
    colRow: 'Row',
    colAction: 'Action',
    colSkuName: 'Code · Name',
    colMessages: 'Notes',
    noMessages: '—',
    // What to do with codes that already exist
    strategyTitle: 'Codes that already exist',
    strategyLabel: 'What do we do with articles that are already there?',
    strategyHint:
      'Only the columns you mapped and filled in are written. A blank cell never clears an existing value, and a column you did not map is never touched.',
    strategy_update_existing: 'Update them with the sheet data',
    strategy_skip_existing: 'Leave them as they are',
    // Per-row outcome
    actionCreated: 'New',
    actionUpdated: 'Update',
    actionUnchanged: 'Unchanged',
    actionSkipped: 'Skipped',
    actionError: 'Error',
    noteWillUpdate: 'Changes: {{fields}}',
    noteWillSkip: 'Already exists; will not be touched (would change: {{fields}})',
    noteUnchanged: 'Already exists with the same information',
    nothingToChange:
      'No row changes anything: all these articles are already there with the same information.',
    nothingToImport: 'There is no valid row to import. Check the mapping or the file.',
    commitCreateOnly_one: 'Create {{count}} article',
    commitCreateOnly_other: 'Create {{count}} articles',
    commitUpdateOnly_one: 'Update {{count}} article',
    commitUpdateOnly_other: 'Update {{count}} articles',
    commitBoth: 'Create {{created}} and update {{updated}}',
    committing: 'Writing…',
    noRunError: 'The import run was not found; review the rows again.',
    // Result step
    resultSummary_one: 'Import complete · {{count}} article written to the catalog',
    resultSummary_other: 'Import complete · {{count}} articles written to the catalog',
    resultNoChanges_one:
      'Nothing changed in the catalog: the row in the file created or updated nothing.',
    resultNoChanges_other:
      'Nothing changed in the catalog: none of the {{count}} rows in the file created or updated anything.',
    resultTitle: 'Import result',
    resultCreated: 'Created',
    resultUpdated: 'Updated',
    resultUnchanged: 'Unchanged',
    resultSkipped: 'Skipped',
    resultErrors: 'Errors',
    resultTotal_one: '{{count}} row in the file',
    resultTotal_other: '{{count}} rows in the file',
    newImport: 'New import',
    viewCatalog: 'Go to the catalog',
    // ── Existences importer (entity 'stock') ──────────────────────────────────
    entityTitle: 'What do you want to import?',
    entityLabel: 'Data type',
    entityHint: 'Switching type clears whatever you had half-imported.',
    entity_articles: 'Article catalog',
    entity_stock: 'Existences',
    // Mappable stock fields
    field_quantity: 'Quantity',
    field_unit: 'Unit',
    field_lot_code: 'Lot',
    field_expiry_date: 'Expiry',
    // Stock template
    templatesHintStock:
      'Download a sheet with the columns the importer recognizes, with one lotted example row and one without.',
    downloadTemplateStock: 'Existences template',
    downloadTemplateStockHint:
      'Write the TOTAL quantity you hold of each article. Leave the unit blank to use the article’s base unit, and write the expiry as 2026-12-31.',
    templateStockSheetName: 'Existences',
    templateStockFilename: 'existences-template.xlsx',
    // Stock validation messages
    msgUnknownArticle: 'No article carries the code «{{sku}}»; create it in the catalog first',
    msgArticleNotStockTracked:
      'Article «{{sku}}» does not manage existences; the stock will be loaded anyway',
    msgNegativeQuantity: 'The quantity cannot be negative: «{{value}}»',
    msgNoUnitConversion:
      'No conversion is declared from «{{code}}» to the base unit of «{{sku}}»',
    msgInvalidDate: 'Invalid date: «{{value}}»',
    msgExpiryNeedsLot:
      'The expiry «{{value}}» needs a lot; fill in the lot or remove the expiry',
    msgAmbiguousDate: '«{{value}}» was read as {{date}} (day/month); please check',
    msgExpiryInPast: 'The expiry {{date}} has already passed',
    msgLotExpiryKept:
      'Lot «{{lot}}» already exists with expiry {{stored}}; the stored one is kept',
    msgDuplicateCoordinateInFile: 'This article and lot already appear on row {{row}}',
    // Stock preview
    stockLotNote:
      'The lot code in the file is saved as the «Supplier lot», so it matches the supplier’s own. The internal lot number (MP-L-…) is generated by Obrava and cannot be set from the file: Stock shows it with the supplier code next to it.',
    strategyHintStock:
      'Each row states the TOTAL held. Only the difference from what is already on record is written, so re-importing the same file moves nothing.',
    strategyStock_update_existing: 'Adjust them to the sheet’s quantity',
    strategyStock_skip_existing: 'Leave them exactly as they are',
    noteStockLot: 'lot {{lot}}',
    noteStockCreate: 'Will load {{quantity}}',
    noteStockUpdate: 'Will go from {{from}} to {{to}}',
    noteStockSkip: 'Stays at {{current}}',
    noteStockUnchanged: 'Already holds {{quantity}}',
    commitCreateOnlyStock_one: 'Load {{count}} existence',
    commitCreateOnlyStock_other: 'Load {{count}} existences',
    commitUpdateOnlyStock_one: 'Adjust {{count}} existence',
    commitUpdateOnlyStock_other: 'Adjust {{count}} existences',
    commitBothStock: 'Load {{created}} and adjust {{updated}}',
    // Stock commit + result
    stockContextMissing:
      'Current existences could not be read yet; please try again in a moment.',
    stockReceptionReference: 'SALDO-INICIAL',
    stockReceptionNote: 'Opening existences load (importer)',
    resultSummaryStock_one: 'Import complete · {{count}} existences line written',
    resultSummaryStock_other: 'Import complete · {{count}} existences lines written',
    resultNoChangesStock_one:
      'Nothing moved: the file’s row already matched the current existences.',
    resultNoChangesStock_other:
      'Nothing moved: none of the {{count}} rows changed the current existences.',
    viewStock: 'Go to existences',
  },

  'backoffice.supplierImport': {
    title: 'Import suppliers',
    subtitle: 'Load the supplier list from a spreadsheet',
    // Step 1 — template and file
    templateTitle: '1. Download the template',
    templateHint:
      'The template carries the columns Obrava recognizes plus one example row. Fill it in and upload it back.',
    downloadTemplate: 'Blank template',
    downloadCurrent: 'Template with the current suppliers ({{count}})',
    downloadCurrentHint:
      'Download the suppliers you already have, edit them in Excel and upload them back: the rows you do not touch write nothing.',
    templateFilename: 'supplier-template.xlsx',
    currentFilename: 'suppliers.xlsx',
    templateSheetName: 'Suppliers',
    uploadTitle: '2. Upload the filled file',
    uploadHint:
      'Accepts CSV, XLS and XLSX. The first row must be the header, using the template column names.',
    chooseFile: 'Choose a file',
    reading: 'Reading the file…',
    fileReadError: 'The file could not be read.',
    noRows: 'The file has no data rows. Check that there is a header and at least one row.',
    missingColumns:
      'The required column «{{fields}}» was not found. Download the template and use its column names.',
    ignoredColumns: 'Columns that will not be used: {{columns}}',
    legacyTermsColumn_one: 'The column «{{column}}» is no longer imported: Obrava no longer has «Commercial terms». {{count}} row has content: move it to «Notes» and upload the file again.',
    legacyTermsColumn_other: 'The column «{{column}}» is no longer imported: Obrava no longer has «Commercial terms». {{count}} rows have content: move it to «Notes» and upload the file again.',
    columnsGuide: 'Delivery days: days of the week separated by commas, e.g. «Mon, Wed, Fri» (also «Monday, Wednesday» or «Mon-Fri»). Order channel: «Email», «WhatsApp» or «Other means»; the Email channel needs the order email, and the WhatsApp channel the order phone/WhatsApp (the general email and phone do not replace them). Free-form information, such as payment terms, goes in «Notes». The three emails — general, order and quality — are independent columns: none is assumed from another.',
    guideSheetName: 'Instructions',
    guideColField: 'Field',
    guideColWhat: 'What goes in',
    guideColExample: 'Example',
    guide_code:
      'Optional. If blank, Obrava generates it when creating the supplier. A new code must follow the obrador’s code format (Settings) and cannot be repeated; a code that already exists identifies the supplier.',
    guide_name: 'Required.',
    guide_commercial_name: 'Optional. The name it is known by, if different from the legal name.',
    guide_tax_id: 'Optional. Cannot be repeated among active suppliers.',
    guide_contact_name: 'Optional.',
    guide_phone: 'Optional. General contact phone; the order phone goes separately.',
    guide_email: 'Optional. General contact email; the order and quality emails go separately.',
    guide_address: 'Optional. Main (billing) address.',
    guide_delivery_weekdays: 'Optional. Days of the week it delivers, separated by commas: Mon, Tue, Wed, Thu, Fri, Sat, Sun (full names or a range such as Mon-Fri also work).',
    guide_order_channel: 'Optional. Email, WhatsApp or Other means (phone, in person…).',
    guide_order_emails: 'One or more emails separated by commas (maximum 10). Required if the channel is Email.',
    guide_order_phone: 'Order phone or WhatsApp, with the country prefix if possible. Required if the channel is WhatsApp.',
    guide_quality_email: 'Optional. One email: where technical sheet requests and renewals go.',
    guide_notes: 'Optional. Free-form information: payment terms, remarks…',
    guide_status: 'Optional. Active or Inactive.',
    recognizedColumns: 'Recognized columns: {{columns}}',
    // Fields
    field_code: 'Supplier code',
    field_name: 'Name / legal name',
    field_commercial_name: 'Trading name',
    field_tax_id: 'Tax ID',
    field_contact_name: 'Contact person',
    field_phone: 'Phone',
    field_email: 'Email (general)',
    field_address: 'Main address',
    field_delivery_weekdays: 'Usual delivery days',
    field_order_channel: 'Order channel',
    field_order_emails: 'Order email',
    field_order_phone: 'Order phone/WhatsApp',
    field_quality_email: 'Quality email',
    field_notes: 'Notes',
    field_status: 'Status',
    statusActive: 'Active',
    statusInactive: 'Inactive',
    channelEmail: 'Email',
    channelWhatsapp: 'WhatsApp',
    channelManual: 'Other means',
    templateExampleName: 'Puig Flour Ltd',
    templateExampleCommercialName: 'Puig Flour',
    templateExampleContact: 'Anna Puig',
    templateExampleAddress: '12 Mill Street · 17800 Olot',
    templateExampleNotes: 'Payment in 30 days · shipping included',
    // Per-row notes
    msgRequiredMissing: 'The required field «{{field}}» is missing',
    msgInvalidEmail: 'Invalid email address in «{{field}}»: «{{value}}»',
    msgInvalidStatus: 'Unrecognized status: «{{value}}». Use «Active» or «Inactive».',
    msgInvalidBoolean: 'Invalid yes/no value in «{{field}}»: «{{value}}»',
    msgInvalidNumber: 'Invalid numeric value in «{{field}}»: «{{value}}»',
    msgAmbiguousTaxId:
      'More than one active supplier carries the tax ID «{{value}}»: fill in the code to say which one.',
    msgUnidentifiable:
      'With no code and no tax ID we cannot check whether it already exists; it will be created as a new supplier.',
    msgDuplicateInFile: 'Repeated in the file (already on row {{row}})',
    msgCodeTaken: 'The code «{{code}}» already belongs to another supplier ({{name}}).',
    msgCodeFormat:
      'The code «{{code}}» in «{{field}}» does not follow the obrador’s code format (like {{example}}). It is not corrected automatically: fix it in the file or leave it empty and one will be generated.',
    msgTaxIdTaken: 'The tax ID «{{value}}» already belongs to another supplier ({{name}}).',
    msgTaxIdInFile: 'The tax ID «{{value}}» already appears on row {{row}} of the file.',
    msgInvalidWeekdays: 'Delivery days not recognised in «{{field}}»: «{{value}}». Write them as «Mon, Wed, Fri» (also «Monday, Wednesday» or «Mon-Fri»).',
    msgInvalidChannel: 'Channel not recognised in «{{field}}»: «{{value}}». Allowed values: Email, WhatsApp, Other means.',
    msgChannelUnavailable: 'The channel «{{value}}» is not available in Obrava yet. Allowed values: Email, WhatsApp, Other means.',
    msgInvalidOrderEmails: 'Invalid order email: «{{value}}»',
    msgTooManyOrderEmails: 'At most {{max}} order emails per supplier.',
    msgInvalidPhone: 'Invalid phone in «{{field}}»: «{{value}}». Use 7 to 15 digits, with spaces and a + prefix if needed.',
    msgOrderEmailRequired: 'The channel is Email: «{{field}}» is missing. The general email does not replace it.',
    msgOrderPhoneRequired: 'The channel is WhatsApp: «{{field}}» is missing. The general phone does not replace it.',
    // Step 3 — preview
    previewTitle: '3. Review before importing',
    previewSummary_one:
      '{{count}} row · {{created}} to create · {{updated}} to update · {{unchanged}} unchanged · {{skipped}} to skip · {{error}} with errors',
    previewSummary_other:
      '{{count}} rows · {{created}} to create · {{updated}} to update · {{unchanged}} unchanged · {{skipped}} to skip · {{error}} with errors',
    colRow: 'Row',
    colAction: 'Action',
    colSupplier: 'Supplier',
    colMessages: 'Notes',
    noMessages: '—',
    codeWillBeGenerated: 'auto code',
    strategyLabel: 'What do we do with suppliers that already exist?',
    strategyHint:
      'Only the columns the sheet fills in are written. A blank cell never clears a stored value.',
    strategy_update_existing: 'Update them with the sheet data',
    strategy_skip_existing: 'Leave them as they are',
    actionCreated: 'New',
    actionUpdated: 'Update',
    actionUnchanged: 'Unchanged',
    actionSkipped: 'Skipped',
    actionError: 'Error',
    noteWillUpdate: 'Changes: {{fields}}',
    noteWillSkip: 'Already exists; it will not be touched (would change: {{fields}})',
    noteUnchanged: 'Already exists with the same information',
    noteWillDeactivate: 'Will be deactivated',
    noteWillReactivate: 'Will be reactivated',
    deactivationReason: 'Supplier import: the file marks it as inactive',
    nothingToChange: 'No row changes anything: every supplier in the file is already the same.',
    nothingToImport: 'No row can be imported. Fix the errors and upload the file again.',
    commitCreateOnly_one: 'Create {{count}} supplier',
    commitCreateOnly_other: 'Create {{count}} suppliers',
    commitUpdateOnly_one: 'Update {{count}} supplier',
    commitUpdateOnly_other: 'Update {{count}} suppliers',
    commitBoth: 'Create {{created}} and update {{updated}}',
    committing: 'Importing…',
    back: 'Back',
    // Step 4 — result
    resultTitle: 'Import result',
    resultSummary_one: '{{count}} supplier imported',
    resultSummary_other: '{{count}} suppliers imported',
    resultNoChanges_one: 'Nothing changed: {{count}} row processed.',
    resultNoChanges_other: 'Nothing changed: {{count}} rows processed.',
    resultCreated: 'Created',
    resultUpdated: 'Updated',
    resultUnchanged: 'Unchanged',
    resultSkipped: 'Skipped',
    resultErrors: 'Errors',
    resultTotal_one: '{{count}} row in the file',
    resultTotal_other: '{{count}} rows in the file',
    newImport: 'Import another file',
    close: 'Close',
  },

  'backoffice.supplierArticleImport': {
    title: 'Import article-supplier links',
    subtitle: 'Load which suppliers serve each article, at what price and in which unit',
    prerequisites:
      'The suppliers, the articles and each article purchase units must exist first. This importer creates none of them.',
    // Step 1 — template and file
    templateTitle: '1. Download the template',
    templateHint:
      'One row per article ↔ supplier link. The codes must exist: the importer links, it does not create articles, suppliers or conversions.',
    downloadTemplate: 'Blank template',
    downloadCurrent: 'Template with the current links ({{count}})',
    downloadCurrentHint:
      'Download what you already have, edit it in Excel and upload it back: the rows you do not touch write nothing.',
    templateFilename: 'article-supplier-links-template.xlsx',
    currentFilename: 'article-supplier-links.xlsx',
    templateSheetName: 'Article-supplier links',
    uploadTitle: '2. Upload the filled file',
    uploadHint:
      'Accepts CSV, XLS and XLSX. The first row must be the header, using the template column names.',
    chooseFile: 'Choose a file',
    reading: 'Reading the file…',
    fileReadError: 'The file could not be read.',
    noRows: 'The file has no data rows. Check that there is a header and at least one row.',
    missingColumns:
      'The required column «{{fields}}» was not found. Download the template and use its column names.',
    ignoredColumns: 'Columns that will not be used: {{columns}}',
    // Fields
    field_article_code: 'Article code',
    field_supplier_code: 'Supplier code',
    field_supplier_ref: 'Supplier reference',
    field_supplier_description: 'Supplier description',
    field_last_price: 'Purchase price (per base unit)',
    field_purchase_unit: 'Purchase unit',
    field_purchase_unit_factor: 'Equivalence (informational)',
    field_is_primary: 'Primary supplier',
    booleanTrue: 'Yes',
    booleanFalse: 'No',
    // Template examples
    exampleArticleCode: 'MP00001',
    exampleSupplierCode: 'PRV-0001',
    exampleSupplierRef: 'FAR-25',
    exampleDescription: 'Strong flour 25 kg',
    exampleUnit: 'Sack',
    // Per-row notes
    msgRequiredMissing: 'The required field «{{field}}» is missing',
    msgInvalidNumber: 'Invalid numeric value in «{{field}}»: «{{value}}»',
    msgInvalidBoolean: 'Invalid yes/no value in «{{field}}»: «{{value}}»',
    msgUnknownArticle: 'The article code «{{code}}» does not exist in the catalog.',
    msgUnknownSupplier: 'The supplier code «{{code}}» does not exist in the master.',
    msgUnknownUnit:
      'The unit «{{unit}}» does not exist. Use a packaging unit name or the article base unit code.',
    msgUnitNotForArticle:
      'Article «{{code}}» has no conversion for «{{unit}}»: define it on the article card.',
    msgDuplicateInFile:
      'This article and this supplier already appear together on row {{row}}.',
    msgTwoPrimaries:
      'This article already has row {{row}} marked as the primary supplier; there can only be one.',
    msgPrimaryMoves: 'Will become the primary supplier ({{name}} stops being it)',
    // Step 3 — preview
    previewTitle: '3. Review before importing',
    previewSummary_one:
      '{{count}} row · {{created}} to create · {{updated}} to update · {{unchanged}} unchanged · {{skipped}} to skip · {{error}} with errors',
    previewSummary_other:
      '{{count}} rows · {{created}} to create · {{updated}} to update · {{unchanged}} unchanged · {{skipped}} to skip · {{error}} with errors',
    colRow: 'Row',
    colAction: 'Action',
    colLink: 'Article · Supplier',
    colMessages: 'Notes',
    noMessages: '—',
    strategyLabel: 'What do we do with links that already exist?',
    strategyHint:
      'Only the columns the sheet fills in are written. A blank cell never clears a value, and a link the sheet does not mention is left alone.',
    strategy_update_existing: 'Update them with the sheet data',
    strategy_skip_existing: 'Leave them as they are',
    actionCreated: 'New',
    actionUpdated: 'Update',
    actionUnchanged: 'Unchanged',
    actionSkipped: 'Skipped',
    actionError: 'Error',
    noteWillUpdate: 'Changes: {{fields}}',
    noteWillSkip: 'Already exists; it will not be touched (would change: {{fields}})',
    noteUnchanged: 'Already exists with the same information',
    nothingToChange: 'No row changes anything: everything in the file is already the same.',
    nothingToImport: 'No row can be imported. Fix the errors and upload the file again.',
    commitCreateOnly_one: 'Create {{count}} link',
    commitCreateOnly_other: 'Create {{count}} links',
    commitUpdateOnly_one: 'Update {{count}} link',
    commitUpdateOnly_other: 'Update {{count}} links',
    commitBoth: 'Create {{created}} and update {{updated}}',
    committing: 'Importing…',
    back: 'Back',
    // Step 4 — result
    resultTitle: 'Import result',
    resultSummary_one: '{{count}} link imported',
    resultSummary_other: '{{count}} links imported',
    resultNoChanges_one: 'Nothing changed: {{count}} row processed.',
    resultNoChanges_other: 'Nothing changed: {{count}} rows processed.',
    resultCreated: 'Created',
    resultUpdated: 'Updated',
    resultUnchanged: 'Unchanged',
    resultSkipped: 'Skipped',
    resultErrors: 'Errors',
    resultTotal_one: '{{count}} row in the file',
    resultTotal_other: '{{count}} rows in the file',
    newImport: 'Import another file',
    close: 'Close',
  },
  'backoffice.clientImport': {
    title: 'Import customers',
    subtitle: 'Load customers and their delivery points from a spreadsheet',
    // Step 1 — template and file
    templateTitle: '1. Download the template',
    templateHint:
      'The template has two sheets: «Customers» (one row per customer) and «Delivery addresses» (one row per delivery point, tied to its customer by code).',
    downloadTemplate: 'Blank template',
    downloadCurrent: 'Template with the current customers ({{count}})',
    downloadCurrentHint:
      'Download the customers and points you already have, edit them in Excel and upload them back: the rows you do not touch write nothing.',
    templateFilename: 'customer-template.xlsx',
    currentFilename: 'customers.xlsx',
    sheetClients: 'Customers',
    sheetPoints: 'Delivery addresses',
    uploadTitle: '2. Upload the filled file',
    uploadHint:
      'Accepts XLSX and XLS with both sheets. A CSV works too, but can only carry customers: a flat file has no sheets.',
    chooseFile: 'Choose a file',
    reading: 'Reading the file…',
    fileReadError: 'The file could not be read.',
    noRows: 'The «{{sheet}}» sheet has no data rows. Check that there is a header and at least one row.',
    missingColumns:
      'The required column «{{fields}}» was not found on the «{{sheet}}» sheet. Download the template and use its column names.',
    ignoredColumns: '{{sheet}} · columns that will not be used: {{columns}}',
    noPointsSheet:
      'The file carries no «Delivery addresses» sheet: only the customers will be imported.',
    // Customer fields
    field_code: 'Customer code',
    field_name: 'Name / legal name',
    field_commercial_name: 'Trading name',
    field_tax_id: 'Tax ID',
    field_contact_name: 'Main contact person',
    field_phone: 'Main phone',
    field_email: 'Main email',
    field_address: 'Billing address',
    field_notes: 'Notes',
    field_status: 'Status',
    // Delivery point fields
    point_client_code: 'Customer code',
    point_code: 'Delivery point code',
    point_name: 'Delivery point name',
    point_address: 'Address',
    point_postal_code: 'Postcode',
    point_city: 'City',
    point_province: 'Province',
    point_country: 'Country',
    point_contact_name: 'Contact person',
    point_phone: 'Phone',
    point_email: 'Email',
    point_delivery_instructions: 'Delivery instructions',
    point_is_default: 'Default address',
    statusActive: 'Active',
    statusInactive: 'Inactive',
    booleanTrue: 'Yes',
    booleanFalse: 'No',
    // Template examples
    exampleClientName: 'Girona Pastries Ltd',
    exampleClientCommercial: 'Girona Pastries',
    exampleClientContact: 'Marta Vidal',
    exampleClientAddress: '4 Devesa Avenue · 17001 Girona',
    exampleClientNotes: 'Invoiced at month end',
    examplePointName: 'Girona city shop',
    examplePointAddress: '21 Nou Street',
    examplePointCity: 'Girona',
    examplePointProvince: 'Girona',
    examplePointCountry: 'Spain',
    examplePointContact: 'Jordi Serra',
    examplePointInstructions: 'Deliver before 9 am · rear loading bay',
    exampleWarehouseName: 'Central warehouse',
    exampleWarehouseAddress: 'Mas Xirgu industrial estate, unit 7',
    // Per-row notes
    msgRequiredMissing: 'The required field «{{field}}» is missing',
    msgInvalidEmail: 'Invalid email address: «{{value}}»',
    msgInvalidStatus: 'Unrecognized status: «{{value}}». Use «Active» or «Inactive».',
    msgInvalidBoolean: 'Invalid yes/no value in «{{field}}»: «{{value}}»',
    msgInvalidNumber: 'Invalid numeric value in «{{field}}»: «{{value}}»',
    msgAmbiguousTaxId:
      'More than one active customer carries the tax ID «{{value}}»: fill in the code to say which one.',
    msgUnidentifiable:
      'With no code and no tax ID we cannot check whether it already exists; it will be created as a new customer.',
    msgDuplicateInFile: 'Repeated in the file (already on row {{row}})',
    msgCodeTaken: 'The code «{{code}}» already belongs to another customer ({{name}}).',
    msgCodeFormat:
      'The code «{{code}}» in «{{field}}» does not follow the obrador’s code format (like {{example}}). It is not corrected automatically: fix it in the file or leave it empty and one will be generated.',
    msgTaxIdTaken: 'The tax ID «{{value}}» already belongs to another customer ({{name}}).',
    msgTaxIdInFile: 'The tax ID «{{value}}» already appears on row {{row}} of the file.',
    msgUnknownClientCode:
      'The customer code «{{code}}» does not exist and is not on the «Customers» sheet.',
    msgClientRowFailed:
      'Customer «{{code}}» will not be imported (its row has errors), so neither will this point.',
    msgPointDuplicateInFile:
      'This point already appears on row {{row}} for the same customer.',
    msgPointAmbiguousName:
      'The customer already has more than one point named «{{name}}»: fill in the point code to say which one.',
    msgPointAddressRequired: 'A new point needs an address.',
    msgTwoDefaults:
      'This customer already has row {{row}} marked as the default address; there can only be one.',
    msgDefaultMoves: 'Will become the customer default address',
    // Step 3 — preview
    previewTitle: '3. Review before importing',
    previewClients: 'Customers',
    previewPoints: 'Delivery points',
    previewSummary_one:
      '{{count}} row · {{created}} to create · {{updated}} to update · {{unchanged}} unchanged · {{skipped}} to skip · {{error}} with errors',
    previewSummary_other:
      '{{count}} rows · {{created}} to create · {{updated}} to update · {{unchanged}} unchanged · {{skipped}} to skip · {{error}} with errors',
    noPointRows: 'The file carries no delivery points.',
    colRow: 'Row',
    colAction: 'Action',
    colClient: 'Customer',
    colPoint: 'Point',
    colMessages: 'Notes',
    noMessages: '—',
    codeWillBeGenerated: 'auto code',
    strategyLabel: 'What do we do with customers and points that already exist?',
    strategyHint:
      'Only the columns the sheet fills in are written. A blank cell never clears a value, and a delivery point the sheet does not mention is left alone.',
    strategy_update_existing: 'Update them with the sheet data',
    strategy_skip_existing: 'Leave them as they are',
    actionCreated: 'New',
    actionUpdated: 'Update',
    actionUnchanged: 'Unchanged',
    actionSkipped: 'Skipped',
    actionError: 'Error',
    noteWillUpdate: 'Changes: {{fields}}',
    noteWillSkip: 'Already exists; it will not be touched (would change: {{fields}})',
    noteUnchanged: 'Already exists with the same information',
    noteWillDeactivate: 'Will be deactivated',
    noteWillReactivate: 'Will be reactivated',
    deactivationReason: 'Customer import: the file marks it as inactive',
    nothingToChange: 'No row changes anything: everything in the file is already the same.',
    nothingToImport: 'No row can be imported. Fix the errors and upload the file again.',
    commitLabel: 'Import',
    commitClients_one: '{{count}} customer',
    commitClients_other: '{{count}} customers',
    commitPoints_one: '{{count}} point',
    commitPoints_other: '{{count}} points',
    committing: 'Importing…',
    back: 'Back',
    // Step 4 — result
    resultTitle: 'Import result',
    resultClients: 'Customers',
    resultPoints: 'Delivery points',
    resultSummary: 'Import done: {{clients}} and {{points}}',
    resultNoChanges: 'Nothing changed.',
    resultCreated: 'Created',
    resultUpdated: 'Updated',
    resultUnchanged: 'Unchanged',
    resultSkipped: 'Skipped',
    resultErrors: 'Errors',
    newImport: 'Import another file',
    close: 'Close',
  },
  'backoffice.settings': {
    title: 'Settings',
    subtitle: 'Workshop administration',
    // Hub (grouped settings index)
    hubSearchPlaceholder: 'Search settings',
    hubSearchNoResults: 'No setting matches your search',
    groupObrador: 'Workshop',
    groupProduction: 'Production',
    groupCatalog: 'Catalog',
    groupLabeling: 'Labeling & compliance',
    groupThirdParties: 'Third parties',
    descGeneral: 'Numbering, thresholds and operator session',
    descShifts: 'Workshop work shifts',
    descStructure: 'Zones, sections and locations: where work happens and where existences sit',
    descCategories: 'Catalog article classification',
    descUnits: 'Units of measure',
    descTaxes: 'VAT and other applicable taxes',
    descPackagingUnits: 'Packaging and presentation units',
    descArticleFamilies: 'Conservation, usage, preparation and container',
    descIngredients: 'Recipe ingredients master',
    descProcesses: 'Reusable recipe steps',
    descResources: 'Ovens, mixers and other equipment',
    descLegislation: 'Reference standards for tech sheets',
    descLabelTemplates: 'Label designs for lots',
    descBranding: 'Tech-sheet logo and identity',
    descClients: 'Clients master for sales',
    descSuppliers: 'Suppliers master for purchasing',
    descDevices: 'Plant tablets and devices',
    descAuditLog: 'Who changed what and when',
    // Tabs
    tabGeneral: 'General',
    tabShifts: 'Shifts',
    tabStructure: 'Obrador structure',
    tabCategories: 'Categories',
    tabUnits: 'Units',
    tabTaxes: 'Taxes',
    tabPackagingUnits: 'Packaging units',
    tabClients: 'Clients',
    tabSuppliers: 'Suppliers',
    tabDevices: 'Devices',
    tabAuditLog: 'Activity log',
    // Activity log (F0.9)
    auditTitle: 'Activity log',
    auditSubtitle: 'History of creations, changes and removals in the obrador: who, when and what',
    auditEmpty: 'No activity recorded yet',
    auditFilterAllActions: 'All actions',
    auditFilterAllEntities: 'All items',
    auditLoadMore: 'Load more',
    // Shared
    general: 'General',
    numbering: 'Numbering',
    // Ingredients declaration composition rule (tech sheet + label)
    ingredientsRuleTitle: 'Ingredients declaration',
    ingredientsRuleIntro:
      'How the ingredients list of the tech sheet and the label is written. The same rule serves both: the label prints the text the tech-sheet version saved.',
    ingredientsRule: 'Composition rule',
    ingredientsRuleHint:
      'Applies to tech sheets generated from now on; published versions keep their text.',
    ingredientsRule_raw_material: 'Raw material only',
    ingredientsRule_ingredients: 'Ingredients only',
    ingredientsRule_both: 'Raw material and ingredients',
    ingredientsRuleExample: 'Example',
    thresholds: 'Thresholds',
    quietHours: 'Quiet hours',
    users: 'Users',
    devices: 'Devices',
    sectorVocabulary: 'Sector vocabulary',
    loadError: 'The settings data could not be loaded',
    saved: 'Changes saved',
    saveError: 'The changes could not be saved',
    noPermission: 'Only administrators can access settings',
    add: 'Add',
    edit: 'Edit',
    deactivate: 'Deactivate',
    reactivate: 'Reactivate',
    showInactive: 'Show deactivated',
    selectAllRows: 'Select all visible rows',
    selectRow: 'Select {{name}}',
    bulkSelected_one: '{{count}} selected',
    bulkSelected_other: '{{count}} selected',
    clearSelection: 'Clear selection',
    bulkDeactivateConfirm_one: 'Deactivate ({{count}})',
    bulkDeactivateConfirm_other: 'Deactivate ({{count}})',
    bulkDeactivateClientsTitle_one: 'Deactivate {{count}} client',
    bulkDeactivateClientsTitle_other: 'Deactivate {{count}} clients',
    bulkDeactivateClientsBody_one: 'The selected client will be deactivated. This is a logical deactivation: history, traceability and linked documents are kept, and it can be reactivated. The reason is recorded.',
    bulkDeactivateClientsBody_other: 'The {{count}} selected clients will be deactivated. This is a logical deactivation: history, traceability and linked documents are kept, and they can be reactivated. The reason is recorded on each one.',
    bulkDeactivateSuppliersTitle_one: 'Deactivate {{count}} supplier',
    bulkDeactivateSuppliersTitle_other: 'Deactivate {{count}} suppliers',
    bulkDeactivateSuppliersBody_one: 'The selected supplier will be deactivated. This is a logical deactivation: history, traceability and linked documents are kept, and it can be reactivated. The reason is recorded.',
    bulkDeactivateSuppliersBody_other: 'The {{count}} selected suppliers will be deactivated. This is a logical deactivation: history, traceability and linked documents are kept, and they can be reactivated. The reason is recorded on each one.',
    bulkDeactivatePartial: '{{done}} of {{total}} were deactivated. These could not be deactivated: {{failed}}. The selection keeps the ones that failed.',
    bulkDeactivateCount_one: 'Deactivate ({{count}})',
    bulkDeactivateCount_other: 'Deactivate ({{count}})',
    bulkReactivateCount_one: 'Reactivate ({{count}})',
    bulkReactivateCount_other: 'Reactivate ({{count}})',
    bulkDeactivateRecordsTitle_one: 'Deactivate {{count}} record',
    bulkDeactivateRecordsTitle_other: 'Deactivate {{count}} records',
    bulkDeactivateRecordsBody_one: 'The selected record will be deactivated. This is a logical deactivation: history, traceability and linked documents are kept, and it can be reactivated. The reason is recorded.',
    bulkDeactivateRecordsBody_other: 'The {{count}} selected records will be deactivated. This is a logical deactivation: history, traceability and linked documents are kept, and they can be reactivated. The reason is recorded on each one.',
    bulkReactivateConfirm_one: 'Reactivate ({{count}})',
    bulkReactivateConfirm_other: 'Reactivate ({{count}})',
    bulkReactivateClientsTitle_one: 'Reactivate {{count}} client',
    bulkReactivateClientsTitle_other: 'Reactivate {{count}} clients',
    bulkReactivateClientsBody_one: 'The selected client will be reactivated: it will show up in the dropdowns again and can be used.',
    bulkReactivateClientsBody_other: 'The {{count}} selected clients will be reactivated: they will show up in the dropdowns again and can be used.',
    bulkReactivateSuppliersTitle_one: 'Reactivate {{count}} supplier',
    bulkReactivateSuppliersTitle_other: 'Reactivate {{count}} suppliers',
    bulkReactivateSuppliersBody_one: 'The selected supplier will be reactivated: it will show up in the dropdowns again and can be used.',
    bulkReactivateSuppliersBody_other: 'The {{count}} selected suppliers will be reactivated: they will show up in the dropdowns again and can be used.',
    bulkReactivateRecordsTitle_one: 'Reactivate {{count}} record',
    bulkReactivateRecordsTitle_other: 'Reactivate {{count}} records',
    bulkReactivateRecordsBody_one: 'The selected record will be reactivated: it will show up in the dropdowns again and can be used.',
    bulkReactivateRecordsBody_other: 'The {{count}} selected records will be reactivated: they will show up in the dropdowns again and can be used.',
    bulkReactivatePartial: '{{done}} of {{total}} were reactivated. These could not be reactivated: {{failed}}. The selection keeps the ones that failed.',
    searchGeneric: 'Search',
    sectionIdentity: 'Identification',
    sectionContact: 'Contact',
    sectionAddress: 'Address',
    sectionAddressDelivery: 'Address and delivery',
    sectionAddressNotes: 'Address and notes',
    filterTypeAll: 'All types',
    filterTypeNone: 'No type pinned',
    inactiveBadge: 'Inactive',
    empty: 'There are no records yet',
    emptyFiltered: 'No record matches the search',
    searchByCodeOrName: 'Search by code or name',
    colName: 'Name',
    colStatus: 'Status',
    colActions: 'Actions',
    // General / tenant_settings
    generalTitle: 'General parameters',
    generalSubtitle: 'Numbering, thresholds and operator session of the workshop',
    lotNumberingFormat: 'Batch numbering format',
    lotNumberingHint: 'Include {seq} for the sequence, e.g. L-{seq:05}',
    workOrderNumberingFormat: 'Work order numbering format',
    workOrderNumberingHint: 'Include {seq} for the sequence, e.g. WO-{seq:05}',
    purchaseOrderNumberingFormat: 'Purchase order number format',
    purchaseOrderNumberingHint: 'Include {seq} for the sequence, e.g. PO-{seq:05}',
    receptionNumberingFormat: 'Purchase delivery note number format',
    receptionNumberingHint: 'Include {seq} for the sequence, e.g. GR-{seq:05}',
    salesOrderNumberingFormat: 'Sales order numbering format',
    salesOrderNumberingHint: 'Include {seq} for the sequence, e.g. SO-{seq:05}',
    shipmentNumberingFormat: 'Sales delivery note numbering format',
    shipmentNumberingHint: 'Include {seq} for the sequence, e.g. DN-{seq:05}',
    purchaseReturnNumberingFormat: 'Purchase return note numbering format',
    purchaseReturnNumberingHint: 'Include {seq} for the sequence, e.g. DV-{seq:05}',
    supplierCodeNumberingFormat: 'Supplier code format',
    supplierCodeNumberingHint:
      'Include {seq} for the sequence, e.g. PRV-{seq:04}. Only applied when the new supplier carries no code.',
    clientCodeNumberingFormat: 'Customer code format',
    clientCodeNumberingHint:
      'Include {seq} for the sequence, e.g. CLI-{seq:04}. Only applied when the new customer carries no code.',
    zoneCodeNumberingFormat: 'Zone code format',
    zoneCodeNumberingHint:
      'Include {seq} for the sequence, e.g. ZON-{seq:03}. Only applied when the new zone carries no code.',
    numberingPlaceholderRequired: 'You must include the {seq} placeholder',
    defaultMermaThresholdPct: 'Default waste threshold (%)',
    defaultMermaThresholdHint: 'Informational warning when waste exceeds this percentage. Never blocks.',
    weighingDeviationThresholdPct: 'Weighing deviation threshold (%)',
    weighingDeviationThresholdHint: 'Visual warning when a weighing deviates more than this percentage.',
    expiryAlertLeadDays: 'Expiry warning lead days',
    expiryAlertLeadDaysHint: 'Days before expiry when a warning is issued.',
    operatorSessionHours: 'Operator session duration (hours)',
    operatorSessionHoursHint:
      'Shift limit on the plant app: after this time it asks for the PIN again.',
    operatorIdleMinutes: 'Lock after inactivity (minutes)',
    operatorIdleMinutesHint:
      'With the PIN app, the plant app locks and asks for the PIN when nobody touches it for this long. 0 turns it off.',
    idleMinutesRange: 'Must be a whole number between 0 and 240',
    quietHoursStart: 'Quiet hours start',
    quietHoursEnd: 'Quiet hours end',
    quietHoursHint: 'Window without push notifications (HH:MM). Leave empty to disable it.',
    pctRange: 'Must be between 0 and 100',
    timeFormat: 'Invalid time format (HH:MM)',
    // Shifts
    shiftsTitle: 'Shifts',
    shiftsSubtitle: 'Workshop work shifts',
    newShift: 'New shift',
    editShift: 'Edit the shift',
    shiftName: 'Shift name',
    shiftStart: 'Start time',
    shiftEnd: 'End time',
    shiftSortOrder: 'Order',
    colStart: 'Start',
    colEnd: 'End',
    colOrder: 'Order',
    deleteShiftTitle: 'Deactivate the shift',
    deleteShiftBody: 'The shift will no longer be available for planning. The deactivation is logical and reversible.',
    // Obrador structure (zones · sections · locations)
    structureTitle: 'Obrador structure',
    structureSubtitle: 'Where work happens and where existences sit. Zones are enough; sections and locations only when needed.',
    structureEmpty: 'There are no zones yet. Create one and the obrador can already tell where its existences are.',
    newZone: 'New zone',
    editZone: 'Edit the zone',
    zoneCode: 'Code',
    zoneCodeHint: 'Leave it empty and Obrava generates one (e.g. ZON-001). It is the code the article importer links the zone by.',
    zoneName: 'Zone name',
    deleteZoneTitle: 'Deactivate the zone',
    deleteZoneBody: 'The zone will no longer be available for new existences. The history is kept and the deactivation is reversible.',
    zoneHasChildren: 'To deactivate it, first deactivate its sections and locations.',
    areaFunction: 'Function',
    areaStockMode: 'Logistic behaviour',
    storageCondition: 'Storage condition',
    storageConditionNone: 'Not specified',
    allowsLocations: 'Allows locations',
    allowsLocationsHint: 'Turn it on to record the exact spot (shelf, pallet, cold room) where each existence is.',
    allowsLocationsLocked: 'It has active locations: deactivate them before turning this off.',
    addSection: 'Add section',
    addLocation: 'Add locations',
    newSection: 'New section',
    editSection: 'Edit the section',
    sectionZone: 'Zone',
    sectionZonePlaceholder: 'Choose the zone',
    sectionCode: 'Code',
    sectionCodeHint: 'Optional.',
    sectionName: 'Section name',
    sectionDescription: 'Description',
    colDescription: 'Description',
    deleteSectionTitle: 'Deactivate the section',
    deleteSectionBody: 'The section will no longer be available. The history is kept and the deactivation is reversible.',
    sectionHasLocations: 'To deactivate it, first deactivate its locations.',
    unassignedSectionsTitle: 'Sections without a zone',
    unassignedSectionsBody: 'These sections predate the structure. Edit them to place them inside a zone.',
    newLocations: 'New locations',
    editLocation: 'Edit the location',
    locationParent: 'Inside',
    locationCodes: 'Codes',
    locationCodesHint: 'One location per line. The code can tell the aisle, the shelf and the level (e.g. C-02-03).',
    locationCode: 'Code',
    locationDescription: 'Description',
    locationDescriptionHint: 'Optional. For example, “Flour shelf”.',
    locationDuplicate: 'Some of these codes are already used by another active location.',
    locationCodesRepeated: 'The list repeats some codes.',
    deleteLocationTitle: 'Deactivate the location',
    deleteLocationBody: 'The location will no longer be available for new existences. The history is kept and the deactivation is reversible.',
    locationsCount_one: '{{count}} location',
    locationsCount_other: '{{count}} locations',
    noLocations: 'There are no locations yet.',
    // Reorder the structure (OBR-89)
    reorder: 'Reorder',
    reorderDone: 'Done',
    reorderHint: 'Drag by the dots or use the arrows. The order saves itself and the place pickers follow it.',
    moveUp: 'Move up',
    moveDown: 'Move down',
    dragToReorder: 'Drag to change the order',
    reorderError: 'The order could not be saved. The saved order is back.',
    colType: 'Type',
    // Categories (article_categories)
    categoriesTitle: 'Article categories',
    categoriesSubtitle: 'The obrador catalogue categories — editable and extensible',
    newCategory: 'New category',
    editCategory: 'Edit category',
    categoryName: 'Category name',
    categoryParent: 'Parent category',
    categoryNoParent: 'None (top-level category)',
    categoryParentHint: 'Optional. Leave as “None” for a top-level category.',
    categoryParentLocked: 'This category already has subcategories and cannot become one.',
    colParent: 'Parent category',
    deleteCategoryTitle: 'Deactivate the category',
    deleteCategoryBody:
      'The category will no longer be available for new articles. Articles already assigned keep it. The deactivation is logical and reversible.',
    // Units (units of measure)
    unitsTitle: 'Units of measure',
    unitsSubtitle: 'The workshop’s units; used in articles, recipes, stock and labels',
    newUnit: 'New unit',
    editUnit: 'Edit the unit',
    unitCode: 'Code',
    unitCodeHint: 'Short symbol shown next to quantities (e.g. kg, L, Un)',
    unitNameLabel: 'Name',
    unitDimension: 'Dimension',
    unitIsBase: 'Base unit of the dimension',
    unitIsBaseHint: 'Check it if this is the reference unit of this dimension.',
    unitCodeDuplicate: 'A unit with this code already exists',
    colCode: 'Code',
    colDimension: 'Dimension',
    colBase: 'Base',
    deleteUnitTitle: 'Deactivate the unit',
    deleteUnitBody:
      'The unit will no longer be available for new articles and recipes. Those already using it keep it. The deactivation is logical and reversible.',
    // Taxes
    taxesTitle: 'Taxes',
    taxesSubtitle: 'The obrador’s taxes; an article card picks one instead of typing a rate',
    newTax: 'New tax',
    editTax: 'Edit the tax',
    taxName: 'Name',
    taxNameHint: 'What the obrador calls it (e.g. VAT 21%, reduced VAT)',
    taxRate: 'Rate (%)',
    taxIsDefault: 'Default tax',
    taxIsDefaultHint: 'Offered on a new article card. Only one per obrador.',
    taxNameDuplicate: 'A tax with this name already exists',
    colRate: 'Rate',
    deleteTaxTitle: 'Deactivate the tax',
    deleteTaxBody:
      'The tax will no longer be available for new article cards. Articles already using it keep it. The deactivation is logical and reversible.',
    // Packaging / logistic units (packaging_units)
    packagingUnitsTitle: 'Packaging units',
    packagingUnitsSubtitle:
      'The workshop’s packaging & logistic units (sack, box, pallet…); used in packing and shipping',
    newPackagingUnit: 'New packaging unit',
    editPackagingUnit: 'Edit the packaging unit',
    packagingUnitNameHasDigits:
      '“{{name}}” carries the size in its name. In Obrava the unit is generic — “Sack” — and the size lives in each article’s conversion: the same sack holds 25 kg of flour and 10 of salt. You can still save it if that is what you want.',
    packagingUnitName: 'Name',
    packagingUnitNameDuplicate: 'A packaging unit with this name already exists',
    deletePackagingUnitTitle: 'Deactivate the packaging unit',
    deletePackagingUnitBody:
      'The packaging unit will no longer be available. The deactivation is logical and reversible.',
    // Clients
    clientsTitle: 'Clients',
    clientsSubtitle: 'The obrador customers — the buyers of the finished products',
    newClient: 'New client',
    importClients: 'Import',
    clientCode: 'Code',
    clientCodeHint: 'Leave it empty and Obrava generates one (e.g. CLI-0001).',
    clientCommercialName: 'Trading name',
    clientCommercialNameHint: 'Optional. Only when it differs from the legal name.',
    editClient: 'Edit client',
    clientName: 'Name',
    clientTaxId: 'Tax ID',
    clientTaxIdHint: 'Optional. The client tax identification.',
    clientContactName: 'Contact person',
    clientPhone: 'Phone',
    clientEmail: 'Email',
    clientAddress: 'Fiscal address',
    clientAddressHint: 'Optional. The client fiscal or billing address.',
    clientNotes: 'Notes',
    clientNotesHint: 'Optional. Remarks, delivery terms…',
    sectionInstructions: 'Instructions',
    clientShippingTitle: 'Shipping points',
    clientShippingHint:
      'The client delivery points (shops, centres, sites…). The default one is proposed on sales orders.',
    clientShippingName: 'Delivery point name',
    clientShippingAddress: 'Delivery address',
    clientShippingDefault: 'Default',
    clientShippingAdd: 'Add a shipping point',
    clientShippingEdit: 'Edit',
    clientShippingEmpty: 'This customer has no delivery points.',
    clientShippingNew: 'New delivery point',
    clientShippingEditTitle: 'Delivery point',
    clientShippingDefaultBadge: 'Default',
    clientShippingNeedsAddress: 'The delivery point needs a name and an address.',
    clientShippingIncomplete: 'Each shipping point needs a name and an address.',
    clientEmailInvalid: 'Invalid email address',
    deleteClientTitle: 'Deactivate the client',
    deleteClientBody:
      'The client will no longer be available. The deactivation is logical and reversible.',
    // Suppliers
    suppliersTitle: 'Suppliers',
    suppliersSubtitle: 'The obrador suppliers — they feed the article default supplier dropdown',
    newSupplier: 'New supplier',
    editSupplier: 'Edit supplier',
    importSuppliers: 'Import',
    supplierCode: 'Code',
    supplierCodeHint: 'Leave it empty and Obrava generates one (e.g. PRV-0001).',
    supplierName: 'Name or legal name',
    supplierCommercialName: 'Trading name',
    supplierCommercialNameHint: 'Optional. Only when it differs from the legal name.',
    supplierTaxId: 'Tax ID',
    supplierTaxIdHint: 'Optional. The supplier tax identification.',
    supplierContactName: 'Contact person',
    supplierPhone: 'Phone',
    supplierEmail: 'General email',
    supplierAddress: 'Main address',
    supplierAddressHint: 'Optional. The supplier billing address.',
    supplierNotes: 'Notes',
    colDeliveryDays: 'Delivery days',
    supplierDeliveryDays: 'Usual delivery days',
    supplierDeliveryDaysHint:
      'The days this supplier delivers on. When you create an order, the expected delivery date is proposed from the first matching day; you can always change it.',
    supplierEmailInvalid: 'Invalid email address',
    supplierQualityEmail: 'Quality email',
    supplierQualityEmailHint: 'Optional. Where technical sheet requests and renewals go. Separate from the general and order emails.',
    supplierQualityEmailInvalid: 'The quality email is not valid.',
    colTaxId: 'Tax ID',
    colContact: 'Contact',
    deleteSupplierTitle: 'Deactivate the supplier',
    deleteSupplierBody:
      'The supplier will no longer be available for new articles and new purchases. Links to articles (reference, price, purchase unit), orders and delivery notes keep it, marked as inactive. If it was an article\'s primary supplier, that article is left without a primary supplier until you pick another one: none is assigned automatically. The deactivation is logical and reversible.',
    // Users & roles
    usersTitle: 'Users',
    usersSubtitle: 'Workshop members and role assignment',
    colEmail: 'Email',
    colAccess: 'Access',
    appPlanta: 'Floor app',
    appBackoffice: 'Backoffice',
    noAccess: 'None',
    appsLegend:
      'Each column shows the roles that open that app; a dash means no access. Roles add up: an administrator who also works on the floor carries the Operator role as well.',
    colSection: 'Section',
    memberSection: 'Work section',
    memberSectionHint:
      'Decides which work orders reach their device: they receive the ones assigned to them and the ones assigned to this section.',
    noSection: 'No section',
    assignRole: 'Assign role',
    revokeRole: 'Revoke role',
    selectRole: 'Select a role',
    noRoles: 'No roles assigned',
    rolesUpdated: 'Roles updated',
    rolesError: 'The roles could not be updated',
    inactiveUser: 'Inactive',
    // Add member
    addUserTitle: 'Add a user',
    addUserName: 'Full name',
    addUserEmail: 'Email',
    addUserRole: 'Initial role',
    addUserPassword: 'Password (optional)',
    addUserPasswordPlaceholder: 'At least 6 characters',
    addUserPasswordHint:
      'If you set a password, the user can sign in straight away (share it with them). If you leave it blank, they get an email to set it.',
    addUserCta: 'Add user',
    addUserSubmitting: 'Adding…',
    addUserNameRequired: 'A name is required',
    addUserEmailInvalid: 'Invalid email',
    addUserPasswordWeak: 'The password must be at least 6 characters',
    addUserCreated: 'User added. A set-password email was sent to {{email}}.',
    addUserCreatedWithPassword: 'User added ({{email}}) with the given password. They can sign in now.',
    addUserAlreadyMember: 'This email is already a member of the obrador.',
    addUserMenuAccessDenied:
      'User management is hidden for you at this obrador: you cannot add anyone.',
    addUserError: 'The user could not be added.',
    sortBy: 'Sort by',
    sortByRole: 'Role',
    sortByName: 'Name',
    searchNoResults: 'No user matches the search.',
    editRolesTitle: 'Roles and permissions',
    deactivateUserTitle: 'Deactivate user',
    deactivateUserBody:
      "This user's access is blocked. History is kept and it is reversible. Provide a reason for the deactivation.",
    memberLastAdminError:
      'This cannot be done: the obrador would be left without an administrator who can manage users.',
    memberChangeError: 'The change could not be made.',
    // Menu access
    colMenuAccess: 'Access',
    menuAccessNoneHidden: 'All visible',
    menuAccessHidden_one: '{{count}} hidden',
    menuAccessHidden_other: '{{count}} hidden',
    menuAccessUnknown: 'Could not be read',
    menuAccessSelfHint: 'You cannot change your own access.',
    menuAccessDialogTitle: 'Access for {{name}}',
    menuAccessIntro:
      'Choose what this person sees in the Backoffice menu. A hidden option disappears from the menu and cannot be opened.',
    menuAccessScopeNote:
      'Hiding an option restricts use, it is not a security control: only user management and people’s roles are also enforced on the server.',
    menuAccessIntroPlanta:
      'Choose which options of the planta app this operator can use: Orders, Reception, Sales and Alerts. A hidden option disappears from the planta tab bar.',
    menuAccessScopeNotePlanta:
      'Hiding an option restricts use, it is not a security control. The options are saved now and the planta app will apply them from its next version.',
    menuAccessNotForRole: 'Not available for the role',
    menuAccessProtectedHint: 'The workshop must always have an administrator who can see it.',
    menuAccessError: 'The access could not be saved.',
    menuAccessErrorLastAdmin:
      'This cannot be hidden: the workshop would be left with no administrator able to manage users.',
    menuAccessErrorSelfEdit: 'You cannot change your own access. Ask another administrator.',
    menuAccessErrorDenied:
      'User management is hidden for you at this workshop: you cannot change anyone’s access.',
    menuAccessErrorForbidden: 'Only an administrator can change access.',
    menuAccessErrorNotFound: 'This person is no longer at the workshop.',
    menuAccessErrorInvalidKeys:
      'One of the options cannot be hidden from here. Reload the page and try again.',
    // Operaris amb PIN (OBR-25, F5)
    operatorsTitle: 'Operators',
    operatorsSubtitle: 'People who sign in to the plant app with a PIN',
    newOperator: 'New operator',
    searchTeam: 'Search by name, email or tablet',
    colPin: 'PIN',
    colRoles: 'Roles',
    colLastUse: 'Last use',
    pinStatusActive: 'Active',
    pinStatusPending: 'Pending',
    pinWithdrawn: 'PIN withdrawn',
    pinNeverUsed: 'Never used',
    pinPendingHint: 'No live PIN',
    pinNotShown: 'Regenerate it to see it',
    operatorsPendingBanner_one:
      'There is {{count}} person without a live PIN. Regenerate their PIN or deactivate them.',
    operatorsPendingBanner_other:
      'There are {{count}} people without a live PIN. Regenerate their PINs or deactivate them.',
    operatorsEmpty: 'There are no operators yet. Create one and hand over the PIN in person.',
    operatorDeletedReason: 'Reason: {{reason}}',
    operatorRegenerate: 'Regenerate PIN',
    newOperatorTitle: 'New operator',
    newOperatorName: 'Name (optional)',
    newOperatorNameHint: 'If you leave it empty it will be called “Operator” with the next number.',
    newOperatorRoles: 'Roles',
    newOperatorRolesHint: 'Plant roles only. Backoffice roles are given under Users.',
    newOperatorRoleRequired: 'Pick at least one role.',
    newOperatorLinked: 'This person already has an email user',
    newOperatorLinkedNone: 'No',
    newOperatorLinkedHint:
      'It only records that it is the same person; the history they already have stays with their user.',
    newOperatorCta: 'Create and show the PIN',
    pinRevealTitle: 'PIN for {{name}}',
    pinRevealBody:
      'Hand the PIN over in person. You can always see it in the Operators list.',
    pinRevealWhere: 'They sign in to the plant app from any tablet by typing this PIN.',
    pinReveal: 'Show',
    pinHide: 'Hide',
    pinCopy: 'Copy',
    pinCopied: 'Copied',
    pinAutoMask: 'It hides itself in a minute.',
    pinRevealDone: 'Done',
    regeneratePinTitle: 'Regenerate the PIN of {{name}}',
    regeneratePinBody:
      'The current PIN stops working at once. A new one is created, which you will see in the list.',
    regeneratePinCta: 'Regenerate',
    revokeOperatorTitle: 'Deactivate {{name}}',
    revokeOperatorBody:
      'They stop signing in to the plant app at once and their PIN is withdrawn. The removal is logical: the history is kept and it can be reversed.',
    reactivateOperatorTitle: 'Reactivate {{name}}',
    reactivateOperatorBody:
      'They return to the list with a new PIN (the old one is not recovered), which you will see in the list.',
    reactivateOperatorCta: 'Reactivate and show the PIN',
    editOperatorTitle: 'Edit {{name}}',
    editOperatorNameSave: 'Save the name',
    // Errors de la gestió d’operaris i d’aparells
    pinErrorTryLater: 'It could not be completed right now. Try again.',
    pinErrorUnauthorized: 'The session has expired. Sign in again.',
    pinErrorForbidden: 'Only an administrator can manage operators.',
    pinErrorMenuAccessDenied:
      'The Operators option (or Settings) is hidden for you: you cannot do this.',
    pinErrorUsePinManage: 'Operators with a PIN are managed from the Operators tab.',
    pinErrorNotFound: 'Not found: it may no longer exist. Refresh the page and try again.',
    pinErrorReasonRequired: 'A reason is required.',
    pinErrorRoleNotAllowed: 'This role cannot be given to an operator with a PIN.',
    pinErrorAlreadyIssued:
      'This request was already done. If you did not see the PIN, regenerate it.',
    pinErrorCreatedNoPin:
      'The person was created, but the PIN was not delivered. Close this dialog and regenerate their PIN from the list.',
    pinErrorAlreadyPin: 'This person already has a PIN.',
    pinErrorDuplicateName:
      'There is already an operator with this name: rename one of them and try again.',
    pinErrorPersonDeleted: 'This person is deactivated. Reactivate them first.',
    pinErrorPersonInactive: 'This person is inactive.',
    pinErrorNotDeleted: 'This person is not deactivated.',
    pinErrorNoLivePin: 'This person has no live PIN.',
    pinErrorConvertPlantaOnly:
      'Only someone who works in the plant app alone can be switched to a PIN.',
    pinErrorServer: 'There is a problem on the server. Tell the Obrava team.',
    pinErrorGeneric: 'It could not be done. Try again.',
    // Usuaris: «Passa a PIN» i «Crea també un PIN» (OBR-25, F5)
    convertToPinAction: 'Switch to PIN',
    createAlsoPinAction: 'Also create a PIN',
    convertToPinTitle: 'Switch {{name}} to a PIN',
    convertToPinBody:
      'They stop signing in with email and password and sign in to the plant app with a PIN. The history is kept and their email account is not deleted.',
    convertToPinCta: 'Switch to PIN and show it',
    accessCodeTitle: 'Access code',
    accessCodeHint: 'They will get an email with a link to choose a new one. Until they do, the current code keeps working.',
    accessCodeAction: 'Send a link to change the access code',
    accessCodeSending: 'Sending…',
    accessCodeSent: 'A link to change the access code has been sent to {{email}}.',
    accessCodeError: 'The link could not be sent.',
    accessCodeMenuAccessDenied: 'User management is hidden for you in this obrador: you cannot send codes.',
    accessCodeUserInactive: 'This person is no longer active in the obrador.',
    accessCodeNoEmail: 'This person has no email: they sign in with a PIN.',
    accessCodeAuditError: 'The link was sent, but it could not be recorded in the activity log.',
    pinOfferTitle: 'Plant PIN',
    pinOfferConvertHint:
      'Works on the plant floor only: can sign in with a PIN instead of email and password.',
    pinOfferAlsoHint:
      'Has Backoffice access. If they also work on the plant floor, a separate PIN can be created for them.',
    createAlsoPinTitle: 'Also create a PIN for {{name}}',
    createAlsoPinBody:
      'A new person is created who signs in to the plant app with a PIN. Their email user doesn’t change, and what they do in the plant app with the PIN is recorded under the new person.',
    // Devices
    devicesTitle: 'Devices',
    devicesSubtitle: 'Registered workshop hardware',
    newDevice: 'New device',
    editDevice: 'Edit the device',
    deviceName: 'Device name',
    deviceType: 'Type',
    deviceIdentifier: 'Identifier',
    deviceIdentifierHint: 'Serial number, MAC or address (optional)',
    deviceStatus: 'Status',
    colIdentifier: 'Identifier',
    colPrinterAddress: 'Address',
    printerNotConfigured: 'No address',
    devicePrinterPlantaNote:
      'For now labels are printed from the planta app, with the tablet on the same network as the printer.',
    devicePrinterHost: 'Printer address',
    devicePrinterHostHint:
      'IP or name on the obrador’s network, for example 192.168.1.80. No http:// and no port.',
    devicePrinterHostInvalid:
      'Invalid address. Enter an IP (192.168.1.80) or a name, without http:// or a port.',
    devicePrinterPort: 'Port',
    devicePrinterPortHint: 'Usually 9100',
    devicePrinterPortInvalid: 'The port must be a number between 1 and 65535.',
    devicePrinterLabelWidth: 'Label width (mm)',
    devicePrinterLabelHeight: 'Label height (mm)',
    devicePrinterSizeHint: 'The size of the loaded roll. If left empty, 50 × 30 mm is used.',
    devicePrinterSizeInvalid: 'Both the width and the height are needed, between 10 and 200 mm.',
    deleteDeviceTitle: 'Deactivate the device',
    deleteDeviceBody: 'The device will no longer be available. The deactivation is logical and reversible.',
    tabArticleFamilies: 'Article families',
    tabIngredients: 'Ingredients',
    tabProcesses: 'Processes',
    tabLegislation: 'Legislation & standards',
    tabResources: 'Resources',
    tabLabelTemplates: 'Label formats',
    masterName: 'Name',
    conservationConditionsTitle: 'Storage conditions',
    conservationConditionsSubtitle: 'Storage conditions for articles (ambient, refrigerated, frozen…); used on the article sheet and the label',
    newConservationCondition: 'New storage condition',
    editConservationCondition: 'Edit storage condition',
    conservationConditionDuplicate: 'A storage condition with this name already exists',
    deleteConservationConditionTitle: 'Deactivate storage condition',
    deleteConservationConditionBody: 'The condition will no longer be available for new articles. Articles already using it keep it. Deactivation is logical and reversible.',
    usageTypesTitle: 'Usage types',
    usageTypesSubtitle: 'Article usage types (direct consumption, used in production…); used on the article sheet',
    newUsageType: 'New usage type',
    editUsageType: 'Edit usage type',
    usageTypeDuplicate: 'A usage type with this name already exists',
    deleteUsageTypeTitle: 'Deactivate usage type',
    deleteUsageTypeBody: 'The usage type will no longer be available for new articles. Articles already using it keep it. Deactivation is logical and reversible.',
    preparationModesTitle: 'Preparation modes',
    preparationModesSubtitle: 'Article preparation modes (ready to eat, cook before use…); used on the article sheet',
    newPreparationMode: 'New preparation mode',
    editPreparationMode: 'Edit preparation mode',
    preparationModeDuplicate: 'A preparation mode with this name already exists',
    deletePreparationModeTitle: 'Deactivate preparation mode',
    deletePreparationModeBody: 'The preparation mode will no longer be available for new articles. Articles already using it keep it. Deactivation is logical and reversible.',
    containerTypesTitle: 'Container types',
    containerTypesSubtitle: 'Sales containers (jar, bottle, tray…); distinct from the logistic packaging units',
    newContainerType: 'New container type',
    editContainerType: 'Edit container type',
    containerTypeDuplicate: 'A container type with this name already exists',
    deleteContainerTypeTitle: 'Deactivate container type',
    deleteContainerTypeBody: 'The container type will no longer be available for new articles. Articles already using it keep it. Deactivation is logical and reversible.',
    complianceItemsTitle: 'Legislation and compliance',
    complianceItemsSubtitle: 'Legislation and compliance items (“GMO free”, certifications…); used on the tech sheet',
    newComplianceItem: 'New compliance item',
    editComplianceItem: 'Edit compliance item',
    complianceItemDuplicate: 'A compliance item with this name already exists',
    deleteComplianceItemTitle: 'Deactivate compliance item',
    deleteComplianceItemBody: 'The item will no longer be available for new articles. Articles already using it keep it. Deactivation is logical and reversible.',
    ingredientsTitle: 'Ingredients',
    ingredientsSubtitle: 'Ingredients for the declared composition of articles; they can also be created on the fly from the article sheet',
    newIngredient: 'New ingredient',
    editIngredient: 'Edit ingredient',
    ingredientDuplicate: 'An ingredient with this name already exists',
    deleteIngredientTitle: 'Deactivate ingredient',
    deleteIngredientBody: 'The ingredient will no longer be available for new articles. Articles already declaring it keep it. Deactivation is logical and reversible.',
    processesTitle: 'Processes',
    processesSubtitle: 'Elaboration processes: the template for a recipe’s steps (duration, temperature, control, instructions and default resources)',
    newProcess: 'New process',
    editProcess: 'Edit process',
    processDescription: 'Description',
    processDuration: 'Duration (min)',
    processTemperature: 'Temperature (°C)',
    processControlType: 'Control type',
    processInstructions: 'Base instructions',
    processInstructionsHint:
      'Copied into the step when one is created from this process; they can then be adapted in each recipe.',
    processResources: 'Default resources',
    processResourcesHint:
      'The resource types this process proposes. They are copied into the step and can then be changed in each recipe.',
    colProcessResources: 'Resources',
    processResourceDuplicate: 'This resource type is already in the list: two units are a single line with quantity 2',
    processDuplicateDescription: 'A process with this description already exists',
    colProcessDuration: 'Duration (min)',
    colProcessTemperature: 'Temperature (°C)',
    colProcessControl: 'Control',
    deleteProcessTitle: 'Deactivate the process',
    deleteProcessBody: 'The process will no longer be available for new recipes. Recipes already using it keep it. Deactivation is logical and reversible.',
    legislationTitle: 'Legislation & standards',
    legislationSubtitle: 'The obrador’s legal and regulatory texts; printed, in this order, as the closing text of every tech sheet',
    newLegislationNote: 'New text',
    editLegislationNote: 'Edit text',
    legislationNoteTitle: 'Title',
    legislationNoteBody: 'Text',
    legislationNoteSortOrder: 'Order',
    colLegislationBody: 'Text',
    colLegislationSortOrder: 'Order',
    deleteLegislationNoteTitle: 'Deactivate text',
    deleteLegislationNoteBody: 'The text will no longer appear on new tech sheets. Already approved sheets keep it. Deactivation is logical and reversible.',
    labelTemplatesTitle: 'Label formats',
    labelTemplatesSubtitle: 'The workshop’s label formats; labels are generated at OT close or packaging, never from the tech sheet',
    newLabelTemplate: 'New label format',
    editLabelTemplate: 'Edit label format',
    labelTemplateName: 'Name',
    labelTemplateType: 'Label type',
    labelType_internal_lot: 'Internal lot',
    labelType_sales_legal: 'Legal sales label',
    labelTemplateIsDefault: 'Default format for its type',
    labelTemplateIsDefaultHint: 'When checked, it replaces the previous default format of this type.',
    labelTemplateDefaultBadge: 'Default',
    colDefault: 'Default',
    deleteLabelTemplateTitle: 'Deactivate label format',
    deleteLabelTemplateBody: 'The format will no longer be available for new labels. Deactivation is logical and reversible.',
    categoryArticleType: 'Article type',
    categoryArticleTypeHint: 'Optional. Restricts the category to one article type; “All” makes it valid for every type.',
    categoryAllTypes: 'All',
    colArticleType: 'Type',
    skuSection: 'Article coding (SKU)',
    skuModeLabel: 'Code assignment',
    skuModeHint: 'How the code (SKU) is assigned when creating a new article.',
    skuMode_free: 'Manual: the code is typed when creating the article',
    skuMode_sequential: 'Automatic: one sequence for the whole catalogue',
    skuMode_per_type: 'Automatic per type: one sequence per article type',
    skuFormat: 'Code mask',
    skuFormatHint: 'Include {seq} for the sequence, e.g. ART-{seq:05}',
    salesBarcodeEnabled: 'Barcodes on sales articles',
    salesBarcodeEnabledHint: 'Shows the barcode (EAN) field on articles that are sold.',
    tabBranding: 'Brand & Tech sheet',
    // Resources (resource types + resources)
    resourceTypesTitle: 'Resource types',
    resourceTypesSubtitle:
      'How you classify what a production run consumes (Oven, Worker, Cold room…); define yours before adding resources',
    newResourceType: 'New resource type',
    editResourceType: 'Edit the resource type',
    resourceTypeDuplicate: 'A resource type with this name already exists',
    deleteResourceTypeTitle: 'Deactivate the resource type',
    deleteResourceTypeBody:
      'The type will no longer be available for new resources and recipes. Those already using it keep it. Deactivation is logical and reversible.',
    resourcesTitle: 'Resources',
    resourcesSubtitle:
      'What a production run occupies that is not raw material (Oven 1, Mixer 02, a worker) and where it is; the recipe asks for the type and planning picks which one',
    newResource: 'New resource',
    editResource: 'Edit the resource',
    resourceType: 'Resource type',
    selectResourceType: 'Select a type',
    resourcePlace: 'Where it is',
    resourcePlaceHint:
      'Zone, section or location in the workshop. Used to suggest the closest resource when planning a work order.',
    resourcePlaceNone: 'Whole workshop',
    colResourcePlace: 'Place',
    resourceCostPerHour: 'Cost per hour (€)',
    resourceCostPerHourHint: 'Optional. Stored for costing; nothing is computed yet.',
    resourceNotes: 'Notes',
    resourceDuplicate: 'A resource with this name already exists',
    colResourceType: 'Type',
    colResourceCost: 'Cost/hour',
    deleteResourceTitle: 'Deactivate the resource',
    deleteResourceBody:
      'The resource will no longer be available for new work orders. Work orders that already have it keep it. Deactivation is logical and reversible.',
    supplierOrdersSection: 'Sending orders',
    supplierOrdersSectionHint: 'How they receive orders: by email from Obrava, by WhatsApp or by other means.',
    supplierOrderChannel: 'Channel',
    supplierOrderChannelEmail: 'Email (from Obrava)',
    supplierOrderChannelWhatsapp: 'WhatsApp (to the order number)',
    supplierOrderChannelManual: 'Other means (phone, in person…)',
    supplierOrderEmails: 'Order emails',
    supplierOrderEmailsHint: 'Separate them with commas. Required if the channel is email: the general contact email does not replace it.',
    supplierOrderEmailsInvalid: 'Some order emails are not valid: {{list}}',
    supplierOrderEmailsRequired: 'The channel is Email: enter the order email (the general email does not replace it).',
    supplierOrderPhone: 'Order phone/WhatsApp',
    supplierOrderPhoneHint: 'Number where the supplier receives orders, with the country prefix if possible (+34 600 000 000). Separate from the general phone.',
    supplierOrderPhoneInvalid: 'The order phone/WhatsApp is not valid: use 7 to 15 digits, with spaces and a + prefix if needed.',
    supplierOrderPhoneRequired: 'The channel is WhatsApp: enter the order phone/WhatsApp (the general phone does not replace it).',
    supplierOrderEmailsTooMany: 'At most ten order emails.',
    supplierOrderLanguage: 'Language',
    supplierOrderLanguageSender: 'The sender’s',
    supplierOrderSubject: 'Subject (optional)',
    supplierOrderMessage: 'Message (optional)',
    supplierOrderTemplateHint: 'Leave it blank to use the default. You can use {{tokens}}.',
    supplierOrderWhatsappMessage: 'WhatsApp message (optional)',
    supplierOrderWhatsappHint: 'Separate from the email message. Leave it blank to use the default. You can use {{tokens}}. The block of items goes where you put {{linesToken}}, ideally on a line of its own (if you leave it out, it goes after the text). WhatsApp cannot carry the PDF: do not mention an attachment.',
    supplierOrderWhatsappPreview: 'Preview (with sample data)',
    supplierOrderWhatsappPreviewTooLong: 'This text is too long for the WhatsApp link: shorten it.',
    supplierOrderWhatsappSampleArticle1: 'Wheat flour',
    supplierOrderWhatsappSampleArticle2: 'Fresh yeast',
    supplierOrderWhatsappSampleSupplier: 'Sample supplier',
    supplierOrderAttachPdf: 'Attach the order PDF',
    supplierOrderAutoSend: 'Send automatically on confirm',
    supplierOrderAutoSendHint: 'Confirming an order for this supplier emails it right away.',
  },

  'backoffice.areaFunction': {
    storage: 'Storage',
    production: 'Production',
    reception: 'Receiving',
    shipping: 'Shipping',
    quality: 'Quality',
    other: 'Other',
  },

  'backoffice.areaStockMode': {
    stock: 'Stock',
    provisioning: 'Provisioning',
    none: 'No stock',
  },

  'backoffice.areaStockModeHint': {
    stock: 'Existences live here routinely (stores, cold rooms).',
    provisioning: 'Work area: material arrives to be consumed or transformed, but does not live here.',
    none: 'No existences are tracked here (cleaning, changing rooms…).',
  },

  'backoffice.storageCondition': {
    ambient: 'Ambient',
    refrigerated: 'Chilled',
    frozen: 'Frozen',
  },

  'backoffice.unitDimension': {
    mass: 'Mass',
    volume: 'Volume',
    count: 'Count',
    length: 'Length',
  },

  'backoffice.deviceType': {
    label_printer: 'Label printer',
    scale: 'Scale',
    barcode_reader: 'Barcode reader',
    sensor: 'Sensor',
    tablet: 'Tablet',
  },

  'backoffice.deviceStatus': {
    registered: 'Registered',
    active: 'Active',
    offline: 'Offline',
    error: 'Error',
    decommissioned: 'Decommissioned',
  },

  'backoffice.roleLabel': {
    admin: 'Administrator',
    production_manager: 'Production manager',
    operator: 'Operator',
    quality: 'Quality',
    purchasing: 'Purchasing',
    sales: 'Sales',
    viewer: 'Viewer',
  },

  'backoffice.traceability': {
    title: 'Traceability',
    backward: 'Backward',
    forward: 'Forward',
    byLot: 'By batch',
  },

  'platformConsole.common': {
    consoleName: 'Platform Console',
    themeLight: 'Light',
    themeDark: 'Dark',
    saving: 'Saving…',
    creating: 'Creating…',
    actionError: 'The action could not be completed',
    loadError: 'The data could not be loaded',
    never: 'Never',
    lang_ca: 'Català',
    lang_es: 'Español',
    lang_en: 'English',
    settings: 'Settings',
    language: 'Language',
    appearance: 'Appearance',
    themeSystem: 'System',
    done: 'Done',
    theme: 'Theme',
    account: 'Account & profile',
    copyVersion: 'Copy version',
    versionCopied: 'Version copied',
    collapse: 'Collapse',
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
    comingSoon: 'Coming soon',
  },

  'platformConsole.auth': {
    consoleTitle: 'Obrava Platform Console',
    consoleSubtitle: 'Access reserved for the Obrava team',
    signInTitle: 'Sign in',
    email: 'Email',
    password: 'Password',
    signInCta: 'Sign in',
    signingIn: 'Signing in…',
    signInError: 'Incorrect credentials',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    notAuthorizedTitle: 'Not authorized',
    notAuthorizedBody:
      'This account is not a platform super user. The console is exclusive to the Obrava team.',
    backToSignIn: 'Back to sign in',
    mfaRequiredTitle: 'Two-step verification',
    mfaRequiredSubtitle: 'Enter the 6-digit code from your authenticator app',
    mfaEnrollTitle: 'Set up two-step verification',
    mfaEnrollSubtitle:
      'Two-step verification is required to access the console. Add this account to your authenticator app (Google Authenticator, 1Password…).',
    mfaSecretLabel: 'Secret key',
    mfaUriLabel: 'Setup URL (otpauth)',
    mfaScanHint: 'Scan the QR code with your authenticator app (Google Authenticator, 1Password…), or enter the key manually.',
    mfaSecretHint:
      'Enter the key manually in your app, or paste the otpauth URL. Then confirm with the generated code.',
    mfaCodeLabel: '6-digit code',
    mfaCodePlaceholder: '000000',
    mfaVerifyCta: 'Verify',
    mfaVerifying: 'Verifying…',
    mfaEnrollCta: 'Activate and verify',
    mfaInvalidCode: 'Incorrect code. Try again.',
    mfaEnrollError: 'Setup could not be started',
    mfaCodeFormat: 'The code must be 6 digits',
    copy: 'Copy',
    copied: 'Copied',
  },

  'platformConsole.obradors': {
    title: 'Workshops',
    subtitle: 'Manage the platform workshops (tenants)',
    create: 'Create workshop',
    empty: 'There are no workshops yet',
    emptyFiltered: 'No workshop matches the filters',
    searchPlaceholder: 'Search by name or tax ID',
    showInactive: 'Show inactive',
    filterStatus: 'Status',
    filterSector: 'Sector',
    filterPlan: 'Plan',
    colName: 'Commercial name',
    colTaxId: 'Tax ID',
    colSector: 'Sector',
    colPlan: 'Plan',
    colStatus: 'Status',
    colCreatedAt: 'Created on',
    colLastAccess: 'Last access',
    suspend: 'Suspend',
    reactivate: 'Reactivate',
    suspendReason: 'Suspension reason',
    reactivateReason: 'Reactivation reason',
    supportAccess: 'Support access',
    supportAccessReason: 'Access reason',
  },

  'platformConsole.statusLabel': {
    active: 'Active',
    suspended: 'Suspended',
    deactivated: 'Deactivated',
  },

  'platformConsole.sectorLabel': {
    forn: 'Bakery',
    pastisseria: 'Pastry shop',
    xarcuteria: 'Charcuterie',
    precuinats: 'Ready meals',
    conserves: 'Preserves',
    generic: 'Generic',
  },

  'platformConsole.planLabel': {
    obrador: 'Workshop',
    pime: 'SME',
    cadena: 'Chain',
  },

  'platformConsole.create': {
    title: 'New workshop',
    subtitle: 'Create a workshop (tenant) on the platform',
    sectionFiscal: 'Workshop details',
    sectionPlan: 'Plan and language',
    sectionAdmin: 'Initial admin',
    name: 'Commercial name',
    legalName: 'Legal name',
    taxId: 'Tax ID',
    sector: 'Sector',
    plan: 'Plan',
    language: 'Language',
    adminEmail: 'Initial admin email',
    submit: 'Create workshop',
    cancel: 'Cancel',
    deployNote:
      'Creating the workshop deploys the default base structure (warehouses, families, shifts, zones). The admin account is provisioned afterwards, from the workshop’s detail screen.',
    createError: 'The workshop could not be created',
    taxIdTaken: 'This tax ID already belongs to an active workshop.',
  },

  'platformConsole.detail': {
    title: 'Workshop record',
    sectionFiscal: 'Tax details',
    sectionPlan: 'Plan and sector',
    sectionStatus: 'Status',
    sectionDanger: 'Deactivate workshop',
    name: 'Commercial name',
    legalName: 'Legal name',
    taxId: 'Tax ID',
    sector: 'Sector',
    plan: 'Plan',
    createdAt: 'Created on',
    lastAccess: 'Last access',
    save: 'Save changes',
    saved: 'Changes saved',
    saveError: 'The changes could not be saved',
    saveTitle: 'Save the workshop changes',
    saveBody:
      'Changes to the tax details and the plan are recorded in the audit log. State the reason for the change.',
    suspend: 'Suspend workshop',
    reactivate: 'Reactivate workshop',
    deactivate: 'Deactivate workshop',
    suspendTitle: 'Suspend the workshop',
    suspendBody:
      'All access (backoffice, app and API) is blocked immediately. The data is kept intact and it is reversible.',
    reactivateTitle: 'Reactivate the workshop',
    reactivateBody: 'Access is restored with all data intact.',
    deactivateTitle: 'Deactivate the workshop',
    deactivateBody:
      'Reversible soft delete: all access is blocked and the workshop disappears from the listings. Regulatory data (batches, work orders, traceability, HACCP) is kept intact. Physical purge is only done through a deferred process, never from here.',
    reasonLabel: 'Reason',
    reasonPlaceholder: 'State the reason (required)',
    reasonRequired: 'A reason is required',
    notFound: 'The workshop was not found',
    deletedBanner: 'This workshop is deactivated. Reactivate it to edit it.',
    suspendedBanner: 'This workshop is suspended.',
    sectionOwner: 'Primary user',
    // Activity log (F0.9)
    sectionActivity: 'Activity log',
    activityNotice:
      'Viewing this log is itself recorded in the obrador’s log and is visible to the client.',
    activityEmpty: 'No activity recorded',
    activityError: 'Could not load the activity log',
    activityLoadMore: 'Load more',
    activityFilterAllActions: 'All actions',
    activityFilterAllEntities: 'All items',
    ownerName: 'Name',
    ownerEmail: 'Email',
    ownerStatus: 'Account status',
    ownerStatusActive: 'Active',
    ownerStatusBlocked: 'Blocked',
    ownerStatusInvited: 'Invited (pending sign-up)',
    ownerStatusNone: 'No owner assigned',
    ownerNone: 'This workshop does not yet have an owner or administrator email.',
    ownerLoadError: 'The primary user could not be loaded.',
    ownerResetCta: 'Send a new access code',
    ownerResetHint:
      'The owner will receive a code at their email to set a new password. The console does not display any password.',
    ownerResetSent: 'An access code has been sent to {{email}}.',
    ownerResetError: 'The access code could not be sent.',
    ownerCreateHint:
      'Create the owner directly. Their account will be created and they will receive an email to set their password; no need to wait for them to sign up.',
    ownerCreateName: 'Owner name',
    ownerCreateEmail: 'Owner email',
    ownerCreateNameRequired: 'The name is required',
    ownerCreateEmailInvalid: 'Invalid email',
    ownerCreatePassword: 'Password (optional)',
    ownerCreatePasswordPlaceholder: 'At least 6 characters',
    ownerCreatePasswordHint:
      'If you set a password, the owner will sign in directly (communicate it to them yourself). If you leave it blank, they will receive an email to set it themselves.',
    ownerCreatePasswordWeak: 'The password must be at least 6 characters',
    ownerCreateCta: 'Create the owner',
    ownerCreated: 'Account created. An email has been sent to {{email}} to set the password.',
    ownerCreatedWithPassword:
      'Account created for {{email}} with the password provided. They can sign in now.',
    ownerCreatedNoEmail:
      'Account created for {{email}}, but the email could not be sent. Try again with «Send a new access code».',
    ownerCreateError: 'The owner account could not be created.',
    ownerEmailInUse: 'This email already has an account. Use «Send a new access code».',
    ownerExistsLinkHint:
      'This email ({{email}}) already has an account on InsForge. It will be linked to this workshop and can sign in with its current password — no email.',
    ownerLinkCta: 'Link to the workshop',
    ownerLinked:
      'Account linked to the workshop. The owner can sign in with their current password.',
  },

  // ── Sector vocabulary OVERRIDE layers (shadow domain.* per dossier §7.8) ─────
  'sectors.forn': {
    finishedProduct: 'Bakery product',
    'unit.loaves': 'loaves',
  },
  'sectors.pastisseria': {
    finishedProduct: 'Pastry product',
  },
  'sectors.xarcuteria': {
    finishedProduct: 'Charcuterie product',
    'unit.loaves': 'pieces',
  },
  'backoffice.brandingTab': {
    logoSection: 'Logo',
    logoHint: 'Shown in the tech-sheet header. Image (PNG or JPG), max 2 MB.',
    logoUploading: 'Uploading…',
    logoRemove: 'Remove logo',
    logoInvalidType: 'The file must be an image.',
    logoTooLarge: 'The image exceeds 2 MB.',
    logoUploadError: 'The logo could not be uploaded.',
    companySection: 'Company details (tech-sheet header)',
    companyHint: 'These details appear in the tech-sheet header. Fill in only the ones you have.',
    fieldInvalid: 'Invalid value',
    field_legal_name: 'Legal name',
    field_tax_id: 'Tax ID',
    field_address: 'Address',
    field_city: 'City',
    field_postal_code: 'Postal code',
    field_province: 'Province',
    field_phone: 'Phone',
    field_email: 'Email',
    field_website: 'Website',
    field_sanitary_registry: 'Sanitary registry (RGSEAA)',
    designSection: 'Tech-sheet design',
    designHint: 'Choose the style used to generate the tech-sheet PDF.',
    design_classic: 'Classic',
    designDesc_classic: 'Like the sample: header with logo and framed sections.',
    design_modern: 'Modern',
    designDesc_modern: 'Clean, with sans-serif type and an accent colour.',
    design_compact: 'Compact',
    designDesc_compact: 'Dense, all information packed onto one page.',
  },
  'backoffice.labelPrint': {
    proofCta: 'Label proof',
    cta: 'Generate label',
    generating: 'Generating…',
    error: 'The label could not be generated.',
    noTemplate: 'No label format is configured. Define one in Settings · Label formats.',
    noSheet: 'This article has no approved tech sheet yet: the label will print without ingredients, allergens or nutrition.',
    sampleLot: 'SAMPLE-LOT',
    sampleDate: '00/00/0000',
    ingredientsPrefix: 'Ingredients',
    copies: 'Copies',
    issued: 'Label generated and recorded.',
  },

  'backoffice.labelEditor': {
    field_legal_denomination: 'Legal denomination',
    field_product_name: 'Product name',
    field_ingredients: 'Ingredients',
    field_allergens: 'Allergens',
    field_nutrition: 'Nutrition',
    field_net_weight: 'Net weight',
    field_conservation: 'Storage',
    field_country_of_origin: 'Country of origin',
    field_business_name: 'Business name',
    field_business_address: 'Business address',
    field_sanitary_registry: 'Sanitary registry',
    field_lot_number: 'Lot number',
    field_production_date: 'Production date',
    field_expiry_date: 'Expiry date',
    field_best_before: 'Best before',
    field_quantity: 'Quantity',
    field_sku: 'Code (SKU)',
    field_custom_text: 'Custom text',
    field_qr: 'QR code',
    field_barcode: 'Barcode',
    field_logo: 'Logo',
    field_image: 'Product image',
    field_spacer: 'Spacer',
    align_left: 'Left',
    align_center: 'Center',
    align_right: 'Right',
    editTitle: 'Edit label format',
    newTitle: 'New label format',
    back: 'Back to list',
    dimensionsTitle: 'Dimensions (mm)',
    widthMm: 'Width (mm)',
    heightMm: 'Height (mm)',
    paddingMm: 'Padding (mm)',
    showBorder: 'Show border',
    blocksTitle: 'Label content',
    resetDefault: 'Reset to default design',
    blockField: 'Field',
    blockCaption: 'Prefix (optional)',
    blockText: 'Text',
    blockFont: 'Size (pt)',
    blockAlign: 'Alignment',
    blockBold: 'Bold',
    addBlock: 'Add block',
    previewTitle: 'Preview',
    previewHint: 'Approximate result · {{w}}×{{h}} mm · {{type}}. Sample data.',
    missingMandatory: 'Missing mandatory fields on the sales label: {{fields}}.',
    invalidLayout: 'The label layout is invalid. Check the dimensions and blocks.',
  },
  ...existenciesEn,
  ...qualityEn,
  ...compresEn,
  ...catalegEn,
} as const;

export type EnResources = typeof en;
